// AccountBilling.tsx
import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom'
import { atticusQuoteReturnPath } from '../atticus/contracts';
import {
  Row,
  Col,
  Card,
  Typography,
  Space,
  Tag,
  Tooltip,
  Spin,
  Button,
  Alert,
  Badge,
  Radio,
  Modal,
  message,
  Grid,
  Input,
  Table,
  Empty,
} from 'antd';
import type { TourProps } from 'antd';
import {
  CreditCardOutlined,
  CalendarOutlined,
  CrownOutlined,
  RocketOutlined,
  FileTextOutlined,
  QuestionCircleOutlined,
  DownloadOutlined,
  SearchOutlined,
  BookOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import axios from '../utils/accountApi';
import { getAuthHeaders } from '../utils/authHeaders';
import { resolveFrontendApiBase } from '../utils/apiBase';
import type { ColumnsType } from 'antd/es/table';
import { useAccountContext } from '../Account';
import { CustomPricing } from '../CustomPricing';
import { ACCOUNT_BILLING_TOUR_EVENT, ACCOUNT_BILLING_TOUR_STORAGE_KEY } from './accountTourEvents';
import LoadingCard from '../components/LoadingCard';
import { getStripePromise } from '../utils/stripe';
import AppTour, { renderAppTourPanel } from '../tours/AppTour';
import {
  HUMAN_REVIEW_PROVIDERS_ROUTE,
  PUBLISHING_SUPPORT_PROVIDERS_ROUTE,
  RESOURCES_ROUTE,
} from '../serviceProviderDirectory';
import './AccountBilling.css';
const API_BASE = resolveFrontendApiBase();
const MAX_PAUSE_SKIP_RENEWALS = 3;
const BILLING_SYNC_DELAY_MS = 1500;
const SUBSCRIPTION_CREDIT_CANCEL_GRACE_DAYS = 60;
const isOnBreakEngagementEnabled = (): boolean =>
  String(process.env.REACT_APP_ON_BREAK_ENGAGEMENT_ENABLED || '').trim().toLowerCase() === 'true';
const delay = (ms: number): Promise<void> =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;
const { TextArea } = Input;

const CANCEL_REASON_OPTIONS = [
  { value: 'finished_project', label: 'Finished my project' },
  { value: 'too_expensive', label: 'Too expensive' },
  { value: 'missing_features', label: 'Missing features' },
  { value: 'hard_to_use', label: 'Hard to use' },
  { value: 'quality_issues', label: 'Quality issues' },
  { value: 'technical_issues', label: 'Technical issues' },
  { value: 'switching_tools', label: 'Switching tools' },
  { value: 'temporary_break', label: 'Temporary break' },
  { value: 'other', label: 'Other' },
] as const;

type CancelReasonCode = (typeof CANCEL_REASON_OPTIONS)[number]['value'];
type CancelFlowOutcome = 'retained' | 'cancelled';

interface CancellationFeedbackPayload {
  cancelReasonCode: CancelReasonCode;
  cancelReasonText: string;
  cancelFlowOutcome: CancelFlowOutcome;
}

const ON_BREAK_STAGE_OPTIONS = [
  { value: 'reviewing_translations', label: "I'm proofreading or reviewing translations" },
  { value: 'formatting_or_publishing', label: "I'm formatting, preparing covers, or publishing" },
  { value: 'finished_project', label: 'I finished my current project' },
  { value: 'unused_credits', label: 'I have enough unused credits' },
  { value: 'writing_next_book', label: "I'm writing my next book" },
  { value: 'taking_break', label: "I'm taking a break" },
  { value: 'other', label: 'Something else' },
] as const;

const ON_BREAK_RETURN_OPTIONS = [
  { value: 'within_1_month', label: 'Within 1 month' },
  { value: '2_to_3_months', label: 'In 2–3 months' },
  { value: '4_to_6_months', label: 'In 4–6 months' },
  { value: 'more_than_6_months', label: 'More than 6 months' },
  { value: 'not_sure', label: "I'm not sure" },
] as const;

type OnBreakStage = (typeof ON_BREAK_STAGE_OPTIONS)[number]['value'];
type OnBreakExpectedReturn = (typeof ON_BREAK_RETURN_OPTIONS)[number]['value'];

interface OnBreakSurveyPayload {
  onBreakStage: OnBreakStage;
  onBreakExpectedReturn: OnBreakExpectedReturn;
  onBreakFlowVersion: 1;
}

interface OnBreakConfirmation {
  intent_id?: string;
  status: 'scheduled' | 'active';
  effective_at?: number | null;
  follow_up_at?: number | null;
  credits_protected: boolean;
  resource_keys: string[];
}

const ON_BREAK_RESOURCE_DETAILS: Record<string, { title: string; description: string; route: string }> = {
  human_review_directory: {
    title: 'Human Review Provider Directory',
    description: 'Find independent proofreading, editing, and translation-review providers.',
    route: HUMAN_REVIEW_PROVIDERS_ROUTE,
  },
  publishing_support_directory: {
    title: 'Publishing & Formatting Providers',
    description: 'Explore independent formatting and publishing-support providers.',
    route: PUBLISHING_SUPPORT_PROVIDERS_ROUTE,
  },
  proofreading_guidance: {
    title: 'Proofread a Chapter',
    description: 'Follow the proofreading workflow and review its quality checklist.',
    route: '/support?tab=articles&article=proofread-a-chapter',
  },
  human_review_guidance: {
    title: 'Do I Need Human Review?',
    description: 'Decide when an independent reviewer should be part of your publishing process.',
    route: '/support?tab=articles&article=faq-do-i-need-human-review-after-scribeshadow',
  },
  export_guidance: {
    title: 'Export Your Book Files',
    description: 'Prepare and download EPUB, Word, or PDF files for final review.',
    route: '/support?tab=articles&article=export-book-files',
  },
  publishing_guidance: {
    title: 'Finish and Publish Your Translation',
    description: 'Use a practical checklist for review, metadata, covers, exports, proofs, and storefront QA.',
    route: '/support?tab=articles&article=publishing-checklist',
  },
  credit_safety_guidance: {
    title: 'Subscription Credits vs. Forever Credits',
    description: 'Understand how each credit type works and how subscription credits stay protected.',
    route: '/support?tab=articles&article=subscription-credits-vs-forever-credits',
  },
  on_break_guidance: {
    title: 'How the On Break Plan Works',
    description: 'Review protected credits, billing, check-ins, and how to resume.',
    route: '/support?tab=articles&article=on-break-plan',
  },
  book_upload_guidance: {
    title: 'Upload Your Next Book',
    description: 'Prepare a DOCX or EPUB and add the next manuscript to your library.',
    route: '/support?tab=articles&article=upload-a-book',
  },
  book_dashboard_guidance: {
    title: 'Book Dashboard and Next Steps',
    description: 'See how ScribeShadow guides a new book from setup through translation and export.',
    route: '/support?tab=articles&article=book-dashboard-next-steps',
  },
  resources_hub: {
    title: 'ScribeShadow Resources',
    description: 'Browse author services and resources for your current stage.',
    route: RESOURCES_ROUTE,
  },
};

const ON_BREAK_RESOURCE_KEYS_BY_STAGE: Record<OnBreakStage, string[]> = {
  reviewing_translations: ['human_review_directory', 'proofreading_guidance', 'human_review_guidance'],
  formatting_or_publishing: ['publishing_support_directory', 'export_guidance', 'publishing_guidance'],
  finished_project: ['publishing_guidance', 'publishing_support_directory'],
  unused_credits: ['credit_safety_guidance', 'on_break_guidance'],
  writing_next_book: ['book_upload_guidance', 'book_dashboard_guidance'],
  taking_break: [],
  other: ['resources_hub'],
};

interface PlanDetails {
  planName?: string;
  monthlyCredits?: number;
  tier?: number;
  monthlyCreditsAllocated?: number;
  billingInterval?: string;
  intervalMonths?: number;
  creditsPerInterval?: number;
  monthlyEquivalentCredits?: number;
  price?: number;
  currency?: string;
  stripeProductID?: string;
  stripePriceID?: string;
  priceId?: string;
  isActive?: boolean;
  isLegacy?: boolean;
  foreverCreditDiscountPercent?: number;
}

interface PaymentMethodSummary {
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
}

interface OnBreakPlanSummary {
  planName?: string | null;
  price?: number | null;
  currency?: string | null;
  stripePriceID?: string | null;
  stripeProductID?: string | null;
}

interface SubscriptionSummary {
  status: string;
  planName: string;
  nextPaymentDate?: number | null;
  currentPeriodEnd: number;
  trialEnd?: number | null;
  cancelAt?: number | null;
  amountDue?: number;
  paymentMethod?: PaymentMethodSummary | null;
  renewalWarning?: string | null;
  retentionCouponUsed?: boolean;
  isOnBreak?: boolean;
  onBreakAvailable?: boolean;
  onBreakPlan?: OnBreakPlanSummary | null;
  legacyReturnEntitlement?: {
    stripePriceID?: string | null;
    stripeProductID?: string | null;
    planKey?: string | null;
    planName?: string | null;
  } | null;
  onBreakReturnPlan?: {
    stripePriceID?: string | null;
    stripeProductID?: string | null;
    planKey?: string | null;
    planName?: string | null;
  } | null;
  isPaused?: boolean;
  pauseResumesAt?: number | null;
  pauseBehavior?: string | null;
  pauseStartedAt?: number | null;
  pauseNextEligibleAt?: number | null;
  pauseRequestedDurationDays?: number | null;
  pauseLastResumedAt?: number | null;
  pauseRequestedSkipCount?: number | null;
  pauseSkipsUsedInWindow?: number | null;
  pauseSkipsRemainingInWindow?: number | null;
  pauseSkipWindowStart?: number | null;
  pauseSkipWindowEnd?: number | null;
  pauseSkipWindowDays?: number | null;
  pauseNextSkipAvailableAt?: number | null;
  // Upcoming coupon/discount (from upcoming invoice)
  couponPercentOff?: number | null;
  couponAmountOff?: number | null; // cents
  couponCurrency?: string | null;
  couponTotalAmount?: number | null; // cents (sum across discounts)
  couponName?: string | null;
  // Scheduled plan change details (if any)
  scheduledTargetPriceId?: string | null;
  scheduledTargetProductId?: string | null;
  scheduledEffectiveDate?: number | null;
  scheduledAction?: 'upgrade' | 'downgrade' | null;
  scheduledIsOnBreak?: boolean;
}

interface InvoiceRow {
  id: string;
  created: number;
  object?: string;
  record_type?: string;
  number?: string;
  display_amount?: number;
  amount_paid?: number;
  total?: number;
  amount_due?: number;
  status?: string;
  hosted_invoice_url?: string;
  invoice_pdf?: string;
  receipt_url?: string;
}

interface NextExpiringSubscriptionBatch {
  batchId?: string;
  remainingCredits?: number;
  expiresAt?: number | null;
  expiresAtISO?: string | null;
}

interface SubscriptionCreditBatch {
  key?: string;
  batchId?: string | null;
  grantedCredits?: number | null;
  remainingCredits?: number | null;
  grantedAt?: number | null;
  grantedAtISO?: string | null;
  expiresAt?: number | null;
  expiresAtISO?: string | null;
  planId?: string | null;
  planName?: string | null;
  source?: string | null;
  invoiceId?: string | null;
  subscriptionId?: string | null;
}

type BillingNoticeType = 'multiple' | 'none' | 'cancelled' | 'error';

interface BillingNotice {
  type: BillingNoticeType;
  title: string;
  message: string;
  cancellationDate?: number;
}

interface CreditTopUpDeepLinkContext {
  source?: string;
  requiredCredits?: number;
  availableCredits?: number;
  shortfallCredits?: number;
  returnTo?: string;
}

type SubscriptionSelectionMode = 'renew' | 'pause' | 'resume' | 'reactivate' | 'extend' | 'onBreakMove' | 'onBreakResume' | 'cancelOnBreak';

interface SubscriptionSelectionCopy {
  title: string;
  description: string;
  primaryButton: string;
  emptySelection: string;
  icon: React.ReactNode;
}

export function selectionCopyForMode(mode: SubscriptionSelectionMode): SubscriptionSelectionCopy {
  const baseIconStyle = { fontSize: 20, marginBottom: 8 };
  switch (mode) {
    case 'pause':
      return {
        title: 'Select Subscription to Pause',
        description: 'Choose which subscription you want to pause.',
        primaryButton: 'Pause Subscription',
        emptySelection: 'Please select a subscription to pause.',
        icon: <PauseCircleOutlined style={{ ...baseIconStyle, color: 'var(--text-warning)' }} />,
      };
    case 'resume':
      return {
        title: 'Select Subscription to Resume',
        description: 'Choose which paused subscription you want to resume.',
        primaryButton: 'Resume Subscription',
        emptySelection: 'Please select a subscription to resume.',
        icon: <PlayCircleOutlined style={{ ...baseIconStyle, color: 'var(--text-success)' }} />,
      };
    case 'onBreakMove':
      return {
        title: 'Select Subscription to Move',
        description: 'Choose which subscription you want to move to On Break.',
        primaryButton: 'Move to On Break',
        emptySelection: 'Please select a subscription to move to On Break.',
        icon: <PauseCircleOutlined style={{ ...baseIconStyle, color: 'var(--text-warning)' }} />,
      };
    case 'onBreakResume':
      return {
        title: 'Select Subscription to Resume',
        description: 'Choose which On Break subscription you want to resume.',
        primaryButton: 'Resume Plan',
        emptySelection: 'Please select a subscription to resume.',
        icon: <PlayCircleOutlined style={{ ...baseIconStyle, color: 'var(--text-success)' }} />,
      };
    case 'cancelOnBreak':
      return {
        title: 'Select Scheduled Change',
        description: 'Choose which scheduled On Break change you want to cancel.',
        primaryButton: 'Cancel On Break Change',
        emptySelection: 'Please select a scheduled change to cancel.',
        icon: <CloseCircleOutlined style={{ ...baseIconStyle, color: 'var(--text-danger)' }} />,
      };
    case 'reactivate':
      return {
        title: 'Select Subscription to Reactivate',
        description: 'Choose which subscription should have auto-renew turned back on.',
        primaryButton: 'Reactivate Subscription',
        emptySelection: 'Please select a subscription to reactivate.',
        icon: <CheckCircleOutlined style={{ ...baseIconStyle, color: 'var(--text-success)' }} />,
      };
    case 'renew':
    default:
      return {
        title: 'Select Subscription to Renew',
        description: 'You have multiple renewable subscriptions. Select one to continue.',
        primaryButton: 'Continue with Renewal',
        emptySelection: 'Please select a subscription to renew.',
        icon: <RocketOutlined style={{ ...baseIconStyle, color: 'var(--text-info)' }} />,
      };
  }
}

export function getPauseTooltipMessage(
  subscriptionSummary: SubscriptionSummary | null,
  planIsAnnual: boolean,
  pauseLimitInfo: { remaining: number | null; nextAvailableAt: number | null }
): string | undefined {
  if (!subscriptionSummary) {
    return 'No active subscription to pause.';
  }
  if (subscriptionSummary.isPaused) {
    return undefined;
  }
  if (subscriptionSummary.cancelAt) {
    return 'Resume auto-renew before pausing.';
  }
  if (planIsAnnual) {
    return 'Pausing is only available for monthly subscriptions.';
  }
  if (typeof pauseLimitInfo.remaining === 'number' && pauseLimitInfo.remaining <= 0) {
    return pauseLimitInfo.nextAvailableAt
      ? `Pause limit reached. You can pause again after ${formatDate(pauseLimitInfo.nextAvailableAt)}.`
      : `Pause limit reached. You can skip up to ${MAX_PAUSE_SKIP_RENEWALS} renewals per 12 months.`;
  }
  return undefined;
}

export function getOnBreakTooltipMessage(
  subscriptionSummary: SubscriptionSummary | null,
  planIsAnnual: boolean,
): string | undefined {
  if (!subscriptionSummary) {
    return 'No active subscription to move to On Break.';
  }
  if (subscriptionSummary.cancelAt) {
    return 'Reactivate auto-renew before moving to On Break.';
  }
  if (planIsAnnual) {
    return 'On Break is available for monthly subscriptions.';
  }
  if (!subscriptionSummary.onBreakAvailable || !subscriptionSummary.onBreakPlan) {
    return 'On Break is not available for this account.';
  }
  return undefined;
}

export function getErrorMessage(error: any): string {
  if (!error?.response) {
    return 'Network error. Please check your connection and try again.';
  }
  const { status, data } = error.response;
  const detail: string = data?.detail || data?.message || '';

  switch (status) {
    case 400:
      if (detail.includes('No renewable subscription found')) {
        return 'Your subscription cannot be renewed because it is scheduled for cancellation. Please reactivate first, then try renewing.';
      }
      if (detail.includes('No subscriptions found')) {
        return 'No active subscription found. Please visit Pricing to set up a plan.';
      }
      if (detail.includes('Payment failed: Invalid or expired payment method')) {
        return 'Your payment method is invalid or expired. Please update it in Manage Subscription and try again.';
      }
      if (detail.includes('Payment failed:')) {
        return detail;
      }
      if (detail.includes('Payment requires additional authentication')) {
        return 'Your payment requires additional verification. Please update your payment method or contact your bank.';
      }
      if (detail.includes('Card declined')) {
        return 'Your card was declined. Please check with your bank or try a different payment method.';
      }
      if (detail.includes('Insufficient funds')) {
        return 'Insufficient funds on your payment method. Please add funds or use a different method.';
      }
      if (detail.includes('Stripe error:')) {
        return detail;
      }
      return detail || 'Unable to process your request. Please try again or contact support.';
    case 403:
      return 'Access denied. Please refresh and try again.';
    case 404:
      if (detail.includes('User not found')) {
        return 'Account not found. Please log out and log back in.';
      }
      return 'Subscription details not found. Please contact support.';
    case 429:
      if (detail.includes('wait approximately')) {
        return detail;
      }
      return 'You are doing that too frequently. Please wait a few minutes and try again.';
    case 500:
      return 'Server error. Please try again in a few minutes or contact support.';
    default:
      return detail || 'An unexpected error occurred. Please try again or contact support.';
  }
}

function formatDate(timestamp: number): string {
  return new Date(timestamp * 1000).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

function formatDateTimeLocal(timestamp?: number | null, tz?: string): string {
  if (timestamp == null) return 'N/A';
  const d = new Date(timestamp * 1000);
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: tz || Intl.DateTimeFormat().resolvedOptions().timeZone,
    timeZoneName: 'short'
  }).format(d);
}

function formatPlanMoney(price?: number, currency?: string): string | null {
  if (typeof price !== 'number' || !Number.isFinite(price)) {
    return null;
  }
  const normalizedCurrency = (currency || 'USD').toUpperCase();
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: normalizedCurrency,
    }).format(price);
  } catch {
    return `${normalizedCurrency} ${price.toFixed(2)}`;
  }
}

function formatPlanInterval(billingInterval?: string, intervalMonths?: number): string | null {
  const interval = (billingInterval || '').toLowerCase();
  if (interval === 'monthly' || interval === 'month' || intervalMonths === 1) {
    return 'Monthly';
  }
  if (interval === 'yearly' || interval === 'annual' || interval === 'year' || intervalMonths === 12) {
    return 'Annual';
  }
  if (typeof intervalMonths === 'number' && intervalMonths > 1) {
    return `Every ${intervalMonths} months`;
  }
  return interval ? interval.charAt(0).toUpperCase() + interval.slice(1) : null;
}

function formatPlanCredits(plan?: PlanDetails | null): string | null {
  if (!plan) {
    return null;
  }
  const intervalLabel = formatPlanInterval(plan.billingInterval, plan.intervalMonths);
  const credits =
    typeof plan.creditsPerInterval === 'number'
      ? plan.creditsPerInterval
      : typeof plan.monthlyCredits === 'number'
      ? plan.monthlyCredits
      : undefined;
  if (typeof credits !== 'number' || !Number.isFinite(credits)) {
    return null;
  }
  if (intervalLabel === 'Annual') {
    const monthlyEquivalent =
      typeof plan.monthlyEquivalentCredits === 'number' && Number.isFinite(plan.monthlyEquivalentCredits)
        ? ` (${plan.monthlyEquivalentCredits.toLocaleString()} / month equivalent)`
        : '';
    return `${credits.toLocaleString()} credits / year${monthlyEquivalent}`;
  }
  return `${credits.toLocaleString()} credits / month`;
}

const parseNonNegativeIntParam = (value: string | null): number | undefined => {
  if (value == null) return undefined;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return undefined;
  return Math.max(Math.ceil(numeric), 0);
};

export default function AccountBilling(): JSX.Element {
  const onBreakEngagementEnabled = isOnBreakEngagementEnabled();
  const { usersSharedState, setUsersSharedState } = useAccountContext();
  const navigate = useNavigate();
  const location = useLocation();

  // Detect auth readiness precisely:
  const rawUserID: string | undefined = usersSharedState?.userID;
  const authKnown: boolean = (usersSharedState as any)?.authKnown ?? rawUserID !== undefined;
  const isAuthenticated: boolean = typeof rawUserID === 'string' && rawUserID.length > 0;
  const userID: string = isAuthenticated && rawUserID ? rawUserID : '';

  const screens = useBreakpoint();
  const userTimeZone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

  // Credits and plan
  const [planDetails, setPlanDetails] = useState<PlanDetails>({});
  const [isLoadingPlanDetails, setIsLoadingPlanDetails] = useState<boolean>(false);
  const [nextExpiringBatch, setNextExpiringBatch] = useState<NextExpiringSubscriptionBatch | null>(null);
  const [subscriptionBatches, setSubscriptionBatches] = useState<SubscriptionCreditBatch[]>([]);
  const [subscriptionBatchesLoading, setSubscriptionBatchesLoading] = useState<boolean>(false);
  const [subscriptionBatchSummary, setSubscriptionBatchSummary] = useState<{ totalActive: number; totalGranted: number }>({ totalActive: 0, totalGranted: 0 });
  const [planNameMap, setPlanNameMap] = useState<Partial<Record<string, string>>>({});
  const planNameMapRef = useRef<Partial<Record<string, string>>>({});
  const pendingPlanFetchesRef = useRef<Set<string>>(new Set());
  const planIdentityRef = useRef<{ friendlyName: string; ids: string[] }>({
    friendlyName: '',
    ids: [],
  });
  const [isBatchModalVisible, setIsBatchModalVisible] = useState<boolean>(false);
  const [foreverCredits, setForeverCredits] = useState<number>(0);
  const [subscriptionCreditsRemaining, setSubscriptionCreditsRemaining] = useState<number>(0);
  const [pendingCredits, setPendingCredits] = useState<number>(0);
  const [availableCredits, setAvailableCredits] = useState<number>(0);
  const [totalCredits, setTotalCredits] = useState<number>(0);
  const [planDetailsFetched, setPlanDetailsFetched] = useState<boolean>(false);
  const toNumber = (value: any, fallback = 0) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : fallback;
  };
  const toBoolean = (value: any, fallback = false) => {
    if (value === undefined || value === null) {
      return fallback;
    }
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (['true', '1', 'yes'].includes(normalized)) return true;
      if (['false', '0', 'no'].includes(normalized)) return false;
    }
    return Boolean(value);
  };

  useEffect(() => {
    planNameMapRef.current = planNameMap;
  }, [planNameMap]);

  const ensurePlanNamesForIds = useCallback(
    async (planIds: Array<string | null | undefined>) => {
      const friendlyName = planIdentityRef.current.friendlyName;
      const normalizedKnownIds = Array.from(
        new Set(
          (planIdentityRef.current.ids || [])
            .map((id) => (typeof id === 'string' ? id.trim() : ''))
            .filter((id): id is string => Boolean(id)),
        ),
      );
      if (friendlyName && normalizedKnownIds.length > 0) {
        setPlanNameMap((prev) => {
          let changed = false;
          const next = { ...prev };
          normalizedKnownIds.forEach((id) => {
            if (!id) {
              return;
            }
            if (next[id] !== friendlyName) {
              next[id] = friendlyName;
              changed = true;
            }
          });
          return changed ? next : prev;
        });
      }
      const normalizedIds = Array.from(
        new Set(
          (planIds || [])
            .map((id) => (typeof id === 'string' ? id.trim() : ''))
            .filter((id): id is string => Boolean(id)),
        ),
      );
      const knownIdsSet = new Set(normalizedKnownIds);
      const toFetch = normalizedIds.filter(
        (id) =>
          !id.startsWith('prod_') &&
          !id.startsWith('price_') &&
          !knownIdsSet.has(id) &&
          !planNameMapRef.current[id] &&
          !pendingPlanFetchesRef.current.has(id),
      );
      if (toFetch.length === 0) {
        return;
      }
      toFetch.forEach((id) => pendingPlanFetchesRef.current.add(id));
      try {
        const results = await Promise.all(
          toFetch.map(async (planId) => {
            try {
              const response = await axios.get(`${API_BASE}/get_plan_details/${planId}`);
              const planData = response?.data ?? {};
              const friendlyNameCandidate = [planData?.planName, planData?.displayName, planData?.name]
                .map((value: unknown) => (typeof value === 'string' ? value.trim() : ''))
                .find((value) => value);
              const friendlyName =
                friendlyNameCandidate && friendlyNameCandidate.length > 0 ? friendlyNameCandidate : planId;
              return { planId, friendlyName };
            } catch (error) {
              console.error(`Failed to load plan details for ${planId}`, error);
              return { planId, friendlyName: planId };
            }
          }),
        );
        setPlanNameMap((prev) => {
          let changed = false;
          const next = { ...prev };
          results.forEach(({ planId, friendlyName }) => {
            if (!planId) {
              return;
            }
            if (next[planId] !== friendlyName) {
              next[planId] = friendlyName;
              changed = true;
            }
          });
          return changed ? next : prev;
        });
      } finally {
        toFetch.forEach((id) => pendingPlanFetchesRef.current.delete(id));
      }
    },
    [setPlanNameMap],
  );

  useEffect(() => {
    const friendlyName =
      typeof planDetails?.planName === 'string' && planDetails.planName.trim()
        ? planDetails.planName.trim()
        : '';
    const candidateIds = [
      typeof planDetails?.stripePriceID === 'string' ? planDetails.stripePriceID.trim() : '',
      typeof planDetails?.priceId === 'string' ? planDetails.priceId.trim() : '',
      typeof planDetails?.stripeProductID === 'string' ? planDetails.stripeProductID.trim() : '',
    ].filter((id): id is string => Boolean(id));
    planIdentityRef.current = { friendlyName, ids: candidateIds };
    if (!friendlyName) {
      return;
    }
    if (candidateIds.length === 0) {
      return;
    }
    setPlanNameMap((prev) => {
      let changed = false;
      const next = { ...prev };
      candidateIds.forEach((id) => {
        if (!id) {
          return;
        }
        if (next[id] !== friendlyName) {
          next[id] = friendlyName;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [planDetails.planName, planDetails.priceId, planDetails.stripePriceID, planDetails.stripeProductID]);

  const syncCreditState = (creditData: any, planData: any) => {
    const total = toNumber(creditData?.credit_balance, 0);
    const pending = toNumber(creditData?.pending_credits, 0);
    const available = toNumber(creditData?.available_balance, Math.max(0, total - pending));
    const forever = toNumber(creditData?.forever_credits, 0);
    const subscriptionRemaining = toNumber(creditData?.temp_Credits, 0);
    const billingInterval = typeof planData?.billingInterval === 'string'
      ? String(planData.billingInterval).toLowerCase()
      : 'monthly';
    const intervalMonths = toNumber(planData?.intervalMonths, billingInterval === 'yearly' ? 12 : 1) || 1;
    const creditsPerInterval = toNumber(planData?.creditsPerInterval, 0);
    const monthlyEquivalent = billingInterval === 'yearly'
      ? toNumber(planData?.monthlyEquivalentCredits, creditsPerInterval / intervalMonths)
      : toNumber(planData?.monthlyEquivalentCredits, planData?.monthlyCredits);
    const planMonthly = billingInterval === 'yearly'
      ? monthlyEquivalent
      : toNumber(planData?.monthlyCredits, monthlyEquivalent);
    const monthlyAllocated = toNumber(creditData?.monthly_credits_allocated, planMonthly);
    const planIsLegacy = toBoolean(planData?.isLegacy, false);
    const foreverDiscount = toNumber(planData?.foreverCreditDiscountPercent, 0);
    const friendlyPlanName =
      typeof planData?.planName === 'string' && planData.planName.trim()
        ? planData.planName.trim()
        : '';
    const knownPlanIds = [
      typeof planData?.stripePriceID === 'string' ? planData.stripePriceID.trim() : '',
      typeof planData?.priceId === 'string' ? planData.priceId.trim() : '',
      typeof planData?.stripeProductID === 'string' ? planData.stripeProductID.trim() : '',
    ].filter((id): id is string => Boolean(id));
    planIdentityRef.current = {
      friendlyName: friendlyPlanName,
      ids: knownPlanIds,
    };
    if (friendlyPlanName && knownPlanIds.length > 0) {
      setPlanNameMap((prev) => {
        let changed = false;
        const next = { ...prev };
        knownPlanIds.forEach((id) => {
          if (!id) {
            return;
          }
          if (next[id] !== friendlyPlanName) {
            next[id] = friendlyPlanName;
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }
    const nextExpiringRaw = creditData?.next_expiring_subscription_batch;
    if (nextExpiringRaw && typeof nextExpiringRaw === 'object') {
      const expiresAtValue = toNumber(nextExpiringRaw.expiresAt, 0);
      setNextExpiringBatch({
        batchId: typeof nextExpiringRaw.batchId === 'string' ? nextExpiringRaw.batchId : undefined,
        remainingCredits: toNumber(nextExpiringRaw.remainingCredits, 0),
        expiresAt: expiresAtValue > 0 ? expiresAtValue : null,
        expiresAtISO: typeof nextExpiringRaw.expiresAtISO === 'string' ? nextExpiringRaw.expiresAtISO : null,
      });
    } else {
      setNextExpiringBatch(null);
    }

    setTotalCredits(total);
    setPendingCredits(pending);
    setAvailableCredits(available);
    setForeverCredits(forever);
    setSubscriptionCreditsRemaining(subscriptionRemaining);
    setPlanDetails({
      planName: planData?.planName,
      monthlyCredits: planMonthly,
      tier: planData?.tier,
      monthlyCreditsAllocated: monthlyAllocated,
      billingInterval,
      intervalMonths,
      creditsPerInterval,
      monthlyEquivalentCredits: monthlyEquivalent,
      price: toNumber(planData?.price, 0),
      currency: planData?.currency,
      stripeProductID: planData?.stripeProductID,
      stripePriceID: planData?.stripePriceID,
      priceId: planData?.priceId,
      isActive: planData?.isActive,
      isLegacy: planIsLegacy,
      foreverCreditDiscountPercent: foreverDiscount,
    });

    setUsersSharedState((prev: any) => ({
      ...prev,
      credits: available,
      totalCredits: total,
      pendingCredits: pending,
    }));
  };

  const applyLiveSubscriptionPlanIdentity = (data: any) => {
    const livePriceId = typeof data?.plan_id === 'string' && data.plan_id.trim() ? data.plan_id.trim() : undefined;
    const liveProductId = typeof data?.product_id === 'string' && data.product_id.trim() ? data.product_id.trim() : undefined;
    if (!livePriceId && !liveProductId) {
      return;
    }

    const isLiveOnBreak = !!data?.is_on_break;
    const liveOnBreakPlan = data?.on_break_plan && typeof data.on_break_plan === 'object' ? data.on_break_plan : null;
    const livePlanName =
      typeof data?.plan_name === 'string' && data.plan_name.trim()
        ? data.plan_name.trim()
        : isLiveOnBreak
        ? (typeof liveOnBreakPlan?.planName === 'string' && liveOnBreakPlan.planName.trim() ? liveOnBreakPlan.planName.trim() : 'On Break')
        : undefined;

    setPlanDetails((prev) => ({
      ...prev,
      planName: livePlanName || prev.planName,
      stripePriceID: livePriceId || prev.stripePriceID,
      priceId: livePriceId || prev.priceId,
      stripeProductID: liveProductId || prev.stripeProductID,
      ...(isLiveOnBreak
        ? {
            monthlyCredits: 0,
            monthlyCreditsAllocated: 0,
            billingInterval: 'monthly',
            intervalMonths: 1,
            creditsPerInterval: 0,
            monthlyEquivalentCredits: 0,
            price: typeof liveOnBreakPlan?.price === 'number' ? liveOnBreakPlan.price : prev.price,
            currency: typeof liveOnBreakPlan?.currency === 'string' ? liveOnBreakPlan.currency : prev.currency,
            isActive: true,
            isLegacy: false,
          }
        : {}),
    }));
  };


  // Subscription summary
  const [subscriptionSummary, setSubscriptionSummary] = useState<SubscriptionSummary | null>(null);
  const [isLoadingSubscriptionSummary, setIsLoadingSubscriptionSummary] = useState<boolean>(false);
  const [subscriptionSummaryFetched, setSubscriptionSummaryFetched] = useState<boolean>(false);
  const [billingNotice, setBillingNotice] = useState<BillingNotice | null>(null);

  // New: keep a fallback cancel date if we can discover it elsewhere
  const [fallbackCancelAt, setFallbackCancelAt] = useState<number | null>(null);
  const [canRenewEarly, setCanRenewEarly] = useState<boolean | null>(null);
  const [renewalWaitMessage, setRenewalWaitMessage] = useState<string | null>(null);

  const applyRenewalEligibility = useCallback((details: any) => {
    if (details && typeof details === 'object') {
      const canRenewValue =
        typeof details.can_renew === 'boolean' ? details.can_renew : null;
      const waitMessageRaw =
        typeof details.renewal_wait_message === 'string'
          ? details.renewal_wait_message.trim()
          : '';
      setCanRenewEarly(canRenewValue);
      setRenewalWaitMessage(waitMessageRaw ? waitMessageRaw : null);
    } else {
      setCanRenewEarly(null);
      setRenewalWaitMessage(null);
    }
  }, [setCanRenewEarly, setRenewalWaitMessage]);

  const loadSubscriptionCreditBatches = useCallback(async () => {
    if (!userID) {
      setSubscriptionBatches([]);
      setSubscriptionBatchSummary({ totalActive: 0, totalGranted: 0 });
      return;
    }
    setSubscriptionBatchesLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/subscription-credit-batches`, {
        params: { userID },
      });
      const batches = Array.isArray(data?.batches) ? data.batches : [];
      const planNameSeeds: Record<string, string> = {};
      const normalized: SubscriptionCreditBatch[] = batches.map((batch: any, index: number) => {
        const coerceTimestamp = (input: any): number | null => {
          if (typeof input === 'number' && Number.isFinite(input)) {
            return input;
          }
          if (typeof input === 'string') {
            const numeric = Number(input);
            if (!Number.isNaN(numeric)) {
              return numeric;
            }
            const parsed = Date.parse(input);
            if (!Number.isNaN(parsed)) {
              return Math.floor(parsed / 1000);
            }
            return null;
          }
          if (input && typeof input === 'object' && typeof input.seconds === 'number') {
            return Number(input.seconds);
          }
          return null;
        };

        const grantedAtNumeric =
          coerceTimestamp(batch?.grantedAt);
        const expiresAtNumeric =
          coerceTimestamp(batch?.expiresAt);
        const rawPlanId = typeof batch?.planId === 'string' ? batch.planId : undefined;
        const trimmedPlanId = rawPlanId ? rawPlanId.trim() : '';
        const normalizedPlanId = trimmedPlanId || rawPlanId || undefined;
        const rawPlanName = typeof batch?.planName === 'string' ? batch.planName : undefined;
        const normalizedPlanName =
          typeof rawPlanName === 'string' && rawPlanName.trim() ? rawPlanName.trim() : undefined;
        if (normalizedPlanId && normalizedPlanName) {
          planNameSeeds[normalizedPlanId] = normalizedPlanName;
        }
        return {
          ...batch,
          planId: normalizedPlanId ?? undefined,
          planName: normalizedPlanName ?? undefined,
          key: batch?.batchId || `batch-${index}`,
          grantedCredits: Number(
            batch?.grantedCredits ??
            batch?.granted_credits ??
            batch?.grantedcredits ??
            batch?.granted
          ) || 0,
          grantedAt:
            typeof grantedAtNumeric === 'number' && Number.isFinite(grantedAtNumeric)
              ? grantedAtNumeric
              : null,
          expiresAt:
            typeof expiresAtNumeric === 'number' && Number.isFinite(expiresAtNumeric)
              ? expiresAtNumeric
              : null,
        };
      }).sort((a: SubscriptionCreditBatch, b: SubscriptionCreditBatch) => {
        const aExpires = typeof a.expiresAt === 'number' ? a.expiresAt : Number.POSITIVE_INFINITY;
        const bExpires = typeof b.expiresAt === 'number' ? b.expiresAt : Number.POSITIVE_INFINITY;
        return aExpires - bExpires;
      });
      if (Object.keys(planNameSeeds).length > 0) {
        setPlanNameMap((prev) => {
          let changed = false;
          const next = { ...prev };
          Object.entries(planNameSeeds).forEach(([id, name]) => {
            if (!id || !name) {
              return;
            }
            if (next[id] !== name) {
              next[id] = name;
              changed = true;
            }
          });
          return changed ? next : prev;
        });
      }
      setSubscriptionBatches(normalized);
      void ensurePlanNamesForIds(normalized.map((batch) => batch?.planId));
      setSubscriptionBatchSummary({
        totalActive: Number(data?.total_active_credits ?? 0),
        totalGranted: Number(data?.total_granted_credits ?? 0),
      });
    } catch (error: any) {
      console.error('Failed to load subscription credit batches:', error);
      message.error('Unable to load credit batches right now. Please try again later.');
      setSubscriptionBatches([]);
      setSubscriptionBatchSummary({ totalActive: 0, totalGranted: 0 });
    } finally {
      setSubscriptionBatchesLoading(false);
    }
  }, [ensurePlanNamesForIds, userID]);

  const subscriptionBatchColumns = useMemo<ColumnsType<SubscriptionCreditBatch>>(
    () => [
      {
        title: 'Remaining',
        dataIndex: 'remainingCredits',
        key: 'remainingCredits',
        align: 'center' as const,
        render: (value: number | undefined) => Number(value ?? 0).toLocaleString(),
      },
      {
        title: 'Granted Credits',
        dataIndex: 'grantedCredits',
        key: 'grantedCredits',
        align: 'center' as const,
        render: (value: number | undefined) => Number(value ?? 0).toLocaleString(),
      },
      {
        title: 'Granted',
        dataIndex: 'grantedAt',
        key: 'grantedAt',
        align: 'center' as const,
        render: (_: any, record) => {
          const parsedISO = record.grantedAtISO ? Date.parse(record.grantedAtISO) : NaN;
          const timestamp =
            typeof record.grantedAt === 'number'
              ? record.grantedAt
              : !Number.isNaN(parsedISO)
              ? Math.floor(parsedISO / 1000)
              : null;
          return timestamp ? formatDateTimeLocal(timestamp, userTimeZone) : '—';
        },
      },
      {
        title: 'Expires',
        dataIndex: 'expiresAt',
        key: 'expiresAt',
        align: 'center' as const,
        render: (_: any, record) => {
          const parsedISO = record.expiresAtISO ? Date.parse(record.expiresAtISO) : NaN;
          const timestamp =
            typeof record.expiresAt === 'number'
              ? record.expiresAt
              : !Number.isNaN(parsedISO)
              ? Math.floor(parsedISO / 1000)
              : null;
          return timestamp ? formatDateTimeLocal(timestamp, userTimeZone) : '—';
        },
      },
      {
        title: 'Plan',
        dataIndex: 'planId',
        key: 'planId',
        align: 'center' as const,
        render: (value: string | undefined, record) => {
          const rawPlanId =
            typeof value === 'string' && value.trim()
              ? value.trim()
              : typeof record?.planId === 'string' && record.planId.trim()
              ? record.planId.trim()
              : '';
          if (!rawPlanId) {
            return '—';
          }
          const friendlyNameFromRecord =
            typeof record?.planName === 'string' && record.planName.trim() ? record.planName.trim() : '';
          const friendlyName =
            friendlyNameFromRecord ||
            planNameMap[rawPlanId] ||
            planNameMap[rawPlanId.toLowerCase()] ||
            planNameMap[rawPlanId.toUpperCase()];
          const displayValue = friendlyName && friendlyName.length > 0 ? friendlyName : rawPlanId;
          return (
            <Tooltip title={rawPlanId}>
              <Text ellipsis style={{ maxWidth: 200, display: 'inline-block', textAlign: 'center' }}>
                {displayValue}
              </Text>
            </Tooltip>
          );
        },
      },
    ],
    [planNameMap, userTimeZone],
  );

  const handleOpenBatchesModal = useCallback(() => {
    void loadSubscriptionCreditBatches();
    setIsBatchModalVisible(true);
  }, [loadSubscriptionCreditBatches]);

  const renewTooltipMessage = useMemo(() => {
    if (subscriptionSummary?.isOnBreak) {
      return 'Resume a plan before renewing early.';
    }
    if (subscriptionSummary?.isPaused) {
      return 'Resume your subscription before renewing.';
    }
    if (subscriptionSummary?.cancelAt) {
      return 'Reactivate in Manage Subscription to renew early';
    }
    if (canRenewEarly === false) {
      return renewalWaitMessage || 'Early renewal is not available yet.';
    }
    return undefined;
  }, [subscriptionSummary?.cancelAt, subscriptionSummary?.isOnBreak, subscriptionSummary?.isPaused, canRenewEarly, renewalWaitMessage]);

  const planIsAnnual = useMemo(() => {
    const intervalMonthsValue = toNumber(planDetails.intervalMonths, 0);
    if (intervalMonthsValue >= 12) {
      return true;
    }
    const billingInterval = (planDetails.billingInterval || '').toString().toLowerCase();
    return billingInterval === 'year' || billingInterval === 'yearly' || billingInterval === 'annual';
  }, [planDetails.intervalMonths, planDetails.billingInterval]);

  const pauseLimitInfo = useMemo(() => {
    if (!subscriptionSummary) {
      return {
        used: null as number | null,
        remaining: null as number | null,
        windowEnd: null as number | null,
        nextAvailableAt: null as number | null,
      };
    }
    const used = toNumber(subscriptionSummary.pauseSkipsUsedInWindow, 0);
    const remainingRaw = subscriptionSummary.pauseSkipsRemainingInWindow;
    const remaining =
      typeof remainingRaw === 'number'
        ? Math.max(remainingRaw, 0)
        : Math.max(MAX_PAUSE_SKIP_RENEWALS - used, 0);
    const windowEnd = subscriptionSummary.pauseSkipWindowEnd ?? null;
    const nextAvailableAt = subscriptionSummary.pauseNextSkipAvailableAt ?? null;
    return { used, remaining, windowEnd, nextAvailableAt };
  }, [
    subscriptionSummary?.pauseSkipsUsedInWindow,
    subscriptionSummary?.pauseSkipsRemainingInWindow,
    subscriptionSummary?.pauseSkipWindowEnd,
    subscriptionSummary?.pauseNextSkipAvailableAt,
  ]);

  const nextRenewalLabel = useMemo(() => {
    if (!subscriptionSummary) return 'Next renewal';
    if (subscriptionSummary.trialEnd) return 'Trial ends';
    if (subscriptionSummary.cancelAt) return 'Cancels on';
    if (subscriptionSummary.isPaused) return 'Resumes on';
    return 'Next renewal';
  }, [
    subscriptionSummary?.trialEnd,
    subscriptionSummary?.cancelAt,
    subscriptionSummary?.isPaused,
  ]);

  const nextRenewalTimestamp = useMemo(() => {
    if (!subscriptionSummary) return null;
    if (subscriptionSummary.trialEnd) return subscriptionSummary.trialEnd;
    if (subscriptionSummary.cancelAt) return subscriptionSummary.cancelAt;
    if (subscriptionSummary.isPaused) {
      return (
        subscriptionSummary.pauseResumesAt ??
        subscriptionSummary.nextPaymentDate ??
        subscriptionSummary.currentPeriodEnd ??
        null
      );
    }
    return (
      subscriptionSummary.nextPaymentDate ??
      subscriptionSummary.currentPeriodEnd ??
      null
    );
  }, [
    subscriptionSummary?.trialEnd,
    subscriptionSummary?.cancelAt,
    subscriptionSummary?.isPaused,
    subscriptionSummary?.pauseResumesAt,
    subscriptionSummary?.nextPaymentDate,
    subscriptionSummary?.currentPeriodEnd,
  ]);

  const onBreakTooltipMessage = useMemo(
    () => getOnBreakTooltipMessage(subscriptionSummary, planIsAnnual),
    [subscriptionSummary, planIsAnnual]
  );

  const [isSubscriptionSelectionModalVisible, setIsSubscriptionSelectionModalVisible] = useState<boolean>(false);
  const [availableSubscriptions, setAvailableSubscriptions] = useState<any[]>([]);
  const [selectedSubscriptionCustomerId, setSelectedSubscriptionCustomerId] = useState<string | null>(null);
  const [selectionGuidance, setSelectionGuidance] = useState<string | null>(null);
  const [renewAfterSelectionImmediate, setRenewAfterSelectionImmediate] = useState<boolean>(false);
  const [reactivateAfterSelectionImmediate, setReactivateAfterSelectionImmediate] = useState<boolean>(false);
  const [isReactivating, setIsReactivating] = useState<boolean>(false);
  const [subscriptionSelectionMode, setSubscriptionSelectionMode] = useState<SubscriptionSelectionMode | null>(null);
  const [pauseAfterSelectionImmediate, setPauseAfterSelectionImmediate] = useState<boolean>(false);
  const [resumeAfterSelectionImmediate, setResumeAfterSelectionImmediate] = useState<boolean>(false);
  const [extendAfterSelectionImmediate, setExtendAfterSelectionImmediate] = useState<boolean>(false);
  const [pendingPauseSkips, setPendingPauseSkips] = useState<number | null>(null);
  const [pendingExtendSkips, setPendingExtendSkips] = useState<number | null>(null);
  const [isPauseModalVisible, setIsPauseModalVisible] = useState<boolean>(false);
  const [isExtendPauseModalVisible, setIsExtendPauseModalVisible] = useState<boolean>(false);
  const [selectedPauseSkips, setSelectedPauseSkips] = useState<number>(1);
  const [selectedExtendPauseSkips, setSelectedExtendPauseSkips] = useState<number>(1);
  const [isPausing, setIsPausing] = useState<boolean>(false);
  const [isExtendingPause, setIsExtendingPause] = useState<boolean>(false);
  const [isResuming, setIsResuming] = useState<boolean>(false);
  const [isResumeConfirmVisible, setIsResumeConfirmVisible] = useState<boolean>(false);
  const [isOnBreakModalVisible, setIsOnBreakModalVisible] = useState<boolean>(false);
  const [onBreakAction, setOnBreakAction] = useState<'move' | 'resume'>('move');
  const [selectedOnBreakTiming, setSelectedOnBreakTiming] = useState<'now' | 'next_cycle'>('now');
  const [isChangingOnBreak, setIsChangingOnBreak] = useState<boolean>(false);
  const [onBreakAfterSelectionImmediate, setOnBreakAfterSelectionImmediate] = useState<boolean>(false);
  const [pendingOnBreakChange, setPendingOnBreakChange] = useState<{
    action: 'move' | 'resume';
    timing: 'now' | 'next_cycle';
    targetPriceId: string | null;
    targetProductId: string | null;
    cancellationFeedback?: CancellationFeedbackPayload | null;
    survey?: OnBreakSurveyPayload | null;
    idempotencyKey: string;
  } | null>(null);
  const [isCancellingScheduledOnBreak, setIsCancellingScheduledOnBreak] = useState<boolean>(false);
  const [cancelOnBreakAfterSelectionImmediate, setCancelOnBreakAfterSelectionImmediate] = useState<boolean>(false);
  const [onBreakReturnPlanDetails, setOnBreakReturnPlanDetails] = useState<PlanDetails | null>(null);
  const [isLoadingOnBreakReturnPlanDetails, setIsLoadingOnBreakReturnPlanDetails] = useState<boolean>(false);
  const [selectedOnBreakStage, setSelectedOnBreakStage] = useState<OnBreakStage | null>(null);
  const [selectedOnBreakExpectedReturn, setSelectedOnBreakExpectedReturn] = useState<OnBreakExpectedReturn | null>(null);
  const [onBreakConfirmation, setOnBreakConfirmation] = useState<OnBreakConfirmation | null>(null);
  const [isOnBreakConfirmationVisible, setIsOnBreakConfirmationVisible] = useState<boolean>(false);

  const waitForBillingSync = useCallback(async () => {
    await delay(BILLING_SYNC_DELAY_MS);
  }, []);

  const selectionCopyForMode = useCallback(
    (mode: SubscriptionSelectionMode): SubscriptionSelectionCopy => {
      const baseIconStyle = { fontSize: 20, marginBottom: 8 };
      switch (mode) {
        case 'pause':
          return {
            title: 'Select Subscription to Pause',
            description: 'Choose which subscription you want to pause.',
            primaryButton: 'Pause Subscription',
            emptySelection: 'Please select a subscription to pause.',
            icon: <PauseCircleOutlined style={{ ...baseIconStyle, color: 'var(--text-warning)' }} />,
          };
        case 'resume':
          return {
            title: 'Select Subscription to Resume',
            description: 'Choose which paused subscription you want to resume.',
            primaryButton: 'Resume Subscription',
            emptySelection: 'Please select a subscription to resume.',
            icon: <PlayCircleOutlined style={{ ...baseIconStyle, color: 'var(--text-success)' }} />,
          };
        case 'onBreakMove':
          return {
            title: 'Select Subscription to Move',
            description: 'Choose which subscription you want to move to On Break.',
            primaryButton: 'Move to On Break',
            emptySelection: 'Please select a subscription to move to On Break.',
            icon: <PauseCircleOutlined style={{ ...baseIconStyle, color: 'var(--text-warning)' }} />,
          };
        case 'onBreakResume':
          return {
            title: 'Select Subscription to Resume',
            description: 'Choose which On Break subscription you want to resume.',
            primaryButton: 'Resume Plan',
            emptySelection: 'Please select a subscription to resume.',
            icon: <PlayCircleOutlined style={{ ...baseIconStyle, color: 'var(--text-success)' }} />,
          };
        case 'cancelOnBreak':
          return {
            title: 'Select Scheduled Change',
            description: 'Choose which scheduled On Break change you want to cancel.',
            primaryButton: 'Cancel On Break Change',
            emptySelection: 'Please select a scheduled change to cancel.',
            icon: <CloseCircleOutlined style={{ ...baseIconStyle, color: 'var(--text-danger)' }} />,
          };
        case 'extend':
          return {
            title: 'Select Subscription to Extend',
            description: 'Choose which paused subscription you want to extend.',
            primaryButton: 'Extend Pause',
            emptySelection: 'Please select a subscription to extend.',
            icon: <ArrowRightOutlined style={{ ...baseIconStyle, color: 'var(--text-warning)' }} />,
          };
        case 'reactivate':
          return {
            title: 'Select Subscription to Reactivate',
            description: 'Choose which subscription should have auto-renew turned back on.',
            primaryButton: 'Reactivate Subscription',
            emptySelection: 'Please select a subscription to reactivate.',
            icon: <CheckCircleOutlined style={{ ...baseIconStyle, color: 'var(--text-success)' }} />,
          };
        case 'renew':
        default:
          return {
            title: 'Select Subscription to Renew',
            description: 'You have multiple renewable subscriptions. Select one to continue.',
            primaryButton: 'Continue with Renewal',
            emptySelection: 'Please select a subscription to renew.',
            icon: <RocketOutlined style={{ ...baseIconStyle, color: 'var(--text-info)' }} />,
          };
      }
    },
    [],
  );

  const pauseButtonDisabled =
    !subscriptionSummary ||
    isLoadingSubscriptionSummary ||
    (!subscriptionSummary.isPaused &&
      (isPausing ||
        !!subscriptionSummary.cancelAt ||
        planIsAnnual ||
        (typeof pauseLimitInfo.remaining === 'number' && pauseLimitInfo.remaining <= 0)));

  const isOnBreakSubscription = Boolean(subscriptionSummary?.isOnBreak);
  const isStripePausedRecovery = Boolean(subscriptionSummary?.isPaused && !isOnBreakSubscription);
  const isOnBreakScheduled = Boolean(subscriptionSummary?.scheduledIsOnBreak);
  const onBreakPlan = subscriptionSummary?.onBreakPlan || null;
  const onBreakPlanPrice = formatPlanMoney(
    onBreakPlan?.price ?? undefined,
    onBreakPlan?.currency ?? undefined,
  );
  const onBreakStartTimestamp =
    [
      subscriptionSummary?.scheduledEffectiveDate,
      subscriptionSummary?.pauseResumesAt,
      subscriptionSummary?.nextPaymentDate,
      subscriptionSummary?.currentPeriodEnd,
    ].find(
      (timestamp): timestamp is number =>
        typeof timestamp === 'number' && Number.isFinite(timestamp) && timestamp > 0,
    ) ?? null;
  const onBreakStartLabel = onBreakStartTimestamp ? formatDate(onBreakStartTimestamp) : 'your next billing date';
  const onBreakReturnTarget =
    subscriptionSummary?.legacyReturnEntitlement ||
    subscriptionSummary?.onBreakReturnPlan ||
    null;
  const onBreakReturnPlanLookupId =
    onBreakReturnTarget?.stripePriceID ||
    onBreakReturnTarget?.stripeProductID ||
    null;
  const onBreakReturnPlanPrice = formatPlanMoney(
    onBreakReturnPlanDetails?.price,
    onBreakReturnPlanDetails?.currency,
  );
  const onBreakReturnPlanInterval = formatPlanInterval(
    onBreakReturnPlanDetails?.billingInterval,
    onBreakReturnPlanDetails?.intervalMonths,
  );
  const onBreakReturnPlanCredits = formatPlanCredits(onBreakReturnPlanDetails);
  const onBreakBaseActionDisabled =
    isLoadingSubscriptionSummary ||
    isChangingOnBreak ||
    isCancellingScheduledOnBreak ||
    !subscriptionSummary ||
    !userID ||
    !!subscriptionSummary.cancelAt;
  const moveToOnBreakDisabled =
    onBreakBaseActionDisabled ||
    !subscriptionSummary?.onBreakAvailable ||
    !onBreakPlan ||
    planIsAnnual;
  const resumeOnBreakDisabled = onBreakBaseActionDisabled || !isOnBreakSubscription;
  const resumePausedSubscriptionDisabled =
    isLoadingSubscriptionSummary ||
    isResuming ||
    !subscriptionSummary?.isPaused ||
    !userID;
  const cancelScheduledOnBreakDisabled =
    isLoadingSubscriptionSummary ||
    isChangingOnBreak ||
    isCancellingScheduledOnBreak ||
    !subscriptionSummary ||
    !userID;
  const canOfferOnBreakCancellationSave = useCallback(
    (summary?: SubscriptionSummary | null): boolean =>
      Boolean(
        summary &&
          !summary.isOnBreak &&
          !summary.scheduledIsOnBreak &&
          !summary.isPaused &&
          !summary.cancelAt &&
          summary.onBreakAvailable &&
          summary.onBreakPlan &&
          !planIsAnnual
      ),
    [planIsAnnual],
  );
  const showOnBreakCancellationSave = canOfferOnBreakCancellationSave(subscriptionSummary);
  const showRetentionDiscountSave = Boolean(subscriptionSummary && !subscriptionSummary.retentionCouponUsed);

  useEffect(() => {
    let cancelled = false;

    const loadReturnPlanDetails = async () => {
      if (!isOnBreakModalVisible || onBreakAction !== 'resume' || !onBreakReturnPlanLookupId || !userID) {
        setOnBreakReturnPlanDetails(null);
        setIsLoadingOnBreakReturnPlanDetails(false);
        return;
      }

      setIsLoadingOnBreakReturnPlanDetails(true);
      try {
        const headers = await getAuthHeaders();
        const response = await axios.get(`${API_BASE}/get_plan_details/${onBreakReturnPlanLookupId}`, {
          params: {
            ...(onBreakReturnTarget?.stripePriceID ? { priceId: onBreakReturnTarget.stripePriceID } : {}),
            userID,
            surface: 'account',
          },
          headers,
        });
        if (!cancelled) {
          setOnBreakReturnPlanDetails(response.data || null);
        }
      } catch {
        if (!cancelled) {
          setOnBreakReturnPlanDetails(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingOnBreakReturnPlanDetails(false);
        }
      }
    };

    loadReturnPlanDetails();

    return () => {
      cancelled = true;
    };
  }, [
    isOnBreakModalVisible,
    onBreakAction,
    onBreakReturnPlanLookupId,
    onBreakReturnTarget?.stripePriceID,
    userID,
  ]);

  const canExtendPause =
    !!subscriptionSummary?.isPaused &&
    typeof pauseLimitInfo.remaining === 'number' &&
    pauseLimitInfo.remaining > 0;

  const currentSelectionMode: SubscriptionSelectionMode =
    subscriptionSelectionMode ?? 'renew';
  const selectionModalCopy = useMemo(
    () => selectionCopyForMode(currentSelectionMode),
    [currentSelectionMode]
  );

  const extractSelectionGuidance = (payload: any, fallback?: string | null): string | null => {
    const primary = typeof payload?.guidance === 'string' ? payload.guidance.trim() : '';
    if (primary) {
      return primary;
    }
    const secondary = typeof payload?.message === 'string' ? payload.message.trim() : '';
    if (secondary) {
      return secondary;
    }
    const fallbackText = typeof fallback === 'string' ? fallback.trim() : '';
    return fallbackText || null;
  };

  const openSubscriptionSelectionModal = (
    payload: any,
    mode: SubscriptionSelectionMode,
    guidanceFallback?: string | null
  ): void => {
    setAvailableSubscriptions(Array.isArray(payload?.customers) ? payload.customers : []);
    setSelectionGuidance(extractSelectionGuidance(payload, guidanceFallback));
    setSubscriptionSelectionMode(mode);
    setSelectedSubscriptionCustomerId(null);
    setIsSubscriptionSelectionModalVisible(true);
  };

  const currentPauseEligibleLabel = useMemo(() => {
    if (!subscriptionSummary?.pauseNextSkipAvailableAt) return null;
    return formatDate(subscriptionSummary.pauseNextSkipAvailableAt);
  }, [subscriptionSummary?.pauseNextSkipAvailableAt]);

  const currentPauseEndsLabel = useMemo(() => {
    if (!subscriptionSummary?.pauseResumesAt) return null;
    return formatDate(subscriptionSummary.pauseResumesAt);
  }, [subscriptionSummary?.pauseResumesAt]);

  // Billing portal selection
  const [isCustomerSelectionModalVisible, setIsCustomerSelectionModalVisible] = useState<boolean>(false);
  const [availableCustomers, setAvailableCustomers] = useState<any[]>([]);
  const [isLoadingCustomerPortal, setIsLoadingCustomerPortal] = useState<boolean>(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);

  // Renew Now flow
  const [isRenewModalVisible, setIsRenewModalVisible] = useState<boolean>(false);
  const [isRenewing, setIsRenewing] = useState<boolean>(false);
  const [renewalAmount, setRenewalAmount] = useState<number | null>(null);
  const [renewalPlanName, setRenewalPlanName] = useState<string>('');
  const [isLoadingRenewalDetails, setIsLoadingRenewalDetails] = useState<boolean>(false);
  const [renewalErrorMessage, setRenewalErrorMessage] = useState<string | null>(null);

  // Renew flow visual feedback (Toast + small overlay modal)
  const RENEW_TOAST_KEY = 'renewing';
  const [isRenewFlowActive, setIsRenewFlowActive] = useState<boolean>(false);

  // Invoices
  const [isInvoicesModalVisible, setIsInvoicesModalVisible] = useState<boolean>(false);
  const [invoices, setInvoices] = useState<InvoiceRow[]>([]);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState<boolean>(false);
  const [invoicesError, setInvoicesError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  // Credit Top Up modal state
  const [isGetCreditsModalVisible, setIsGetCreditsModalVisible] = useState<boolean>(false);
  const [creditTopUpContext, setCreditTopUpContext] = useState<CreditTopUpDeepLinkContext | null>(null);
  // Guided tour state
  const [billingTourOpen, setBillingTourOpen] = useState<boolean>(false);
  const [billingTourStep, setBillingTourStep] = useState<number>(0);
  const [billingTourFlagsChecked, setBillingTourFlagsChecked] = useState<boolean>(false);
  const [billingTourShouldOpen, setBillingTourShouldOpen] = useState<boolean>(false);
  const [billingTourSeen, setBillingTourSeen] = useState<boolean>(false);
  const tourFallbackRef = useRef<HTMLElement | null>(null);
  const tourOpenedBatchModalRef = useRef<boolean>(false);



  // Retention and cancellation state
  const [isRetentionModalVisible, setIsRetentionModalVisible] = useState<boolean>(false);
  const [retentionChoice, setRetentionChoice] = useState<'25_NEXT' | '15_3'>('25_NEXT');
  const [isApplyingRetention, setIsApplyingRetention] = useState<boolean>(false);
  const [retentionError, setRetentionError] = useState<string | null>(null);
  const [isCancelReasonModalVisible, setIsCancelReasonModalVisible] = useState<boolean>(false);
  const [cancelFlowNextStep, setCancelFlowNextStep] = useState<'retention' | 'cancel' | null>(null);
  const [isCancelConfirmVisible, setIsCancelConfirmVisible] = useState<boolean>(false);
  const [isCancelling, setIsCancelling] = useState<boolean>(false);
  const [cancelReasonCode, setCancelReasonCode] = useState<CancelReasonCode | null>(null);
  const [cancelReasonText, setCancelReasonText] = useState<string>('');

  // Plans data/state

  const goToPricing = (): void => {
    navigate('/account/subscription');
  };

  const resetCancelFeedbackState = useCallback(() => {
    setCancelReasonCode(null);
    setCancelReasonText('');
  }, []);

  const trimmedCancelReasonText = cancelReasonText.trim();
  const isCancelFeedbackValid = Boolean(
    cancelReasonCode && (cancelReasonCode !== 'other' || trimmedCancelReasonText)
  );
  const buildCancellationFeedbackPayload = useCallback(
    (outcome: CancelFlowOutcome): CancellationFeedbackPayload | null => {
      if (!cancelReasonCode || (cancelReasonCode === 'other' && !trimmedCancelReasonText)) {
        return null;
      }
      return {
        cancelReasonCode,
        cancelReasonText: trimmedCancelReasonText,
        cancelFlowOutcome: outcome,
      };
    },
    [cancelReasonCode, trimmedCancelReasonText],
  );

  const renderCancellationFeedbackSection = () => (
    <section className="account-billing-modal-block account-billing-modal-block--muted">
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>
            Why are you leaving?
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Please choose one reason before continuing.
          </Text>
        </div>

        <Radio.Group
          onChange={(e) => setCancelReasonCode(e.target.value as CancelReasonCode)}
          value={cancelReasonCode}
          style={{ width: '100%' }}
        >
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            {CANCEL_REASON_OPTIONS.map((option) => (
              <Radio key={option.value} value={option.value}>
                {option.label}
              </Radio>
            ))}
          </Space>
        </Radio.Group>

        <div>
          <TextArea
            value={cancelReasonText}
            onChange={(e) => setCancelReasonText(e.target.value)}
            placeholder={
              cancelReasonCode === 'other'
                ? 'Please tell us more about why you are leaving.'
                : 'Anything else you want to share? (optional)'
            }
            rows={4}
            style={{
              background: 'var(--surface-base)',
              color: 'var(--text-main)',
              border: '1px solid var(--border-subtle)',
            }}
          />
          <Text type={cancelReasonCode === 'other' && !trimmedCancelReasonText ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>
            {cancelReasonCode === 'other'
              ? 'Free text is required when you select Other.'
            : 'Free text is optional.'}
          </Text>
        </div>
      </Space>
    </section>
  );

  useEffect(() => {
    setPlanDetailsFetched(false);
    setSubscriptionSummaryFetched(false);
    setBillingTourFlagsChecked(false);
    setBillingTourSeen(false);
    setBillingTourShouldOpen(false);
    setBillingTourOpen(false);
    setBillingTourStep(0);
    setIsBatchModalVisible(false);
    tourOpenedBatchModalRef.current = false;
    setIsPauseModalVisible(false);
    setIsExtendPauseModalVisible(false);
    setPauseAfterSelectionImmediate(false);
    setExtendAfterSelectionImmediate(false);
    setPendingPauseSkips(null);
    setPendingExtendSkips(null);
    setIsResumeConfirmVisible(false);
    setIsOnBreakModalVisible(false);
    setOnBreakAction('move');
    setSelectedOnBreakTiming('now');
    setOnBreakAfterSelectionImmediate(false);
    setPendingOnBreakChange(null);
    setCancelOnBreakAfterSelectionImmediate(false);
  }, [userID]);

  const billingDataReady = useMemo(() => {
    if (!isAuthenticated) return false;
    return (
      planDetailsFetched &&
      subscriptionSummaryFetched &&
      !isLoadingPlanDetails &&
      !isLoadingSubscriptionSummary &&
      !subscriptionBatchesLoading
    );
  }, [
    isAuthenticated,
    planDetailsFetched,
    subscriptionSummaryFetched,
    isLoadingPlanDetails,
    isLoadingSubscriptionSummary,
    subscriptionBatchesLoading
  ]);

  const billingActionsDisabled = !billingDataReady;

  useEffect(() => {
    if (!isAuthenticated || !billingDataReady) {
      return;
    }

    const params = new URLSearchParams(location.search);
    if (params.get('openTopUp') !== '1') {
      return;
    }

    const requiredCredits = parseNonNegativeIntParam(params.get('requiredCredits'));
    const availableCredits = parseNonNegativeIntParam(params.get('availableCredits'));
    const providedShortfall = parseNonNegativeIntParam(params.get('shortfallCredits'));
    const computedShortfall =
      requiredCredits !== undefined && availableCredits !== undefined
        ? Math.max(requiredCredits - availableCredits, 0)
        : undefined;
    const shortfallCredits = computedShortfall ?? providedShortfall;
    const source = (params.get('source') || '').trim();
    const returnToRaw = params.get('returnTo');
    const returnTo = source === 'atticus'
      ? atticusQuoteReturnPath(returnToRaw)
      : returnToRaw && returnToRaw.trim() ? returnToRaw.trim() : undefined;

    const nextContext: CreditTopUpDeepLinkContext = {};
    if (source) nextContext.source = source;
    if (requiredCredits !== undefined) nextContext.requiredCredits = requiredCredits;
    if (availableCredits !== undefined) nextContext.availableCredits = availableCredits;
    if (shortfallCredits !== undefined) nextContext.shortfallCredits = shortfallCredits;
    if (returnTo) nextContext.returnTo = returnTo;
    setCreditTopUpContext(Object.keys(nextContext).length > 0 ? nextContext : null);
    setIsGetCreditsModalVisible(true);

    [
      'openTopUp',
      'source',
      'requiredCredits',
      'availableCredits',
      'shortfallCredits',
      'returnTo',
    ].forEach((key) => params.delete(key));
    const nextSearch = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
      },
      { replace: true }
    );
  }, [billingDataReady, isAuthenticated, location.pathname, location.search, navigate]);

  const extendButtonDisabled =
    billingActionsDisabled ||
    !subscriptionSummary?.isPaused ||
    !canExtendPause ||
    isExtendingPause;

  const foreverDiscountPercent = useMemo(
    () => toNumber(planDetails.foreverCreditDiscountPercent, 0),
    [planDetails.foreverCreditDiscountPercent]
  );

  // Load credits and plan details
  useEffect(() => {
    const fetchDetails = async () => {
      if (!userID) {
        setPlanDetailsFetched(false);
        return;
      }
      setIsLoadingPlanDetails(true);
      setPlanDetailsFetched(false);
      try {
        const creditResponse = await axios.get(`${API_BASE}/check_credit_balance/`, {
          params: { userID }
        });

        const planProductID = typeof creditResponse.data.planProductID === 'string' ? creditResponse.data.planProductID : undefined;
        const planPriceID = typeof creditResponse.data.planPriceID === 'string' ? creditResponse.data.planPriceID : undefined;
        const lookupField = typeof creditResponse.data.planLookupID === 'string' ? creditResponse.data.planLookupID : undefined;
        const planIDRaw: any = creditResponse.data.planID;
        const priceIDRaw: any = creditResponse.data.priceID;
        const fallbackPriceID: string | undefined =
          typeof priceIDRaw === 'string' && priceIDRaw
            ? priceIDRaw
            : undefined;
        const fallbackPlanID: string | undefined =
          typeof planIDRaw === 'string' && planIDRaw
            ? planIDRaw
            : (typeof planIDRaw === 'number' && planIDRaw !== 0 ? String(planIDRaw) : undefined);
        const lookupId = planPriceID || fallbackPriceID || lookupField || fallbackPlanID || planProductID;
        const resolvedPriceId = planPriceID || fallbackPriceID;

        const creditData = creditResponse.data;
        let planData: any = {};
        if (lookupId) {
          try {
            const headers = await getAuthHeaders();
            const planResponse = await axios.get(`${API_BASE}/get_plan_details/${lookupId}`, {
              params: { ...(resolvedPriceId ? { priceId: resolvedPriceId } : {}), userID, surface: 'account' },
              headers,
            });
            const appliedPriceId =
              resolvedPriceId ||
              (typeof planResponse.data?.stripePriceID === 'string' ? planResponse.data.stripePriceID : undefined);
            planData = { ...(planResponse.data || {}), priceId: appliedPriceId };
          } catch (planError) {
            console.warn(`Failed to load plan details for ${lookupId}`, planError);
            planData = { priceId: resolvedPriceId ?? undefined };
          }
        }
        syncCreditState(creditData, planData);
        await loadSubscriptionCreditBatches();
      } catch (error) {
        console.error('Failed to fetch plan details:', error);
      } finally {
        setIsLoadingPlanDetails(false);
        setPlanDetailsFetched(true);
      }
    };
    if (authKnown && isAuthenticated) {
      fetchDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authKnown, isAuthenticated, applyRenewalEligibility, loadSubscriptionCreditBatches]); // run only once auth is known and the user is authenticated

  // Load subscription summary (fetch once per userID)
  useEffect(() => {
    if (!userID) return;
    setSubscriptionSummaryFetched(false);
    let cancelled = false;
    const controller = new AbortController();
    const didFetchUserSubsRef = { current: false }; // guards fallback call within this run
    setIsLoadingSubscriptionSummary(true); // keep previous data to avoid flicker

    const fetchSubscriptionSummary = async () => {
      try {
        const headers = await getAuthHeaders();
        const { data } = await axios.get(
          `${API_BASE}/get-subscription-details`,
          { params: { userID }, signal: controller.signal, headers }
        );

        if (cancelled) return;

        applyRenewalEligibility(data);

        if (data.multiple_customers) {
          setBillingNotice({
            type: 'multiple',
            title: 'Multiple subscriptions detected',
            message:
              'We found more than one subscription on your account. Use Manage Subscription to choose which one to view or update.'
          });
          return;
        }

        if (typeof data.cancel_at === 'number') {
          setFallbackCancelAt(data.cancel_at);
        }

        if (data.status === 'cancelled') {
          const whenRaw =
            (typeof data.cancellation_date === 'number' ? data.cancellation_date : null) ??
            (typeof data.cancel_at === 'number' ? data.cancel_at : null) ??
            (typeof fallbackCancelAt === 'number' ? fallbackCancelAt : null);
          const hasPassed = typeof whenRaw === 'number' ? whenRaw * 1000 < Date.now() : true;
          setBillingNotice({
            type: 'cancelled',
            title: hasPassed ? 'Subscription cancelled' : 'Subscription scheduled for cancellation',
            message: hasPassed
              ? (whenRaw
                  ? `Your subscription was cancelled on ${formatDate(whenRaw)}. You can reactivate or start a new plan from Manage Subscription.`
                  : 'Your subscription has been cancelled. You can reactivate or start a new plan from Manage Subscription.')
              : (whenRaw
                  ? `Your subscription is scheduled to cancel on ${formatDate(whenRaw)}.`
                  : 'Your subscription is scheduled for cancellation.')
          });
          return;
        }

        applyLiveSubscriptionPlanIdentity(data);

        if (data.status && (data.current_period_end || data.next_payment_date || data.plan_id || data.product_id)) {
            const summary: SubscriptionSummary = {
              status: data.status,
              planName: data.plan_name || 'Plan',
              nextPaymentDate: data.next_payment_date ?? null,
              currentPeriodEnd: data.current_period_end ?? (data.next_payment_date ?? 0),
              trialEnd: data.trial_end ?? null,
              cancelAt: data.cancel_at ?? null,
              amountDue: typeof data.amount_due === 'number' ? data.amount_due : undefined,
              paymentMethod: data.default_payment_method
                ? {
                    brand: data.default_payment_method.brand,
                    last4: data.default_payment_method.last4,
                    exp_month: data.default_payment_method.exp_month,
                    exp_year: data.default_payment_method.exp_year
                  }
                : null,
              renewalWarning: data.renewal_proximity_warning ?? null,
              // Scheduled plan change details (if any)
              scheduledTargetPriceId: data.scheduled_target_price_id ?? null,
              scheduledTargetProductId: data.scheduled_target_product_id ?? null,
              scheduledEffectiveDate: data.scheduled_effective_date ?? null,
              scheduledAction: data.scheduled_action ?? null,
              scheduledIsOnBreak: !!data.scheduled_is_on_break,
              retentionCouponUsed: !!data.retention_coupon_used,
              isOnBreak: !!data.is_on_break,
              onBreakAvailable: !!data.on_break_available,
              onBreakPlan: data.on_break_plan ?? null,
              legacyReturnEntitlement: data.legacy_return_entitlement ?? null,
              onBreakReturnPlan: data.on_break_return_plan ?? null,
              isPaused: !!data.is_paused,
              pauseResumesAt:
                typeof data.pause_resumes_at === 'number' ? data.pause_resumes_at : null,
              pauseBehavior: data.pause_behavior ?? null,
              pauseStartedAt:
                typeof data.pause_started_at === 'number' ? data.pause_started_at : null,
              pauseNextEligibleAt:
                typeof data.pause_next_eligible_at === 'number' ? data.pause_next_eligible_at : null,
              pauseRequestedDurationDays:
                typeof data.pause_requested_duration_days === 'number'
                  ? data.pause_requested_duration_days
                  : null,
              pauseLastResumedAt:
                typeof data.pause_last_resumed_at === 'number'
                  ? data.pause_last_resumed_at
                  : null,
              pauseRequestedSkipCount:
                typeof data.pause_requested_skip_count === 'number'
                  ? data.pause_requested_skip_count
                  : null,
              pauseSkipsUsedInWindow:
                typeof data.pause_skips_used_in_window === 'number'
                  ? data.pause_skips_used_in_window
                  : null,
              pauseSkipsRemainingInWindow:
                typeof data.pause_skips_remaining_in_window === 'number'
                  ? data.pause_skips_remaining_in_window
                  : null,
              pauseSkipWindowStart:
                typeof data.pause_skip_window_start === 'number'
                  ? data.pause_skip_window_start
                  : null,
              pauseSkipWindowEnd:
                typeof data.pause_skip_window_end === 'number'
                  ? data.pause_skip_window_end
                  : null,
              pauseSkipWindowDays:
                typeof data.pause_skip_window_days === 'number'
                  ? data.pause_skip_window_days
                  : null,
              pauseNextSkipAvailableAt:
                typeof data.pause_next_skip_available_at === 'number'
                  ? data.pause_next_skip_available_at
                  : null,
              // Upcoming coupon/discount (optional)
              couponPercentOff:
                typeof data.upcoming_discount_percent_off === 'number'
                  ? data.upcoming_discount_percent_off
                  : null,
              couponAmountOff:
                typeof data.upcoming_discount_amount_off === 'number'
                  ? data.upcoming_discount_amount_off
                  : null,
              couponCurrency: data.upcoming_discount_currency ?? null,
              couponTotalAmount:
                typeof data.upcoming_discount_total_amount === 'number'
                  ? data.upcoming_discount_total_amount
                  : null,
              couponName: data.upcoming_coupon_name ?? null
            };
          setSubscriptionSummary(summary);
          return;
        }

        setBillingNotice({
          type: 'none',
          title: 'No active subscription',
          message:
            'There is no active subscription on your account right now. Go to Pricing to set up a plan.'
        });
      } catch (error: any) {
        if (cancelled) return;
        console.error('Failed to fetch subscription summary:', error);

        const status: number | undefined = error?.response?.status;
        const detail: string = error?.response?.data?.detail || '';

        const isNoRenewable =
          detail.includes('No renewable subscription found') ||
          detail.includes('No renewable subscriptions found');

        if (status === 400 && isNoRenewable) {
          // Best-effort: fetch subscriptions ONCE to distinguish fully cancelled vs pending cancel
          try {
            if ((didFetchUserSubsRef as any).current) throw new Error('already fetched');
            (didFetchUserSubsRef as any).current = true;
            const subsResp = await axios.get(`${API_BASE}/get-user-subscriptions`, {
              params: { userID }
            });
            const subs: any[] = Array.isArray(subsResp?.data?.subscriptions) ? subsResp.data.subscriptions : [];

            // Prefer a fully cancelled subscription if present
            const cancelledSub = subs.find((s: any) => s.status === 'canceled' || s.status === 'cancelled');
            if (cancelledSub) {
              const when =
                (typeof cancelledSub.canceled_at === 'number' ? cancelledSub.canceled_at : null) ??
                (typeof cancelledSub.cancel_at === 'number' ? cancelledSub.cancel_at : null) ??
                null;
              if (typeof when === 'number') setFallbackCancelAt(when);
              setBillingNotice({
                type: 'cancelled',
                title: 'Subscription cancelled',
                message: when
                  ? `Your subscription was cancelled on ${formatDate(when)}. You can reactivate or start a new plan from Manage Subscription.`
                  : 'Your subscription has been cancelled. You can reactivate or start a new plan from Manage Subscription.',
                cancellationDate: when ?? undefined
              });
              return;
            }

            // Otherwise, look for a scheduled cancellation on a renewable sub
            const scheduledSub = subs.find(
              (s: any) =>
                typeof s.cancel_at === 'number' &&
                (s.status === 'active' || s.status === 'trialing' || s.status === 'past_due')
            );
            if (scheduledSub) {
              const when: number = scheduledSub.cancel_at;
              setFallbackCancelAt(when);
              const hasPassed = when * 1000 < Date.now();
              setBillingNotice({
                type: 'cancelled',
                title: hasPassed ? 'Subscription cancelled' : 'Subscription scheduled for cancellation',
                message: hasPassed
                  ? `Your subscription was cancelled on ${formatDate(when)}. You can reactivate or start a new plan from Manage Subscription.`
                  : `Your subscription is scheduled to cancel on ${formatDate(when)}.`,
                cancellationDate: when
              });
              return;
            }

            // No renewable or scheduled subs found — treat as no active subscription
            setBillingNotice({
              type: 'none',
              title: 'No active subscription',
              message: 'Looks like you do not have an active subscription at the moment. Go to Pricing to start a plan.'
            });
            return;
          } catch (e) {
            // Fall back to using any known cancel date if we have it
          }

          const when = fallbackCancelAt ?? null;
          const hasPassed = typeof when === 'number' ? when * 1000 < Date.now() : false;
          setBillingNotice({
            type: 'cancelled',
            title: hasPassed ? 'Subscription cancelled' : 'Subscription scheduled for cancellation',
            message: hasPassed
              ? (when
                  ? `Your subscription was cancelled on ${formatDate(when)}. You can reactivate or start a new plan from Manage Subscription.`
                  : 'Your subscription has been cancelled. You can reactivate or start a new plan from Manage Subscription.')
              : (when
                  ? `Your subscription is scheduled to cancel on ${formatDate(when)}. Use Manage Subscription to reactivate if you want to keep your plan.`
                  : 'Your subscription is scheduled for cancellation. Use Manage Subscription to reactivate if you want to keep your plan.'),
            cancellationDate: when ?? undefined
          });
        } else if (
          status === 400 &&
          (detail.includes('User does not have a Stripe customer ID') || detail.includes('No subscriptions found'))
        ) {
          setBillingNotice({
            type: 'none',
            title: 'No active subscription',
            message:
              'Looks like you do not have an active subscription at the moment. Go to Pricing to start a plan.'
          });
        } else {
          setBillingNotice({
            type: 'error',
            title: 'Unable to load billing details',
            message: getErrorMessage(error)
          });
        }
      } finally {
        if (!cancelled) {
          setIsLoadingSubscriptionSummary(false);
          setSubscriptionSummaryFetched(true);
        }
      }
    };

    fetchSubscriptionSummary();
    return () => {
      cancelled = true;
      controller.abort();
    };
  // IMPORTANT: we intentionally depend only on userID to avoid re-fetch loops
  }, [userID, applyRenewalEligibility]);

  useEffect(() => {
    if (!userID) return;
    if (billingTourFlagsChecked) return;
    let cancelled = false;

    const fetchTourFlags = async () => {
      try {
        const res = await axios.get(`${API_BASE}/user/ui-flags`, {
          params: { userId: userID }
        });
        if (cancelled) return;
        const seen = !!res.data?.uiFlags?.accountBillingTourSeen;
        setBillingTourSeen(seen);
        if (!seen) {
          setBillingTourShouldOpen(true);
        }
      } catch (_err) {
        if (cancelled) return;
        setBillingTourSeen(false);
      } finally {
        if (!cancelled) {
          setBillingTourFlagsChecked(true);
        }
      }
    };

    fetchTourFlags();
    return () => {
      cancelled = true;
    };
  }, [userID, billingTourFlagsChecked]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const pending = window.sessionStorage.getItem(ACCOUNT_BILLING_TOUR_STORAGE_KEY);
    if (pending) {
      window.sessionStorage.removeItem(ACCOUNT_BILLING_TOUR_STORAGE_KEY);
      setBillingTourShouldOpen(true);
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleTourRequest = (event: Event) => {
      const manual = !!(event as CustomEvent)?.detail?.manual;
      setBillingTourShouldOpen(true);
    };
    window.addEventListener(ACCOUNT_BILLING_TOUR_EVENT, handleTourRequest as EventListener);
    return () => {
      window.removeEventListener(ACCOUNT_BILLING_TOUR_EVENT, handleTourRequest as EventListener);
    };
  }, []);

  useEffect(() => {
    if (!billingTourFlagsChecked) return;
    if (!billingTourShouldOpen) return;
    if (!billingDataReady) return;
    setBillingTourOpen(true);
    setBillingTourStep(0);
    setBillingTourShouldOpen(false);
  }, [billingTourShouldOpen, billingDataReady, billingTourFlagsChecked]);

  const resolveTourTarget = useCallback(
    (element?: HTMLElement | null): HTMLElement => {
      if (element) return element;
      if (typeof document !== 'undefined' && document.body) {
        return document.body;
      }
      if (!tourFallbackRef.current) {
        tourFallbackRef.current =
          typeof document !== 'undefined' ? document.createElement('div') : ({} as HTMLElement);
      }
      return tourFallbackRef.current as HTMLElement;
    },
    []
  );

  const getTourElement = useCallback((id: string) => {
    if (typeof document === 'undefined') return null;
    return document.querySelector(`[data-tour-id="${id}"]`) as HTMLElement | null;
  }, []);

  const renderTeacherPanel = useCallback(
    (content: React.ReactNode) => renderAppTourPanel(content),
    []
  );

  const handleBillingTourChange = useCallback((nextStep: number) => {
    if (nextStep === 9 && !isBatchModalVisible) {
      tourOpenedBatchModalRef.current = true;
      setIsBatchModalVisible(true);
      if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(() => setBillingTourStep(nextStep));
      } else {
        setTimeout(() => setBillingTourStep(nextStep), 0);
      }
      return;
    }

    if (nextStep !== 9 && tourOpenedBatchModalRef.current) {
      setIsBatchModalVisible(false);
      tourOpenedBatchModalRef.current = false;
    }

    setBillingTourStep(nextStep);
  }, [isBatchModalVisible, setIsBatchModalVisible]);

  const handleBillingTourClose = useCallback(async () => {
    setBillingTourOpen(false);
    setBillingTourStep(0);
    setBillingTourShouldOpen(false);
    if (tourOpenedBatchModalRef.current) {
      setIsBatchModalVisible(false);
      tourOpenedBatchModalRef.current = false;
    }
    if (!billingTourSeen) {
      setBillingTourSeen(true);
      if (userID) {
        try {
          await axios.post(`${API_BASE}/user/ui-flags/set`, {
            userId: userID,
            flags: { accountBillingTourSeen: true }
          });
        } catch (_err) {
          // Non-blocking — failure just means we may show the tour again later.
        }
      }
    }
  }, [billingTourSeen, setIsBatchModalVisible, userID]);

  const billingTourSteps = useMemo<TourProps['steps']>(() => {
    const targetOrFallback = (primaryId: string, secondaryId?: string) =>
      resolveTourTarget(
        getTourElement(primaryId) ?? (secondaryId ? getTourElement(secondaryId) : null)
      );

    const steps: NonNullable<TourProps['steps']> = [
      {
        title: 'Plan & credits overview',
        description: renderTeacherPanel(
          <>
            <div>This card keeps your subscription details and credit status front and center. Check it for a quick overview of the status of your credits.</div>
          </>
        ),
        target: () => targetOrFallback('billing-plan-overview'),
        placement: 'right'
      },
      {
        title: 'Top up credits',
        description: renderTeacherPanel(
          <>
            <div>The Credit Top Up button adds extra credits beyond your subscription allotment.</div>
            <div>Use top-ups for spikes or one-off projects. Subscription credits are the protected monthly/annual balance tied to your plan.</div>
          </>
        ),
        target: () => targetOrFallback('billing-credit-top-up'),
        placement: 'left'
      },
      {
        title: 'Subscription insights',
        description: renderTeacherPanel(
          <>
            <div>This panel summarizes renewal timing, payment status, and pause details.</div>
            <div>If something needs attention, you’ll see a clear notice in this spot.</div>
          </>
        ),
        target: () => targetOrFallback('billing-subscription-summary', 'billing-subscription-notice'),
        placement: 'left'
      },
      {
        title: 'Manage subscription',
        description: renderTeacherPanel(
          <>
            <div>Jump to the subscription tab to change plans, adjust add-ons, or reactivate service.</div>
            <div>Most billing edits open Stripe’s portal for secure updates; credit history and batch details stay in-app.</div>
          </>
        ),
        target: () => targetOrFallback('billing-action-manage-subscription'),
        placement: 'left'
      },
      {
        title: 'Renew on demand',
        description: renderTeacherPanel(
          <>
            <div>Trigger an immediate renewal when you want the next billing cycle to start right away.</div>
            <div>The button stays disabled when early renewal isn’t available.</div>
          </>
        ),
        target: () => targetOrFallback('billing-action-renew-now'),
        placement: 'left'
      },
      {
        title: 'Payment methods',
        description: renderTeacherPanel(
          <>
            <div>Open Stripe’s customer portal to add, replace, or remove saved payment methods. If the action is disabled, this account has no portal-ready customer record yet.</div>
          </>
        ),
        target: () => targetOrFallback('billing-action-manage-payment'),
        placement: 'left'
      },
      {
        title: 'Invoices & receipts',
        description: renderTeacherPanel(
          <>
            <div>Download past invoices and receipts without leaving the billing tab.</div>
          </>
        ),
        target: () => targetOrFallback('billing-action-invoices'),
        placement: 'left'
      },
      {
        title: 'Pause or resume',
        description: renderTeacherPanel(
          <>
            <div>Temporarily pause your subscription or resume when you’re ready to continue service. Availability depends on the current plan state.</div>
          </>
        ),
        target: () => targetOrFallback('billing-action-pause-resume'),
        placement: 'left'
      },
      {
        title: 'Track subscription credits',
        description: renderTeacherPanel(
          <>
            <div>Open the subscription credits view to see your protected subscription credit balance.</div>
            <div>Subscription credits do not expire while subscribed and remain usable for 60 days after cancellation. Top-up credits are separate.</div>
          </>
        ),
        target: () => targetOrFallback('billing-action-subscription-credits'),
        placement: 'left'
      },
      {
        title: 'Credit batch details',
        description: renderTeacherPanel(
          <>
            <div>This table breaks down every batch, showing remaining credits and protection status.</div>
            <div>Use it when planning renewals or monitoring usage.</div>
          </>
        ),
        target: () => targetOrFallback('billing-subscription-credits-modal'),
        placement: 'top'
      },
      {
        title: 'Credit history',
        description: renderTeacherPanel(
          <>
            <div>Click here to jump to your detailed credit history.</div>
          </>
        ),
        target: () => targetOrFallback('billing-action-credit-history'),
        placement: 'left'
      },
      {
        title: 'Cancel subscription',
        description: renderTeacherPanel(
          <>
            <div>Start the cancellation workflow when you need to end service. This stays disabled when there is no active subscription to cancel.</div>
          </>
        ),
        target: () => targetOrFallback('billing-action-cancel-subscription', 'billing-action-credit-history'),
        placement: 'left'
      }
    ];

    return steps;
  }, [getTourElement, renderTeacherPanel, resolveTourTarget]);


  const launchBillingPortal = useCallback((url: string) => {
    if (typeof window === 'undefined') return;
    window.location.assign(url);
  }, []);

  // Billing portal
  const handleBilling = async (customerID?: string) => {
    if (!userID) return;
    setIsLoadingCustomerPortal(true);
    try {
      const response = await axios.post(
        `${API_BASE}/create-customer-portal-session`,
        { userID, customerID }
      );

        if (response.data.multiple_customers) {
        const customersWithActiveSubs = response.data.customers.filter(
          (customer: any) => customer.has_active_subscription
        );
        if (customersWithActiveSubs.length === 0) {
          message.info('No active subscriptions found. Opening Subscription tab...');
          goToPricing();
          return;
        }
        setAvailableCustomers(customersWithActiveSubs);
        setIsCustomerSelectionModalVisible(true);
      } else if (response.data?.url) {
        launchBillingPortal(response.data.url);
      } else {
        message.error('Unable to access billing portal: No session URL returned.');
      }
    } catch (error) {
      console.error('Failed to open customer portal:', error);
      const errorMessage = getErrorMessage(error);
      message.error(`Unable to access billing portal: ${errorMessage}`);
    } finally {
      setIsLoadingCustomerPortal(false);
    }
  };

  // Credit Top Up helpers
  const handleCreditTopUpClick = () => {
    if (!userID) return;
    setIsGetCreditsModalVisible(true);
  };

  const buildOnBreakSurveyPayload = (): OnBreakSurveyPayload | null => {
    if (!onBreakEngagementEnabled) {
      return null;
    }
    if (!selectedOnBreakStage || !selectedOnBreakExpectedReturn) {
      return null;
    }
    return {
      onBreakStage: selectedOnBreakStage,
      onBreakExpectedReturn: selectedOnBreakExpectedReturn,
      onBreakFlowVersion: 1,
    };
  };

  const showOnBreakSuccess = (
    data: any,
    survey: OnBreakSurveyPayload,
    fallbackEffectiveAt?: number | null,
  ): void => {
    const responseConfirmation = data?.on_break_confirmation;
    const confirmation: OnBreakConfirmation = responseConfirmation && typeof responseConfirmation === 'object'
      ? {
          status: responseConfirmation.status === 'active' ? 'active' : 'scheduled',
          intent_id: responseConfirmation.intent_id,
          effective_at: responseConfirmation.effective_at ?? fallbackEffectiveAt ?? null,
          follow_up_at: responseConfirmation.follow_up_at ?? null,
          credits_protected: responseConfirmation.credits_protected !== false,
          resource_keys: Array.isArray(responseConfirmation.resource_keys)
            ? responseConfirmation.resource_keys
            : ON_BREAK_RESOURCE_KEYS_BY_STAGE[survey.onBreakStage],
        }
      : {
          status: 'scheduled',
          effective_at: fallbackEffectiveAt ?? data?.effective_date ?? null,
          follow_up_at: null,
          credits_protected: true,
          resource_keys: ON_BREAK_RESOURCE_KEYS_BY_STAGE[survey.onBreakStage],
        };
    setOnBreakConfirmation(confirmation);
    setIsOnBreakConfirmationVisible(true);
  };

  const openMoveToOnBreakModal = () => {
    if (!subscriptionSummary) {
      message.info('No active subscription found.');
      return;
    }
    if (subscriptionSummary.cancelAt) {
      message.info('Reactivate auto-renew before moving to On Break.');
      return;
    }
    if (!subscriptionSummary.onBreakAvailable || !subscriptionSummary.onBreakPlan) {
      message.info('On Break is not available for this account.');
      return;
    }
    setSelectedOnBreakStage(null);
    setSelectedOnBreakExpectedReturn(null);
    setOnBreakAction('move');
    setSelectedOnBreakTiming('next_cycle');
    setIsOnBreakModalVisible(true);
  };

  const openResumeFromOnBreakModal = () => {
    if (!isOnBreakSubscription) {
      message.info('Your subscription is not on break.');
      return;
    }
    setOnBreakAction('resume');
    setSelectedOnBreakTiming('now');
    setIsOnBreakModalVisible(true);
  };

  const confirmOnBreakPlanChange = async () => {
    if (!userID) return;
    const targetPriceId =
      onBreakAction === 'move'
        ? (onBreakPlan?.stripePriceID || null)
        : (onBreakReturnTarget?.stripePriceID || null);
    const targetProductId =
      onBreakAction === 'move'
        ? (onBreakPlan?.stripeProductID || null)
        : (targetPriceId ? (onBreakReturnTarget?.stripeProductID || null) : null);
    if (onBreakAction === 'resume' && !targetPriceId) {
      message.info('Choose a current plan from the Subscription page.');
      setIsOnBreakModalVisible(false);
      navigate('/account/subscription');
      return;
    }
    if (!targetPriceId && !targetProductId) {
      message.info('Choose a plan to resume from the Subscription page.');
      setIsOnBreakModalVisible(false);
      navigate('/account/subscription');
      return;
    }
    const effectiveTiming = onBreakAction === 'move' ? 'next_cycle' : selectedOnBreakTiming;
    const survey = onBreakAction === 'move' ? buildOnBreakSurveyPayload() : null;
    if (onBreakAction === 'move' && onBreakEngagementEnabled && !survey) {
      message.info('Please answer both On Break questions before continuing.');
      return;
    }
    const idempotencyKey = `${userID}-${targetPriceId || targetProductId}-${Date.now()}`;
    setIsChangingOnBreak(true);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(`${API_BASE}/change-subscription-plan`, {
        userID,
        timing: effectiveTiming,
        targetPriceId,
        targetProductId,
        idempotencyKey,
        ...(survey || {}),
      }, {
        headers,
      });
      const data = response?.data || {};
      if (data.multiple_customers) {
        setPendingOnBreakChange({
          action: onBreakAction,
          timing: effectiveTiming,
          targetPriceId,
          targetProductId,
          survey,
          idempotencyKey,
        });
        setOnBreakAfterSelectionImmediate(true);
        openSubscriptionSelectionModal(data, onBreakAction === 'move' ? 'onBreakMove' : 'onBreakResume');
        setIsOnBreakModalVisible(false);
        return;
      }
      const detail = String(data.message || '');
      if (onBreakAction === 'move') {
        message.success(detail || 'On Break scheduled for your next billing date.');
        if (survey) {
          showOnBreakSuccess(data, survey, data.effective_date ?? onBreakStartTimestamp);
        }
      } else {
        message.success(detail || (selectedOnBreakTiming === 'now' ? 'Plan resumed.' : 'Plan resume scheduled for your next billing date.'));
      }
      setIsOnBreakModalVisible(false);
      await waitForBillingSync();
      await refreshSubscriptionSummary();
      await fetchUpdatedPlanDetails();
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.payment_intent_client_secret) {
        try {
          const stripe = await getStripePromise();
          if (!stripe) {
            message.error('Payment service is unavailable. Please try again later.');
          } else {
            const result = await stripe.confirmCardPayment(detail.payment_intent_client_secret);
            if (result.error) {
              message.error(result.error.message || 'Payment authentication failed. Please try again.');
            } else {
              message.success(onBreakAction === 'move' ? 'Moved to On Break.' : 'Plan resumed.');
              setIsOnBreakModalVisible(false);
              await waitForBillingSync();
              await refreshSubscriptionSummary();
              await fetchUpdatedPlanDetails();
            }
          }
        } catch {
          message.error('Payment authentication failed. Please try again.');
        }
      } else {
        message.error(getErrorMessage(error));
      }
    } finally {
      setIsChangingOnBreak(false);
    }
  };

  const moveToOnBreakFromCancellation = async () => {
    if (!userID || !subscriptionSummary || !canOfferOnBreakCancellationSave(subscriptionSummary)) {
      message.info('On Break is not available for this account.');
      return;
    }
    const cancellationFeedback = buildCancellationFeedbackPayload('retained');
    if (!cancellationFeedback) {
      message.info('Please choose a cancellation reason before continuing.');
      return;
    }
    const survey = buildOnBreakSurveyPayload();
    if (onBreakEngagementEnabled && !survey) {
      message.info('Please answer both On Break questions before continuing.');
      return;
    }
    const targetPriceId = onBreakPlan?.stripePriceID || null;
    const targetProductId = onBreakPlan?.stripeProductID || null;
    if (!targetPriceId && !targetProductId) {
      message.info('On Break is not available for this account.');
      return;
    }

    const idempotencyKey = `${userID}-${targetPriceId || targetProductId}-cancel-save-${Date.now()}`;
    setIsChangingOnBreak(true);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(`${API_BASE}/change-subscription-plan`, {
        userID,
        timing: 'next_cycle',
        targetPriceId,
        targetProductId,
        idempotencyKey,
        ...cancellationFeedback,
        ...(survey || {}),
      }, {
        headers,
      });
      const data = response?.data || {};
      if (data.multiple_customers) {
        setPendingOnBreakChange({
          action: 'move',
          timing: 'next_cycle',
          targetPriceId,
          targetProductId,
          cancellationFeedback,
          survey,
          idempotencyKey,
        });
        setOnBreakAfterSelectionImmediate(true);
        openSubscriptionSelectionModal(data, 'onBreakMove');
        setIsRetentionModalVisible(false);
        return;
      }
      message.success(String(data.message || '') || 'On Break scheduled for your next billing date.');
      if (survey) {
        showOnBreakSuccess(data, survey, data.effective_date ?? onBreakStartTimestamp);
      }
      setIsRetentionModalVisible(false);
      setCancelFlowNextStep(null);
      resetCancelFeedbackState();
      await waitForBillingSync();
      await refreshSubscriptionSummary();
      await fetchUpdatedPlanDetails();
    } catch (error: any) {
      message.error(getErrorMessage(error));
    } finally {
      setIsChangingOnBreak(false);
    }
  };

  const cancelScheduledOnBreakChange = async (customerID?: string | null) => {
    if (!userID) return;
    const toastKey = 'cancel-on-break-change';
    setIsCancellingScheduledOnBreak(true);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(`${API_BASE}/cancel-scheduled-plan-change`, {
        userID,
        ...(customerID ? { customerID } : {}),
      }, {
        headers,
      });
      const data = response?.data || {};
      if (data.multiple_customers) {
        setCancelOnBreakAfterSelectionImmediate(true);
        openSubscriptionSelectionModal(data, 'cancelOnBreak');
        return;
      }
      message.success({ content: data.message || 'Scheduled On Break change canceled.', key: toastKey });
      await waitForBillingSync();
      await refreshSubscriptionSummary();
      await fetchUpdatedPlanDetails();
    } catch (error: any) {
      message.error({ content: getErrorMessage(error), key: toastKey });
    } finally {
      setIsCancellingScheduledOnBreak(false);
    }
  };

  const handleOpenPauseModal = () => {
    if (!subscriptionSummary) {
      message.info('No active subscription to pause.');
      return;
    }
    if (subscriptionSummary.isPaused) {
      message.info('Your subscription is already paused.');
      return;
    }
    if (subscriptionSummary.cancelAt) {
      message.info('Reactivate auto-renew before pausing.');
      return;
    }
    if (planIsAnnual) {
      message.info('Pausing is only available for monthly subscriptions.');
      return;
    }
    if (typeof pauseLimitInfo.remaining === 'number' && pauseLimitInfo.remaining <= 0) {
      if (pauseLimitInfo.nextAvailableAt) {
        message.info(`Pause limit reached. You can pause again after ${formatDate(pauseLimitInfo.nextAvailableAt)}.`);
      } else {
        message.info(`Pause limit reached. You can skip up to ${MAX_PAUSE_SKIP_RENEWALS} renewals per 12 months.`);
      }
      return;
    }
    const preferredSkips = subscriptionSummary.pauseRequestedSkipCount;
    const maxSelectable =
      typeof pauseLimitInfo.remaining === 'number'
        ? Math.max(1, Math.min(pauseLimitInfo.remaining, MAX_PAUSE_SKIP_RENEWALS))
        : MAX_PAUSE_SKIP_RENEWALS;
    const initialSelection =
      preferredSkips && [1, 2, 3].includes(preferredSkips)
        ? Math.min(preferredSkips, maxSelectable)
        : Math.min(maxSelectable, MAX_PAUSE_SKIP_RENEWALS);
    setSelectedPauseSkips(initialSelection);
    setIsPauseModalVisible(true);
  };

  const handleOpenExtendPauseModal = () => {
    if (!subscriptionSummary?.isPaused) {
      message.info('Your subscription is not currently paused.');
      return;
    }
    if (!canExtendPause) {
      message.info('No pause skips remaining to extend the current pause.');
      return;
    }
    const maxSelectable = Math.max(1, Math.min(pauseLimitInfo.remaining ?? MAX_PAUSE_SKIP_RENEWALS, MAX_PAUSE_SKIP_RENEWALS));
    setSelectedExtendPauseSkips(Math.min(maxSelectable, MAX_PAUSE_SKIP_RENEWALS));
    setIsExtendPauseModalVisible(true);
  };

  const handleConfirmPause = async () => {
    if (!userID) return;
    setIsPausing(true);
    const skipCount = typeof selectedPauseSkips === 'number' ? selectedPauseSkips : 1;
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(`${API_BASE}/pause-subscription`, {
        userID,
        skipRenewals: skipCount,
      }, {
        headers,
      });
      const data = response?.data || {};
      if (data.multiple_customers) {
        setPauseAfterSelectionImmediate(true);
        setPendingPauseSkips(skipCount);
        setRenewalErrorMessage(null);
        openSubscriptionSelectionModal(data, 'pause');
        setIsPauseModalVisible(false);
        return;
      }
      const pausedUntil: number | null =
        typeof data.paused_until === 'number' ? data.paused_until : null;
      const pauseStartedAt =
        typeof data.pause_started_at === 'number'
          ? data.pause_started_at
          : Math.floor(Date.now() / 1000);
      if (pausedUntil) {
        message.success(`Subscription paused until ${formatDate(pausedUntil)}.`);
      } else {
        message.success('Subscription paused.');
      }
      setSubscriptionSummary((prev) => {
        if (!prev) return prev;
        const nextRemaining =
          typeof prev.pauseSkipsRemainingInWindow === 'number'
            ? Math.max(prev.pauseSkipsRemainingInWindow - skipCount, 0)
            : prev.pauseSkipsRemainingInWindow ?? null;
        const nextUsed =
          typeof prev.pauseSkipsUsedInWindow === 'number'
            ? prev.pauseSkipsUsedInWindow + skipCount
            : prev.pauseSkipsUsedInWindow ?? null;
        return {
          ...prev,
          isPaused: true,
          pauseResumesAt: pausedUntil ?? prev.pauseResumesAt ?? null,
          pauseStartedAt: pauseStartedAt ?? prev.pauseStartedAt ?? null,
          pauseRequestedSkipCount: skipCount,
          pauseSkipsRemainingInWindow: nextRemaining ?? prev.pauseSkipsRemainingInWindow ?? null,
          pauseSkipsUsedInWindow: nextUsed ?? prev.pauseSkipsUsedInWindow ?? null,
        };
      });
      setIsPauseModalVisible(false);
      setPauseAfterSelectionImmediate(false);
      setPendingPauseSkips(null);
      try {
        await waitForBillingSync();
        await refreshSubscriptionSummary();
        await fetchUpdatedPlanDetails();
      } catch (refreshError) {
        console.error('Failed to refresh subscription details after pause:', refreshError);
        message.warning('Subscription paused, but we could not refresh the latest details. Please refresh the page to confirm.');
      }
    } catch (error: any) {
      message.error(getErrorMessage(error));
      setPauseAfterSelectionImmediate(false);
      setPendingPauseSkips(null);
    } finally {
      setIsPausing(false);
      setSelectedSubscriptionCustomerId(null);
    }
  };

  const handleConfirmExtendPause = async () => {
    if (!userID) return;
    if (!subscriptionSummary?.isPaused) {
      message.info('Your subscription is not currently paused.');
      setIsExtendPauseModalVisible(false);
      return;
    }
    setIsExtendingPause(true);
    const skipCount = typeof selectedExtendPauseSkips === 'number' ? selectedExtendPauseSkips : 1;
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(`${API_BASE}/extend-pause`, {
        userID,
        skipRenewals: skipCount,
      }, {
        headers,
      });
      const data = response?.data || {};
      if (data.multiple_customers) {
        setExtendAfterSelectionImmediate(true);
        setPendingExtendSkips(skipCount);
        setRenewalErrorMessage(null);
        openSubscriptionSelectionModal(data, 'extend');
        setIsExtendPauseModalVisible(false);
        return;
      }
      const pausedUntil: number | null =
        typeof data.paused_until === 'number' ? data.paused_until : null;
      const totalRequestedSkips: number | null =
        typeof data.total_requested_skip_count === 'number' ? data.total_requested_skip_count : null;
      if (pausedUntil) {
        message.success(`Pause extended until ${formatDate(pausedUntil)}.`);
      } else {
        message.success('Pause extended.');
      }
      setSubscriptionSummary((prev) => {
        if (!prev) return prev;
        const nextRemaining =
          typeof prev.pauseSkipsRemainingInWindow === 'number'
            ? Math.max(prev.pauseSkipsRemainingInWindow - skipCount, 0)
            : prev.pauseSkipsRemainingInWindow ?? null;
        const nextUsed =
          typeof prev.pauseSkipsUsedInWindow === 'number'
            ? prev.pauseSkipsUsedInWindow + skipCount
            : prev.pauseSkipsUsedInWindow ?? null;
        const nextRequested =
          typeof totalRequestedSkips === 'number'
            ? totalRequestedSkips
            : typeof prev.pauseRequestedSkipCount === 'number'
            ? prev.pauseRequestedSkipCount + skipCount
            : skipCount;
        return {
          ...prev,
          isPaused: true,
          pauseResumesAt: pausedUntil ?? prev.pauseResumesAt ?? null,
          pauseRequestedSkipCount: nextRequested,
          pauseSkipsRemainingInWindow: nextRemaining ?? prev.pauseSkipsRemainingInWindow ?? null,
          pauseSkipsUsedInWindow: nextUsed ?? prev.pauseSkipsUsedInWindow ?? null,
        };
      });
      setIsExtendPauseModalVisible(false);
      setExtendAfterSelectionImmediate(false);
      setPendingExtendSkips(null);
      try {
        await waitForBillingSync();
        await refreshSubscriptionSummary();
        await fetchUpdatedPlanDetails();
      } catch (refreshError) {
        console.error('Failed to refresh subscription details after extending pause:', refreshError);
        message.warning('Pause extended, but we could not refresh the latest details. Please refresh the page to confirm.');
      }
    } catch (error: any) {
      message.error(getErrorMessage(error));
      setExtendAfterSelectionImmediate(false);
      setPendingExtendSkips(null);
    } finally {
      setIsExtendingPause(false);
      setSelectedSubscriptionCustomerId(null);
    }
  };

  const handleResumeClick = () => {
    if (!subscriptionSummary?.isPaused) {
      message.info('Your subscription is not currently paused.');
      return;
    }
    setIsResumeConfirmVisible(true);
  };

  const resumeSubscriptionRequest = async () => {
    if (!userID) return;
    if (!subscriptionSummary?.isPaused) {
      message.info('Your subscription is not currently paused.');
      return;
    }
    setIsResuming(true);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(`${API_BASE}/resume-subscription`, { userID }, { headers });
      const data = response?.data || {};
      if (data.multiple_customers) {
        setResumeAfterSelectionImmediate(true);
        setRenewalErrorMessage(null);
        openSubscriptionSelectionModal(data, 'resume');
        return;
      }
      message.success('Subscription resumed.');
      setResumeAfterSelectionImmediate(false);
      try {
        await waitForBillingSync();
        await refreshSubscriptionSummary();
        await fetchUpdatedPlanDetails();
      } catch (refreshError) {
        console.error('Failed to refresh subscription details after resume:', refreshError);
        message.warning('Subscription resumed, but we could not refresh the latest details. Please refresh the page to confirm.');
      }
    } catch (error: any) {
      message.error(getErrorMessage(error));
      setResumeAfterSelectionImmediate(false);
    } finally {
      setIsResuming(false);
      if (!resumeAfterSelectionImmediate) {
        setSelectedSubscriptionCustomerId(null);
      }
    }
  };

  // Retention / Cancel helpers
  const refreshSubscriptionSummary = async (): Promise<SubscriptionSummary | null> => {
    if (!userID) {
      setSubscriptionSummaryFetched(false);
      return null;
    }
    setSubscriptionSummaryFetched(false);
    setIsLoadingSubscriptionSummary(true);
    try {
      const headers = await getAuthHeaders();
      const { data } = await axios.get(`${API_BASE}/get-subscription-details`, {
        params: { userID },
        headers
      });
      applyRenewalEligibility(data);
      if (typeof data.cancel_at === 'number') {
        setFallbackCancelAt(data.cancel_at);
      }
      applyLiveSubscriptionPlanIdentity(data);

      if (data.status && (data.current_period_end || data.next_payment_date || data.plan_id || data.product_id)) {
        const summary: SubscriptionSummary = {
          status: data.status,
          planName: data.plan_name || planDetails.planName || 'Plan',
          nextPaymentDate: data.next_payment_date ?? null,
          currentPeriodEnd: data.current_period_end ?? (data.next_payment_date ?? 0),
          trialEnd: data.trial_end ?? null,
          cancelAt: data.cancel_at ?? null,
          amountDue: typeof data.amount_due === 'number' ? data.amount_due : undefined,
          paymentMethod: data.default_payment_method
            ? {
                brand: data.default_payment_method.brand,
                last4: data.default_payment_method.last4,
                exp_month: data.default_payment_method.exp_month,
                exp_year: data.default_payment_method.exp_year
              }
            : null,
          renewalWarning: data.renewal_proximity_warning ?? null,
          // Scheduled plan change details (if any)
          scheduledTargetPriceId: data.scheduled_target_price_id ?? null,
          scheduledTargetProductId: data.scheduled_target_product_id ?? null,
          scheduledEffectiveDate: data.scheduled_effective_date ?? null,
          scheduledAction: data.scheduled_action ?? null,
          scheduledIsOnBreak: !!data.scheduled_is_on_break,
          retentionCouponUsed: !!data.retention_coupon_used,
          isOnBreak: !!data.is_on_break,
          onBreakAvailable: !!data.on_break_available,
          onBreakPlan: data.on_break_plan ?? null,
          legacyReturnEntitlement: data.legacy_return_entitlement ?? null,
          onBreakReturnPlan: data.on_break_return_plan ?? null,
          isPaused: !!data.is_paused,
          pauseResumesAt:
            typeof data.pause_resumes_at === 'number' ? data.pause_resumes_at : null,
          pauseBehavior: data.pause_behavior ?? null,
          pauseStartedAt:
            typeof data.pause_started_at === 'number' ? data.pause_started_at : null,
          pauseNextEligibleAt:
            typeof data.pause_next_eligible_at === 'number' ? data.pause_next_eligible_at : null,
          pauseRequestedDurationDays:
            typeof data.pause_requested_duration_days === 'number'
              ? data.pause_requested_duration_days
              : null,
          pauseLastResumedAt:
            typeof data.pause_last_resumed_at === 'number'
              ? data.pause_last_resumed_at
              : null,
          pauseRequestedSkipCount:
            typeof data.pause_requested_skip_count === 'number'
              ? data.pause_requested_skip_count
              : null,
          pauseSkipsUsedInWindow:
            typeof data.pause_skips_used_in_window === 'number'
              ? data.pause_skips_used_in_window
              : null,
          pauseSkipsRemainingInWindow:
            typeof data.pause_skips_remaining_in_window === 'number'
              ? data.pause_skips_remaining_in_window
              : null,
          pauseSkipWindowStart:
            typeof data.pause_skip_window_start === 'number'
              ? data.pause_skip_window_start
              : null,
          pauseSkipWindowEnd:
            typeof data.pause_skip_window_end === 'number'
              ? data.pause_skip_window_end
              : null,
          pauseSkipWindowDays:
            typeof data.pause_skip_window_days === 'number'
              ? data.pause_skip_window_days
              : null,
          pauseNextSkipAvailableAt:
            typeof data.pause_next_skip_available_at === 'number'
              ? data.pause_next_skip_available_at
              : null,
          // Upcoming coupon/discount (optional)
          couponPercentOff:
            typeof data.upcoming_discount_percent_off === 'number'
              ? data.upcoming_discount_percent_off
              : null,
          couponAmountOff:
            typeof data.upcoming_discount_amount_off === 'number'
              ? data.upcoming_discount_amount_off
              : null,
          couponCurrency: data.upcoming_discount_currency ?? null,
          couponTotalAmount:
            typeof data.upcoming_discount_total_amount === 'number'
              ? data.upcoming_discount_total_amount
              : null,
          couponName: data.upcoming_coupon_name ?? null
        };
        setSubscriptionSummary(summary);
        return summary;
      }
      return null;
    } catch (e) {
      return null;
    } finally {
      setIsLoadingSubscriptionSummary(false);
      setSubscriptionSummaryFetched(true);
    }
  };

  const handleCancelSubscriptionClick = async () => {
    setRetentionError(null);
    resetCancelFeedbackState();
    setCancelFlowNextStep(null);
    setSelectedOnBreakStage(null);
    setSelectedOnBreakExpectedReturn(null);
    const latest = await refreshSubscriptionSummary();
    if (!latest) {
      message.info('No active subscription to cancel.');
      return;
    }
    const hasOnBreakSave = canOfferOnBreakCancellationSave(latest);
    const hasDiscountSave = !latest.retentionCouponUsed;
    if (hasDiscountSave) {
      setRetentionChoice('25_NEXT');
    }
    if (hasOnBreakSave || hasDiscountSave) {
      setCancelFlowNextStep('retention');
    } else {
      setCancelFlowNextStep('cancel');
    }
    setIsCancelReasonModalVisible(true);
  };

  const continueCancelFlow = () => {
    if (!isCancelFeedbackValid || !cancelFlowNextStep) return;
    setIsCancelReasonModalVisible(false);
    if (cancelFlowNextStep === 'retention') {
      setIsRetentionModalVisible(true);
      return;
    }
    setIsCancelConfirmVisible(true);
  };

  const applyRetention = async () => {
    if (!userID) return;
    setIsApplyingRetention(true);
    setRetentionError(null);
    try {
      await axios.post(`${API_BASE}/apply-retention-coupon`, {
        userID,
        offer: retentionChoice,
        cancelReasonCode,
        cancelReasonText: trimmedCancelReasonText,
        cancelFlowOutcome: 'retained' as CancelFlowOutcome,
      });
      message.success('Savings applied. Thanks for staying!');
      setIsRetentionModalVisible(false);
      resetCancelFeedbackState();
      await refreshSubscriptionSummary();
    } catch (error: any) {
      const msg = getErrorMessage(error);
      setRetentionError(msg);
      const detail = error?.response?.data?.detail || '';
      if (typeof detail === 'string' && detail.toLowerCase().includes('already used')) {
        setIsRetentionModalVisible(false);
        setIsCancelConfirmVisible(true);
      }
    } finally {
      setIsApplyingRetention(false);
    }
  };

  const confirmCancelSubscription = async () => {
    if (!userID) return;
    setIsCancelling(true);
    try {
      const payload: Record<string, any> = {
        userID
      };
      if (selectedSubscriptionCustomerId) {
        payload.customerID = selectedSubscriptionCustomerId;
      }
      payload.cancelReasonCode = cancelReasonCode;
      payload.cancelReasonText = trimmedCancelReasonText;
      payload.cancelFlowOutcome = 'cancelled' as CancelFlowOutcome;
      const headers = await getAuthHeaders();
      const resp = await axios.post(`${API_BASE}/cancel-subscription`, payload, { headers });
      message.success('Your subscription will cancel at the end of the current period.');
      if (resp?.data?.cancellation_date) {
        const ts = resp.data.cancellation_date;
        setSubscriptionSummary(prev =>
          prev ? ({ ...prev, cancelAt: ts } as SubscriptionSummary) : prev as any
        );
        setFallbackCancelAt(ts);
      }
      setIsCancelConfirmVisible(false);
      resetCancelFeedbackState();
      await refreshSubscriptionSummary();
    } catch (error) {
      const msg = getErrorMessage(error);
      message.error(msg);
    } finally {
      setIsCancelling(false);
    }
  };

  // Un-cancel / Reactivate subscription (turn auto-renew back on)
  const handleReactivateSubscriptionClick = async () => {
    if (!userID) return;
    setIsReactivating(true);
    try {
      const headers = await getAuthHeaders();
      const resp = await axios.post(`${API_BASE}/reactivate-subscription`, { userID }, { headers });
      if (resp?.data?.multiple_customers) {
        setReactivateAfterSelectionImmediate(true);
        setRenewalErrorMessage(null);
        openSubscriptionSelectionModal(resp.data, 'reactivate');
        return;
      }
      message.success('Auto-renew turned back on.');
      await fetchUpdatedPlanDetails();
      await refreshSubscriptionSummary();
    } catch (error: any) {
      const msg = getErrorMessage(error);
      message.error(msg);
    } finally {
      setIsReactivating(false);
    }
  };

  // Renew Now helpers

  const attemptRenew = async (customerId?: string) => {
    if (!userID) return;
    if (isRenewing) return;
    setIsRenewing(true);
    let preserveImmediateRenew = false;
    try {
      const payload: any = { userID };
      if (customerId) payload.customerID = customerId;
      const response = await axios.post(`${API_BASE}/renew-subscription`, payload);
      const data = response?.data || {};
      if (data.multiple_customers) {
        preserveImmediateRenew = true;
        setRenewAfterSelectionImmediate(true);
        setRenewalErrorMessage(null);
        message.destroy(RENEW_TOAST_KEY);
        setIsRenewFlowActive(false);
        openSubscriptionSelectionModal(data, 'renew');
        return;
      }
      const serverMessage: string | undefined = typeof data?.message === 'string' ? data.message : undefined;
      const invoiceId: string | undefined = data?.invoice_id || data?.invoiceId;
      const invoiceStatus: string | undefined = data?.invoice_status || data?.invoiceStatus;
      const hostedInvoiceUrl: string | undefined = data?.hosted_invoice_url || data?.hostedInvoiceUrl;
      const invoicePdf: string | undefined = data?.invoice_pdf || data?.invoicePdf;

      if (invoiceId && invoiceStatus !== 'paid') {
        const notice = serverMessage || 'Renewal invoice created. Please complete payment to finalize the renewal.';
        message.info({ content: notice, key: RENEW_TOAST_KEY });
        Modal.info({
          title: 'Manual Payment Required',
          content: (
            <Space direction="vertical">
              <Text>{notice}</Text>
              {hostedInvoiceUrl ? (
                <Button type="primary" href={hostedInvoiceUrl} target="_blank" rel="noopener noreferrer">
                  View Invoice
                </Button>
              ) : null}
              {!hostedInvoiceUrl && invoicePdf ? (
                <Button type="primary" href={invoicePdf} target="_blank" rel="noopener noreferrer">
                  Download Invoice PDF
                </Button>
              ) : null}
            </Space>
          ),
          okText: 'Close',
        });
      } else {
        const successMessage = serverMessage || 'Your subscription has been renewed successfully.';
        message.success({ content: successMessage, key: RENEW_TOAST_KEY });
      }
      await fetchUpdatedPlanDetails();
      await refreshSubscriptionSummary();
    } catch (error: any) {
      const multiPayload = error?.response?.data;
      if (multiPayload?.multiple_customers) {
        preserveImmediateRenew = true;
        setRenewAfterSelectionImmediate(true);
        setRenewalErrorMessage(null);
        message.destroy(RENEW_TOAST_KEY);
        setIsRenewFlowActive(false);
        openSubscriptionSelectionModal(multiPayload, 'renew');
        return;
      }
      // Handle SCA (3DS) inline if required
      const detail = error?.response?.data?.detail;
      const clientSecret: string | undefined =
        typeof detail === 'object' && detail ? detail.payment_intent_client_secret : undefined;
      if (clientSecret) {
        const stripe = await getStripePromise();
        if (!stripe) {
          message.error({ content: 'Payment service is unavailable. Please try again later.', key: RENEW_TOAST_KEY });
        } else {
          // Hide overlay during card authentication to avoid overlay conflicts
          setIsRenewFlowActive(false);
          message.loading({ content: 'Authenticating payment...', key: RENEW_TOAST_KEY, duration: 0 });
          const result = await stripe.confirmCardPayment(clientSecret);
          if (result.error) {
            message.error({ content: result.error.message || 'Payment authentication failed. Please try again.', key: RENEW_TOAST_KEY });
          } else {
            message.success({ content: 'Your subscription has been renewed successfully.', key: RENEW_TOAST_KEY });
            await fetchUpdatedPlanDetails();
            await refreshSubscriptionSummary();
          }
        }
      } else {
        const msg = getErrorMessage(error);
        message.error({ content: msg, key: RENEW_TOAST_KEY });
      }
    } finally {
      setIsRenewing(false);
      if (!preserveImmediateRenew) {
        setRenewAfterSelectionImmediate(false);
      }
      setIsRenewFlowActive(false);
    }
  };

  const startDirectRenewFlow = async () => {
    if (!userID) return;
    setRenewAfterSelectionImmediate(true);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(`${API_BASE}/get-subscription-details`, {
        params: { userID },
        headers
      });
      applyRenewalEligibility(response.data);
      if (response?.data?.multiple_customers) {
        // Show selection modal; remove processing indicators so user can choose
        message.destroy(RENEW_TOAST_KEY);
        setIsRenewFlowActive(false);
        openSubscriptionSelectionModal(response.data, 'renew');
      } else {
        // Single subscription -> renew immediately
        await attemptRenew();
      }
    } catch (error: any) {
      // Some backends might return multiple_customers via error channel; handle gracefully
      const multiple = error?.response?.data?.multiple_customers;
      if (multiple && error?.response?.data?.customers) {
        message.destroy(RENEW_TOAST_KEY);
        setIsRenewFlowActive(false);
        openSubscriptionSelectionModal(error.response.data, 'renew');
        applyRenewalEligibility(error.response.data);
      } else {
        const msg = getErrorMessage(error);
        message.error({ content: msg, key: RENEW_TOAST_KEY });
        setIsRenewFlowActive(false);
      }
    }
  };
  const fetchUpdatedPlanDetails = async () => {
    if (!userID) {
      setPlanDetailsFetched(false);
      return;
    }
    setPlanDetailsFetched(false);
    setIsLoadingPlanDetails(true);
    try {
      const creditResponse = await axios.get(
        `${API_BASE}/check_credit_balance/`,
        { params: { userID } }
      );
      const planProductID = typeof creditResponse.data.planProductID === 'string' ? creditResponse.data.planProductID : undefined;
      const planPriceID = typeof creditResponse.data.planPriceID === 'string' ? creditResponse.data.planPriceID : undefined;
      const lookupField = typeof creditResponse.data.planLookupID === 'string' ? creditResponse.data.planLookupID : undefined;
      const planIDRaw: any = creditResponse.data.planID;
      const priceIDRaw: any = creditResponse.data.priceID;
      const fallbackPriceID: string | undefined =
        typeof priceIDRaw === 'string' && priceIDRaw
          ? priceIDRaw
          : undefined;
      const fallbackPlanID: string | undefined =
        typeof planIDRaw === 'string' && planIDRaw
          ? planIDRaw
          : (typeof planIDRaw === 'number' && planIDRaw !== 0 ? String(planIDRaw) : undefined);
      const lookupId = planPriceID || fallbackPriceID || lookupField || fallbackPlanID || planProductID;
      const resolvedPriceId = planPriceID || fallbackPriceID;

      const creditData = creditResponse.data;
      let planData: any = {};
      if (lookupId) {
        try {
          const headers = await getAuthHeaders();
          const planResponse = await axios.get(`${API_BASE}/get_plan_details/${lookupId}`, {
            params: { ...(resolvedPriceId ? { priceId: resolvedPriceId } : {}), userID, surface: 'account' },
            headers,
          });
          const appliedPriceId =
            resolvedPriceId ||
            (typeof planResponse.data?.stripePriceID === 'string' ? planResponse.data.stripePriceID : undefined);
          planData = { ...(planResponse.data || {}), priceId: appliedPriceId };
        } catch (planError) {
          console.warn(`Failed to load plan details for ${lookupId}`, planError);
          planData = { priceId: resolvedPriceId ?? undefined };
        }
      }
      syncCreditState(creditData, planData);
      await loadSubscriptionCreditBatches();
    } catch (error) {
      console.error('Failed to fetch updated details:', error);
    } finally {
      setIsLoadingPlanDetails(false);
      setPlanDetailsFetched(true);
    }
  };

  const handleRenewNowClick = async () => {
    if (!userID) return;
    if (subscriptionSummary?.isOnBreak) {
      message.info('Resume a plan before renewing early.');
      return;
    }
    if (subscriptionSummary?.isPaused) {
      message.info('Resume your subscription before renewing.');
      return;
    }
    setIsLoadingRenewalDetails(true);
    setRenewalErrorMessage(null);

    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(`${API_BASE}/get-subscription-details`, {
        params: { userID },
        headers
      });
      applyRenewalEligibility(response.data);

      if (response.data.multiple_customers) {
        openSubscriptionSelectionModal(response.data, 'renew');

        if (!response.data.can_renew && response.data.renewal_wait_message) {
          setRenewalErrorMessage(response.data.renewal_wait_message);
        }
      } else {
        const { amount_due, plan_name, status, message: msg, can_renew, renewal_wait_message } = response.data;

        if (status === 'cancelled') {
          setRenewalErrorMessage(msg || 'Your subscription has been cancelled. Please reactivate it first using Manage Subscription.');
        } else if (!can_renew && renewal_wait_message) {
          setRenewalErrorMessage(renewal_wait_message);
        } else {
          setRenewalAmount(amount_due);
          setRenewalPlanName(plan_name);
        }
        setIsRenewModalVisible(true);
      }
    } catch (error: any) {
      applyRenewalEligibility(error?.response?.data);
      const errorMessage = getErrorMessage(error);
      setRenewalErrorMessage(errorMessage);
      setIsRenewModalVisible(true);
    } finally {
      setIsLoadingRenewalDetails(false);
    }
  };

  const handleSubscriptionSelection = async () => {
    const selectionMode: SubscriptionSelectionMode = subscriptionSelectionMode ?? 'renew';
    const selectionCopy = selectionCopyForMode(selectionMode);
    if (!selectedSubscriptionCustomerId) {
      message.error(selectionCopy.emptySelection);
      return;
    }
    setIsSubscriptionSelectionModalVisible(false);
    setSelectionGuidance(null);
    setSubscriptionSelectionMode(null);

    // Direct reactivate path (no confirmation modal) after selection
    if (reactivateAfterSelectionImmediate) {
      try {
        message.loading({ content: 'Reactivating...', key: 'reactivate', duration: 0 });
        const headers = await getAuthHeaders();
        await axios.post(`${API_BASE}/reactivate-subscription`, {
          userID,
          customerID: selectedSubscriptionCustomerId
        }, {
          headers,
        });
        message.success({ content: 'Auto-renew turned back on.', key: 'reactivate' });
        await fetchUpdatedPlanDetails();
        await refreshSubscriptionSummary();
      } catch (e: any) {
        message.error(getErrorMessage(e));
      } finally {
        setSelectedSubscriptionCustomerId(null);
        setReactivateAfterSelectionImmediate(false);
      }
      return;
    }

    if (cancelOnBreakAfterSelectionImmediate) {
      setCancelOnBreakAfterSelectionImmediate(false);
      const selectedCustomer = selectedSubscriptionCustomerId;
      setSelectedSubscriptionCustomerId(null);
      await cancelScheduledOnBreakChange(selectedCustomer);
      return;
    }

    if (onBreakAfterSelectionImmediate && pendingOnBreakChange) {
      const toastKey = pendingOnBreakChange.action === 'move' ? 'move-on-break' : 'resume-on-break';
      message.loading({
        content: pendingOnBreakChange.action === 'move' ? 'Moving to On Break...' : 'Resuming plan...',
        key: toastKey,
        duration: 0,
      });
      setIsChangingOnBreak(true);
      try {
        const headers = await getAuthHeaders();
        const response = await axios.post(`${API_BASE}/change-subscription-plan`, {
          userID,
          customerID: selectedSubscriptionCustomerId,
          timing: pendingOnBreakChange.timing,
          targetPriceId: pendingOnBreakChange.targetPriceId,
          targetProductId: pendingOnBreakChange.targetProductId,
          idempotencyKey: pendingOnBreakChange.idempotencyKey,
          ...(pendingOnBreakChange.cancellationFeedback || {}),
          ...(pendingOnBreakChange.survey || {}),
        }, {
          headers,
        });
        const detail = String(response?.data?.message || '');
        message.success({
          content:
            detail ||
            (pendingOnBreakChange.action === 'move'
              ? pendingOnBreakChange.timing === 'now'
                ? 'Moved to On Break.'
                : 'On Break scheduled for your next billing date.'
              : pendingOnBreakChange.timing === 'now'
              ? 'Plan resumed.'
              : 'Plan resume scheduled for your next billing date.'),
          key: toastKey,
        });
        if (pendingOnBreakChange.action === 'move' && pendingOnBreakChange.survey) {
          showOnBreakSuccess(
            response?.data || {},
            pendingOnBreakChange.survey,
            response?.data?.effective_date ?? onBreakStartTimestamp,
          );
        }
        await waitForBillingSync();
        await refreshSubscriptionSummary();
        await fetchUpdatedPlanDetails();
        if (pendingOnBreakChange.cancellationFeedback) {
          setCancelFlowNextStep(null);
          resetCancelFeedbackState();
        }
      } catch (e: any) {
        message.error({ content: getErrorMessage(e), key: toastKey });
      } finally {
        setIsChangingOnBreak(false);
        setOnBreakAfterSelectionImmediate(false);
        setPendingOnBreakChange(null);
        setSelectedSubscriptionCustomerId(null);
      }
      return;
    }

    if (pauseAfterSelectionImmediate && pendingPauseSkips) {
      const toastKey = 'pause-subscription';
      message.loading({ content: 'Pausing subscription...', key: toastKey, duration: 0 });
      setIsPausing(true);
      const skipCount = pendingPauseSkips;
      try {
        const headers = await getAuthHeaders();
        const response = await axios.post(`${API_BASE}/pause-subscription`, {
          userID,
          customerID: selectedSubscriptionCustomerId,
          skipRenewals: skipCount,
        }, {
          headers,
        });
        const pausedUntil: number | null =
          typeof response?.data?.paused_until === 'number' ? response.data.paused_until : null;
        const pauseStartedAt =
          typeof response?.data?.pause_started_at === 'number'
            ? response.data.pause_started_at
            : Math.floor(Date.now() / 1000);
        message.success({
          content: pausedUntil
            ? `Subscription paused until ${formatDate(pausedUntil)}.`
            : 'Subscription paused.',
          key: toastKey,
        });
        setSubscriptionSummary((prev) => {
          if (!prev) return prev;
          const nextRemaining =
            typeof prev.pauseSkipsRemainingInWindow === 'number'
              ? Math.max(prev.pauseSkipsRemainingInWindow - skipCount, 0)
              : prev.pauseSkipsRemainingInWindow ?? null;
          const nextUsed =
            typeof prev.pauseSkipsUsedInWindow === 'number'
              ? prev.pauseSkipsUsedInWindow + skipCount
              : prev.pauseSkipsUsedInWindow ?? null;
          return {
            ...prev,
            isPaused: true,
            pauseResumesAt: pausedUntil ?? prev.pauseResumesAt ?? null,
            pauseStartedAt: pauseStartedAt ?? prev.pauseStartedAt ?? null,
            pauseRequestedSkipCount: skipCount,
            pauseSkipsRemainingInWindow: nextRemaining ?? prev.pauseSkipsRemainingInWindow ?? null,
            pauseSkipsUsedInWindow: nextUsed ?? prev.pauseSkipsUsedInWindow ?? null,
          };
        });
        setPauseAfterSelectionImmediate(false);
        setPendingPauseSkips(null);
        try {
          await waitForBillingSync();
          await refreshSubscriptionSummary();
          await fetchUpdatedPlanDetails();
        } catch (refreshError) {
          console.error('Failed to refresh subscription details after pause selection:', refreshError);
          message.warning('Subscription paused, but we could not refresh the latest details. Please refresh the page to confirm.');
        }
      } catch (e: any) {
        message.error({ content: getErrorMessage(e), key: toastKey });
        setPauseAfterSelectionImmediate(false);
        setPendingPauseSkips(null);
      } finally {
        setIsPausing(false);
        setSelectedSubscriptionCustomerId(null);
      }
      return;
    }

    if (extendAfterSelectionImmediate && pendingExtendSkips) {
      const toastKey = 'extend-pause';
      message.loading({ content: 'Extending pause...', key: toastKey, duration: 0 });
      setIsExtendingPause(true);
      const skipCount = pendingExtendSkips;
      try {
        const headers = await getAuthHeaders();
        const response = await axios.post(`${API_BASE}/extend-pause`, {
          userID,
          customerID: selectedSubscriptionCustomerId,
          skipRenewals: skipCount,
        }, {
          headers,
        });
        const pausedUntil: number | null =
          typeof response?.data?.paused_until === 'number' ? response.data.paused_until : null;
        const totalRequestedSkips: number | null =
          typeof response?.data?.total_requested_skip_count === 'number'
            ? response.data.total_requested_skip_count
            : null;
        message.success({
          content: pausedUntil
            ? `Pause extended until ${formatDate(pausedUntil)}.`
            : 'Pause extended.',
          key: toastKey,
        });
        setSubscriptionSummary((prev) => {
          if (!prev) return prev;
          const nextRemaining =
            typeof prev.pauseSkipsRemainingInWindow === 'number'
              ? Math.max(prev.pauseSkipsRemainingInWindow - skipCount, 0)
              : prev.pauseSkipsRemainingInWindow ?? null;
          const nextUsed =
            typeof prev.pauseSkipsUsedInWindow === 'number'
              ? prev.pauseSkipsUsedInWindow + skipCount
              : prev.pauseSkipsUsedInWindow ?? null;
          const nextRequested =
            typeof totalRequestedSkips === 'number'
              ? totalRequestedSkips
              : typeof prev.pauseRequestedSkipCount === 'number'
              ? prev.pauseRequestedSkipCount + skipCount
              : skipCount;
          return {
            ...prev,
            isPaused: true,
            pauseResumesAt: pausedUntil ?? prev.pauseResumesAt ?? null,
            pauseRequestedSkipCount: nextRequested,
            pauseSkipsRemainingInWindow: nextRemaining ?? prev.pauseSkipsRemainingInWindow ?? null,
            pauseSkipsUsedInWindow: nextUsed ?? prev.pauseSkipsUsedInWindow ?? null,
          };
        });
        setExtendAfterSelectionImmediate(false);
        setPendingExtendSkips(null);
        try {
          await waitForBillingSync();
          await refreshSubscriptionSummary();
          await fetchUpdatedPlanDetails();
        } catch (refreshError) {
          console.error('Failed to refresh subscription details after extend selection:', refreshError);
          message.warning('Pause extended, but we could not refresh the latest details. Please refresh the page to confirm.');
        }
      } catch (e: any) {
        message.error({ content: getErrorMessage(e), key: toastKey });
        setExtendAfterSelectionImmediate(false);
        setPendingExtendSkips(null);
      } finally {
        setIsExtendingPause(false);
        setSelectedSubscriptionCustomerId(null);
      }
      return;
    }

    if (resumeAfterSelectionImmediate) {
      const toastKey = 'resume-subscription';
      message.loading({ content: 'Resuming subscription...', key: toastKey, duration: 0 });
      setIsResuming(true);
      try {
        const headers = await getAuthHeaders();
        await axios.post(`${API_BASE}/resume-subscription`, {
          userID,
          customerID: selectedSubscriptionCustomerId,
        }, {
          headers,
        });
        message.success({ content: 'Subscription resumed.', key: toastKey });
        setResumeAfterSelectionImmediate(false);
        try {
          await waitForBillingSync();
          await refreshSubscriptionSummary();
          await fetchUpdatedPlanDetails();
        } catch (refreshError) {
          console.error('Failed to refresh subscription details after resume selection:', refreshError);
          message.warning('Subscription resumed, but we could not refresh the latest details. Please refresh the page to confirm.');
        }
      } catch (e: any) {
        message.error({ content: getErrorMessage(e), key: toastKey });
        setResumeAfterSelectionImmediate(false);
      } finally {
        setIsResuming(false);
        setSelectedSubscriptionCustomerId(null);
      }
      return;
    }

    // Direct renew path (no confirmation modal) after selection
    if (renewAfterSelectionImmediate) {
      message.loading({ content: 'Processing renewal...', key: RENEW_TOAST_KEY, duration: 0 });
      setIsRenewFlowActive(true);
      await attemptRenew(selectedSubscriptionCustomerId);
      setSelectedSubscriptionCustomerId(null);
      return;
    }

    // Legacy path: show confirmation modal (kept for other entry points)
    setIsLoadingRenewalDetails(true);
    setRenewalErrorMessage(null);

    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(`${API_BASE}/get-subscription-details`, {
        params: { userID, customerID: selectedSubscriptionCustomerId },
        headers
      });

      const { amount_due, plan_name, status, message: msg, can_renew, renewal_wait_message } = response.data;

      if (status === 'cancelled') {
        setRenewalErrorMessage(msg || 'This subscription has been cancelled. Please reactivate it first using Manage Subscription.');
      } else if (!can_renew && renewal_wait_message) {
        setRenewalErrorMessage(renewal_wait_message);
      } else {
        setRenewalAmount(amount_due);
        setRenewalPlanName(plan_name);
      }
      setIsRenewModalVisible(true);
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setRenewalErrorMessage(errorMessage);
      setIsRenewModalVisible(true);
    } finally {
      setIsLoadingRenewalDetails(false);
    }
  };

  const handleRenewNowConfirm = async () => {
    if (isRenewing || !userID) return;

    setIsRenewing(true);
    // A + B visual feedback
    message.loading({ content: 'Processing renewal...', key: RENEW_TOAST_KEY, duration: 0 });
    setIsRenewFlowActive(true);
    try {
      const payload: any = { userID };
      if (selectedSubscriptionCustomerId) {
        payload.customerID = selectedSubscriptionCustomerId;
      }
      await axios.post(`${API_BASE}/renew-subscription`, payload);
      message.success({ content: 'Your subscription has been renewed successfully.', key: RENEW_TOAST_KEY });
      await fetchUpdatedPlanDetails();
      await refreshSubscriptionSummary();
      setIsRenewModalVisible(false);
      setSelectedSubscriptionCustomerId(null);
      setRenewalErrorMessage(null);
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setRenewalErrorMessage(errorMessage);
      message.error({ content: errorMessage, key: RENEW_TOAST_KEY });
    } finally {
      setIsRenewing(false);
      setIsRenewFlowActive(false);
    }
  };

  const handleRenewNowCancel = () => {
    setIsRenewModalVisible(false);
    setRenewalErrorMessage(null);
  };

  // Invoices
  const fetchInvoices = async () => {
    if (!userID) return;
    setIsLoadingInvoices(true);
    setInvoicesError(null);
    try {
      const response = await axios.get(`${API_BASE}/get-invoices`, {
        params: { userID }
      });
      setInvoices((response.data.invoices || []) as InvoiceRow[]);
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setInvoicesError(errorMessage);
    } finally {
      setIsLoadingInvoices(false);
    }
  };

  const handleViewInvoices = () => {
    setIsInvoicesModalVisible(true);
    fetchInvoices();
  };

  const filteredInvoices = useMemo<InvoiceRow[]>(() => {
    if (!invoices) return [];
    return invoices.filter((invoice: InvoiceRow) => {
      const amount =
        invoice.display_amount ||
        invoice.amount_paid ||
        invoice.total ||
        invoice.amount_due ||
        0;

      if (amount === 0) return false;

      const s = (searchText || '').toLowerCase();
      const matchesSearch =
        !s ||
        (invoice.number || '').toLowerCase().includes(s) ||
        `${(amount / 100).toFixed(2)}`.includes(searchText) ||
        (invoice.status || '').toLowerCase().includes(s);

      const matchesStatus = !statusFilter || invoice.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [invoices, searchText, statusFilter]);

  const renderOnBreakQuestions = (disabled: boolean = false) => (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <section className="account-billing-modal-block">
        <div className="account-billing-modal-block-header">
          <h3>What best describes where you are right now?</h3>
        </div>
        <Radio.Group
          aria-label="What best describes where you are right now?"
          value={selectedOnBreakStage}
          onChange={(event) => setSelectedOnBreakStage(event.target.value)}
          disabled={disabled}
          className="account-billing-radio-list"
        >
          <Space direction="vertical">
            {ON_BREAK_STAGE_OPTIONS.map((option) => (
              <Radio key={option.value} value={option.value}>{option.label}</Radio>
            ))}
          </Space>
        </Radio.Group>
      </section>
      <section className="account-billing-modal-block">
        <div className="account-billing-modal-block-header">
          <h3>When do you think you may translate again?</h3>
        </div>
        <Radio.Group
          aria-label="When do you think you may translate again?"
          value={selectedOnBreakExpectedReturn}
          onChange={(event) => setSelectedOnBreakExpectedReturn(event.target.value)}
          disabled={disabled}
          className="account-billing-radio-list"
        >
          <Space direction="vertical">
            {ON_BREAK_RETURN_OPTIONS.map((option) => (
              <Radio key={option.value} value={option.value}>{option.label}</Radio>
            ))}
          </Space>
        </Radio.Group>
      </section>
    </Space>
  );

  // UI bits
  const hasCurrentPlan = Boolean(planDetails.stripePriceID || planDetails.priceId);
  const planBillingInterval = (planDetails.billingInterval || 'monthly').toLowerCase();
  const planIntervalMonths = planDetails.intervalMonths && planDetails.intervalMonths > 0
    ? planDetails.intervalMonths
    : planBillingInterval === 'yearly'
    ? 12
    : 1;
  const planMonthlyAllocation = planDetails.monthlyCreditsAllocated ?? planDetails.monthlyCredits ?? 0;
  const planUpfrontCredits = planBillingInterval === 'yearly'
    ? planDetails.creditsPerInterval ?? planMonthlyAllocation * planIntervalMonths
    : undefined;
  const subscriptionCreditsLabel = 'Subscription Credits';
  const nextBatchExpiresLabel = useMemo(() => {
    if (!subscriptionBatches.length) {
      return 'No scheduled expirations';
    }
    const nextBatch = subscriptionBatches[0];
    const expiresAtSeconds =
      typeof nextBatch.expiresAt === 'number'
        ? nextBatch.expiresAt
        : nextBatch.expiresAtISO
        ? Math.floor(Date.parse(nextBatch.expiresAtISO) / 1000)
        : null;
    const remaining = Number(nextBatch.remainingCredits ?? 0);
    if (!expiresAtSeconds || !Number.isFinite(expiresAtSeconds)) {
      return 'Never expire while subscribed';
    }
    const dateLabel = formatDateTimeLocal(expiresAtSeconds, userTimeZone);
    const remainingDisplay = Math.max(remaining, 0).toLocaleString();
    return `Next expiration: ${dateLabel} · ${remainingDisplay} credits`;
  }, [subscriptionBatches, userTimeZone]);
  const subscriptionActiveDisplay = Number.isFinite(subscriptionBatchSummary.totalActive)
    ? subscriptionBatchSummary.totalActive
    : 0;
  const nextExpiringCreditsDisplay = nextExpiringBatch
    ? toNumber(nextExpiringBatch.remainingCredits, 0).toLocaleString()
    : '0';
  const nextExpiringLabel = useMemo(() => {
    if (!nextExpiringBatch) {
      return null;
    }
    const epochSeconds =
      typeof nextExpiringBatch.expiresAt === 'number' && nextExpiringBatch.expiresAt > 0
        ? nextExpiringBatch.expiresAt
        : nextExpiringBatch.expiresAtISO
        ? Math.floor(new Date(nextExpiringBatch.expiresAtISO).getTime() / 1000)
        : null;
    if (!epochSeconds || epochSeconds <= 0) {
      return null;
    }
    return formatDateTimeLocal(epochSeconds, userTimeZone);
  }, [nextExpiringBatch, userTimeZone]);
  const renewNowEmphasized = Boolean(
    subscriptionSummary?.renewalWarning || subscriptionSummary?.scheduledTargetProductId
  );
  const planDetailCreditsLabel =
    planBillingInterval === 'yearly' && typeof planUpfrontCredits === 'number'
      ? `${planUpfrontCredits.toLocaleString()} upfront credits`
      : `${Number(planMonthlyAllocation || 0).toLocaleString()} monthly credits`;
  const planDetailIntervalLabel =
    planBillingInterval === 'yearly'
      ? 'Annual billing'
      : planBillingInterval === 'monthly'
      ? 'Monthly billing'
      : `${planBillingInterval || 'Monthly'} billing`;

  // Auth gating UI: do not show any billing content or buttons until auth is known.
  if (!authKnown) {
    return (
      <Card bordered className="account-billing-auth-card">
        <div className="loading-card-wrapper" style={{ padding: '32px 0' }}>
          <LoadingCard label="Checking your account..." size="default" />
        </div>
      </Card>
    );
  }

  // If auth is known and the user is not signed in, show a minimal notice with no billing actions.
  if (authKnown && !isAuthenticated) {
    return (
      <Card bordered className="account-billing-auth-card">
        <Alert
          type="info"
          showIcon
          message="Sign in required"
          description="Please sign in to view and manage your billing information."
        />
      </Card>
    );
  }

  // Auth is known and user is authenticated. Render full billing UI.
  return (
    <>
      <div className="account-billing-shell">
        <Row gutter={[16, 16]} className="account-billing-grid">
        {/* Combined: Plan + Credits */}
        <Col xs={24} lg={10}>
          <Card
            data-tour-id="billing-plan-overview"
            className="account-billing-card"
            title={
              <div className="account-billing-card-title">
                <div className="account-billing-title-copy">
                  <span className="account-billing-title-kicker">Plan and credits</span>
                  <Title level={4}>{planDetails.planName || 'Free Plan'}</Title>
                </div>
                {(planDetails.isLegacy || planDetails.isActive === false) && (
                  <span className="account-billing-title-tags">
                    {planDetails.isLegacy && (
                      <Tooltip title="Legacy plans are no longer sold. You can keep yours as long as you stay subscribed.">
                        <Tag color='orange'>Legacy Plan</Tag>
                      </Tooltip>
                    )}
                    {planDetails.isActive === false && <Tag color='red'>Inactive</Tag>}
                  </span>
                )}
              </div>
            }
            bordered
          >
            {isLoadingPlanDetails ? (
              <div className="loading-card-wrapper" style={{ padding: '32px 0' }}>
                <LoadingCard label="Loading plan details..." />
              </div>
            ) : (
              <div className="account-billing-stack">
                <div className="account-billing-primary-surface account-billing-credit-hero">
                  <span className="account-billing-credit-hero-label">Total Available Credits</span>
                  <span className="account-billing-credit-hero-value">{availableCredits.toLocaleString()}</span>
                  <span className="account-billing-credit-hero-copy">
                    {pendingCredits > 0
                      ? `${pendingCredits.toLocaleString()} credits on hold (${totalCredits.toLocaleString()} total)`
                      : `${totalCredits.toLocaleString()} total balance`}
                  </span>
                </div>

                <div className="account-billing-plan-details">
                  <div>
                    <span className="account-billing-kicker">Plan credits</span>
                    <strong>{planDetailCreditsLabel}</strong>
                  </div>
                  <div>
                    <span className="account-billing-kicker">Billing cadence</span>
                    <strong>{planDetailIntervalLabel}</strong>
                  </div>
                  {foreverDiscountPercent > 0 && (
                    <div>
                      <span className="account-billing-kicker">Forever discount</span>
                      <strong>{foreverDiscountPercent}% off</strong>
                    </div>
                  )}
                </div>

                <div className="account-billing-metric-list">
                  <button
                    type="button"
                    className="account-billing-metric-row account-billing-metric-row--button"
                    onClick={() => {
                      if (billingActionsDisabled) return;
                      handleOpenBatchesModal();
                    }}
                    disabled={billingActionsDisabled}
                  >
                    <span className="account-billing-metric-main">
                      <span className="account-billing-metric-icon"><CalendarOutlined /></span>
                      <span className="account-billing-metric-text">
                        <span className="account-billing-metric-label">{subscriptionCreditsLabel}</span>
                        <span className="account-billing-metric-helper">{nextBatchExpiresLabel}</span>
                      </span>
                    </span>
                    <span className="account-billing-metric-value">
                      {subscriptionActiveDisplay.toLocaleString()}
                      <ArrowRightOutlined />
                    </span>
                  </button>

                  {nextExpiringBatch && toNumber(nextExpiringBatch.remainingCredits, 0) > 0 && nextExpiringLabel && (
                    <div className="account-billing-metric-row account-billing-metric-row--warning">
                      <span className="account-billing-metric-main">
                        <span className="account-billing-metric-icon"><CalendarOutlined /></span>
                        <span className="account-billing-metric-text">
                          <span className="account-billing-metric-label">
                            Next Credits Expiring
                            <Tooltip title="Oldest active subscription credits. They do not expire while your subscription remains active.">
                              <QuestionCircleOutlined />
                            </Tooltip>
                          </span>
                          <span className="account-billing-metric-helper">
                            {nextExpiringCreditsDisplay} from your oldest active subscription batch. Expires {nextExpiringLabel}.
                          </span>
                        </span>
                      </span>
                    </div>
                  )}
                  {pendingCredits > 0 && (
                    <div className="account-billing-metric-row account-billing-metric-row--danger">
                      <span className="account-billing-metric-main">
                        <span className="account-billing-metric-icon"><ClockCircleOutlined /></span>
                        <span className="account-billing-metric-text">
                          <span className="account-billing-metric-label">
                            Credits On Hold
                          <Tooltip title="Credits reserved for in-progress work. They become available once the job completes or the hold is released.">
                            <QuestionCircleOutlined />
                          </Tooltip>
                          </span>
                          <span className="account-billing-metric-helper">
                            Reserved for running tasks; not currently spendable.
                          </span>
                        </span>
                      </span>
                      <span className="account-billing-metric-value">{pendingCredits.toLocaleString()}</span>
                    </div>
                  )}

                  {foreverCredits > 0 && (
                    <div id="forever-credits-card" className="account-billing-metric-row account-billing-metric-row--success">
                      <span className="account-billing-metric-main">
                        <span className="account-billing-metric-icon"><CrownOutlined /></span>
                        <span className="account-billing-metric-text">
                          <span className="account-billing-metric-label">
                            Forever Credits
                          <Tooltip title="These credits never expire">
                            <QuestionCircleOutlined />
                          </Tooltip>
                          </span>
                          <span className="account-billing-metric-helper">Never expire</span>
                        </span>
                      </span>
                      <span className="account-billing-metric-value">{foreverCredits.toLocaleString()}</span>
                    </div>
                  )}
                </div>

                <div>
                  <Button
                    data-tour-id="billing-credit-top-up"
                    className="account-billing-primary-button"
                    type="primary"
                    onClick={handleCreditTopUpClick}
                    size="large"
                    block
                    disabled={billingActionsDisabled}
                    aria-label="Credit Top Up"
                  >
                    <span className="account-billing-top-up-content">
                      <RocketOutlined aria-hidden="true" />
                      <span>Credit Top Up</span>
                      {foreverDiscountPercent > 0 && <Tag color="gold">{foreverDiscountPercent}% off</Tag>}
                    </span>
                  </Button>
                  {foreverDiscountPercent > 0 && (
                    <div className="account-billing-discount-note">
                      You save {foreverDiscountPercent}% on Forever Credits with your plan
                    </div>
                  )}
                </div>
              </div>
            )}
          </Card>
        </Col>

        {/* Subscription Summary */}
        <Col xs={24} lg={14}>
          <Card
            data-tour-id="billing-subscription-summary"
            className="account-billing-card"
            title={
              <div className="account-billing-card-title">
                <div className="account-billing-title-copy">
                  <span className="account-billing-title-kicker">Billing status</span>
                  <Title level={4}>Subscription Summary</Title>
                </div>
              </div>
            }
            bordered
          >
            {isLoadingSubscriptionSummary ? (
              <div className="account-billing-loading">
                <Spin />
                <Text>Loading billing details...</Text>
              </div>
            ) : subscriptionSummary ? (
              <div className="account-billing-secondary-surface account-billing-summary-list">
                <div className="account-billing-summary-row">
                  <span className="account-billing-summary-label">
                    <CalendarOutlined />
                    {nextRenewalLabel}
                  </span>
                  <span className="account-billing-summary-value">
                    {nextRenewalTimestamp
                      ? formatDateTimeLocal(nextRenewalTimestamp, userTimeZone)
                      : '—'}
                  </span>
                </div>

                {typeof subscriptionSummary.amountDue === 'number' &&
                  !subscriptionSummary.trialEnd &&
                  !subscriptionSummary.isPaused && (
                  <div className="account-billing-summary-row">
                    <span className="account-billing-summary-label">Upcoming charge</span>
                    <span className="account-billing-summary-value">
                      <Text strong>${(subscriptionSummary.amountDue / 100).toFixed(2)}</Text>
                    </span>
                  </div>
                  )}

                {!subscriptionSummary.trialEnd &&
                  (subscriptionSummary.couponPercentOff != null ||
                    subscriptionSummary.couponAmountOff != null ||
                    subscriptionSummary.couponTotalAmount != null) && (
                    <div className="account-billing-summary-row">
                      <span className="account-billing-summary-label">Coupon applied</span>
                      <span className="account-billing-summary-value">
                        {(() => {
                          const formatAmount = (cents?: number | null, currency?: string | null) => {
                            if (typeof cents !== 'number') return null;
                            const amount = (cents / 100).toFixed(2);
                            return currency && currency.toUpperCase() !== 'USD'
                              ? `${currency.toUpperCase()} ${amount}`
                              : `$${amount}`;
                          };
                          const name = subscriptionSummary.couponName;
                          if (subscriptionSummary.couponPercentOff != null) {
                            const desc = `${subscriptionSummary.couponPercentOff}% off`;
                            return name ? `${name} (${desc})` : desc;
                          }
                          if (subscriptionSummary.couponAmountOff != null) {
                            const amt = formatAmount(
                              subscriptionSummary.couponAmountOff,
                              subscriptionSummary.couponCurrency
                            );
                            const desc = `${amt} off`;
                            return name ? `${name} (${desc})` : desc;
                          }
                          if (subscriptionSummary.couponTotalAmount != null) {
                            const amt = formatAmount(
                              subscriptionSummary.couponTotalAmount,
                              subscriptionSummary.couponCurrency
                            );
                            const desc = `${amt} discount`;
                            return name ? `${name} (${desc})` : desc;
                          }
                          return null;
                        })()}
                      </span>
                    </div>
                    )}

                <div className="account-billing-summary-row">
                  <span className="account-billing-summary-label">Auto-renew</span>
                  <span className="account-billing-summary-value">
                    <Tag color={subscriptionSummary.cancelAt ? 'red' : 'green'}>
                      {subscriptionSummary.cancelAt ? 'Off' : 'On'}
                    </Tag>
                  </span>
                </div>

                {subscriptionSummary.isPaused && (
                  <Alert
                    type="warning"
                    showIcon
                    message="Subscription paused"
                    description={
                      <Space direction="vertical" size={12} style={{ width: '100%' }}>
                        <span>
                          {`Billing will resume `}
                          <strong>
                            {formatDate(
                              subscriptionSummary.pauseResumesAt ??
                                subscriptionSummary.currentPeriodEnd
                            )}
                          </strong>
                          .
                        </span>
                        <Button
                          type="default"
                          icon={<PlayCircleOutlined />}
                          onClick={handleResumeClick}
                          size="small"
                          loading={isResuming}
                          disabled={resumePausedSubscriptionDisabled}
                          style={{ width: 'fit-content' }}
                        >
                          Resume Subscription
                        </Button>
                      </Space>
                    }
                    style={{ borderRadius: 8 }}
                  />
                )}

                {subscriptionSummary.paymentMethod && (
                  <div className="account-billing-summary-row">
                    <span className="account-billing-summary-label">Payment method</span>
                    <span className="account-billing-summary-value">
                      {subscriptionSummary.paymentMethod.brand.toUpperCase()} •••• {subscriptionSummary.paymentMethod.last4}{' '}
                      (exp {String(subscriptionSummary.paymentMethod.exp_month).padStart(2, '0')}/
                      {String(subscriptionSummary.paymentMethod.exp_year).slice(-2)})
                    </span>
                  </div>
                )}

                {subscriptionSummary.renewalWarning && (
                  <Alert
                    type="warning"
                    showIcon
                    message="Heads up"
                    description={subscriptionSummary.renewalWarning}
                    style={{ borderRadius: 8 }}
                  />
                )}

                {isOnBreakScheduled && (
                  <Alert
                    type="info"
                    showIcon
                    message="On Break scheduled"
                    description={
                      subscriptionSummary.scheduledEffectiveDate
                        ? `Your subscription will move to On Break on ${formatDate(subscriptionSummary.scheduledEffectiveDate)}.`
                        : 'Your subscription will move to On Break on your next billing date.'
                    }
                    style={{ borderRadius: 8 }}
                  />
                )}
              </div>
            ) : billingNotice ? (
              <Alert
                data-tour-id="billing-subscription-notice"
                type={
                  billingNotice.type === 'error'
                    ? 'error'
                    : billingNotice.type === 'cancelled'
                    ? 'warning'
                    : 'info'
                }
                message={billingNotice.title}
                description={
                  <div>
                    <div style={{ marginBottom: 8 }}>{billingNotice.message}</div>
                    {billingNotice.type === 'none' && (
                    <Button
                      type="primary"
                      icon={<CreditCardOutlined />}
                      onClick={goToPricing}
                      size="middle"
                      disabled={billingActionsDisabled}
                    >
                      Choose a Plan
                    </Button>
                  )}
                  {billingNotice.type === 'cancelled' && (
                      <Button
                        type="primary"
                        icon={<CheckCircleOutlined />}
                      onClick={handleReactivateSubscriptionClick}
                      loading={isReactivating}
                      size="middle"
                      disabled={billingActionsDisabled}
                    >
                      Keep Subscription (Un-cancel)
                    </Button>
                  )}
                </div>
                }
                showIcon
                style={{ borderRadius: 8 }}
              />
            ) : (
              <Alert
                data-tour-id="billing-subscription-notice"
                type="info"
                message="No active subscription"
                description={
                  <div>
                    There is no active subscription on your account. Go to Pricing to get started.
                    <div style={{ marginTop: 8 }}>
                      <Button
                        type="primary"
                        icon={<CreditCardOutlined />}
                        onClick={goToPricing}
                        size="middle"
                        disabled={billingActionsDisabled}
                      >
                        Choose a Plan
                      </Button>
                    </div>
                  </div>
                }
                showIcon
              />
            )}
            <div className="account-billing-action-section">
              <div className="account-billing-action-grid account-billing-action-grid--primary">
                  <Button
                    data-tour-id="billing-action-manage-subscription"
                    type="primary"
                    icon={<CrownOutlined />}
                    onClick={() => navigate('/account/subscription')}
                    size="large"
                    block
                    disabled={billingActionsDisabled}
                  >
                    Manage Subscription
                  </Button>
                  {/* Renew Now button */}
                  <Tooltip title={renewTooltipMessage}>
                    <span style={{ display: 'block' }}>
                      <Button
                        data-tour-id="billing-action-renew-now"
                        type={renewNowEmphasized ? 'primary' : 'default'}
                        className={renewNowEmphasized ? undefined : 'account-billing-muted-action'}
                        icon={<RocketOutlined />}
                        onClick={handleRenewNowClick}
                        size="large"
                        block
                        loading={isLoadingRenewalDetails}
                        disabled={
                          billingActionsDisabled ||
                          !subscriptionSummary ||
                          subscriptionSummary.isPaused ||
                          isOnBreakSubscription ||
                          !!subscriptionSummary.cancelAt ||
                          isLoadingSubscriptionSummary ||
                          isLoadingRenewalDetails ||
                          isRenewing ||
                          canRenewEarly === false
                        }
                      >
                        Renew Now
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={
                      isOnBreakScheduled || isOnBreakSubscription || isStripePausedRecovery
                        ? undefined
                        : onBreakTooltipMessage
                    }
                  >
                    <span style={{ display: 'block' }}>
                      {isOnBreakScheduled ? (
                        <Button
                          data-tour-id="billing-action-pause-resume"
                          type="default"
                          danger
                          icon={<CloseCircleOutlined />}
                          onClick={() => cancelScheduledOnBreakChange()}
                          size="large"
                          block
                          loading={isCancellingScheduledOnBreak}
                          disabled={cancelScheduledOnBreakDisabled}
                          style={{ borderRadius: 8, height: 44 }}
                        >
                          Cancel On Break Change
                        </Button>
                      ) : isOnBreakSubscription ? (
                        <Button
                          data-tour-id="billing-action-pause-resume"
                          type="default"
                          icon={<PlayCircleOutlined />}
                          onClick={openResumeFromOnBreakModal}
                          size="large"
                          block
                          loading={isChangingOnBreak}
                          disabled={resumeOnBreakDisabled}
                          style={{ borderRadius: 8, height: 44 }}
                        >
                          {subscriptionSummary?.legacyReturnEntitlement ? 'Return to Legacy Plan' : 'Resume Plan'}
                        </Button>
                      ) : (
                        <Button
                          data-tour-id="billing-action-pause-resume"
                          type="default"
                          icon={<PauseCircleOutlined />}
                          onClick={openMoveToOnBreakModal}
                          size="large"
                          block
                          loading={isChangingOnBreak}
                          disabled={moveToOnBreakDisabled}
                          style={{ borderRadius: 8, height: 44 }}
                        >
                          Move to On Break
                        </Button>
                      )}
                    </span>
                  </Tooltip>
              </div>

              <div className="account-billing-section-heading">Billing Tools</div>
              <div className="account-billing-action-grid">
                      <Button
                        data-tour-id="billing-action-manage-payment"
                        type="default"
                        icon={<CreditCardOutlined />}
                        onClick={() => handleBilling()}
                        size="large"
                        block
                        loading={isLoadingCustomerPortal}
                        disabled={billingActionsDisabled}
                      >
                        Manage Payment Method
                      </Button>
                      <Button
                        data-tour-id="billing-action-invoices"
                        type="default"
                        icon={<FileTextOutlined />}
                        onClick={handleViewInvoices}
                        size="large"
                        block
                        disabled={billingActionsDisabled}
                      >
                        Invoices
                      </Button>

                      <Button
                        data-tour-id="billing-action-subscription-credits"
                        type="default"
                        icon={<ClockCircleOutlined />}
                        onClick={handleOpenBatchesModal}
                        size="large"
                        block
                        disabled={billingActionsDisabled}
                      >
                        Subscription Credits
                      </Button>

                      <Button
                        data-tour-id="billing-action-credit-history"
                        type="default"
                        icon={<BookOutlined />}
                        onClick={() => navigate('/credit-history')}
                        size="large"
                        block
                        disabled={billingActionsDisabled}
                      >
                        Credit History
                      </Button>
              </div>
              <div className="account-billing-danger-heading">
                <span className="account-billing-section-heading">Plan Cancellation</span>
                <span>Canceling keeps access through the current billing period.</span>
              </div>
              <div className="account-billing-danger-zone">
                <Button
                  data-tour-id="billing-action-cancel-subscription"
                  type="default"
                  danger
                  icon={<CloseCircleOutlined />}
                  onClick={handleCancelSubscriptionClick}
                  size="large"
                  block
                  disabled={billingActionsDisabled}
                >
                  Cancel Subscription
                </Button>
              </div>
            </div>
          </Card>
        </Col>
      </Row>
      </div>

      <AppTour
        open={billingTourOpen}
        steps={billingTourSteps}
        current={billingTourStep}
        onChange={handleBillingTourChange}
        onClose={handleBillingTourClose}
        onFinish={handleBillingTourClose}
        zIndex={2000}
      />

      <Modal
        title={onBreakAction === 'move' ? 'Move to On Break' : (subscriptionSummary?.legacyReturnEntitlement ? 'Return to Legacy Plan' : 'Resume Plan')}
        className="account-billing-modal account-billing-on-break-modal"
        open={isOnBreakModalVisible}
        onCancel={() => {
          if (!isChangingOnBreak) {
            setIsOnBreakModalVisible(false);
          }
        }}
        okText={
          onBreakAction === 'move'
            ? 'Schedule On Break'
            : selectedOnBreakTiming === 'now'
            ? 'Resume now'
            : 'Resume next billing date'
        }
        okButtonProps={{
          loading: isChangingOnBreak,
          disabled:
            onBreakAction === 'move' &&
            onBreakEngagementEnabled &&
            (!selectedOnBreakStage || !selectedOnBreakExpectedReturn),
        }}
        cancelButtonProps={{ disabled: isChangingOnBreak }}
        onOk={confirmOnBreakPlanChange}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {onBreakAction === 'move' ? (
            <>
              <Text>
                On Break keeps your subscription active at a lower monthly price when you do not need new monthly credits.
              </Text>
              {onBreakEngagementEnabled && renderOnBreakQuestions(isChangingOnBreak)}
              <div
                style={{
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 8,
                  padding: 12,
                  background: 'var(--surface-base)',
                }}
              >
                <Space direction="vertical" size={6} style={{ width: '100%' }}>
                  <Text strong>On Break details</Text>
                  {[
                    ['On Break price', onBreakPlanPrice ? `${onBreakPlanPrice} / month` : 'Shown before you confirm'],
                    ['Starts', onBreakStartLabel],
                    ['Monthly credits added', '0'],
                    ['Existing credits', "Won't expire while subscribed"],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 16,
                        alignItems: 'baseline',
                      }}
                    >
                      <Text type="secondary">{label}</Text>
                      <Text style={{ textAlign: 'right' }}>{value}</Text>
                    </div>
                  ))}
                </Space>
              </div>
              <Text>
                {subscriptionSummary?.isPaused && onBreakStartTimestamp
                  ? `Your current pause stays active until ${onBreakStartLabel}. On Break starts automatically after that.`
                  : onBreakStartTimestamp
                  ? `Your current plan stays active until ${onBreakStartLabel}. On Break starts automatically after that.`
                  : 'Your current plan stays active until the end of this billing period. On Break starts automatically after that.'}
              </Text>
            </>
          ) : (
            <>
              <Text>
                {subscriptionSummary?.legacyReturnEntitlement
                  ? `Return to ${subscriptionSummary.legacyReturnEntitlement.planName || 'your legacy plan'}. Choosing a different current plan instead will forfeit this legacy return option.`
                  : `Resume ${onBreakReturnTarget?.planName || 'your previous plan'}.`}
              </Text>
              <div
                style={{
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 8,
                  padding: 12,
                  background: 'var(--surface-base)',
                }}
              >
                {isLoadingOnBreakReturnPlanDetails ? (
                  <Space>
                    <Spin size="small" />
                    <Text type="secondary">Loading previous plan details...</Text>
                  </Space>
                ) : (
                  <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    <Space size={8} wrap>
                      <Text strong>
                        {onBreakReturnPlanDetails?.planName || onBreakReturnTarget?.planName || 'Previous plan'}
                      </Text>
                      {(onBreakReturnPlanDetails?.isLegacy || subscriptionSummary?.legacyReturnEntitlement) && (
                        <Tag color="purple">Legacy</Tag>
                      )}
                    </Space>
                    <Text type="secondary">
                      {[onBreakReturnPlanPrice, onBreakReturnPlanInterval].filter(Boolean).join(' / ') ||
                        'Plan details will be confirmed before checkout.'}
                    </Text>
                    {onBreakReturnPlanCredits && (
                      <Text type="secondary">{onBreakReturnPlanCredits}</Text>
                    )}
                  </Space>
                )}
              </div>
              <Radio.Group
                value={selectedOnBreakTiming}
                onChange={(event) => setSelectedOnBreakTiming(event.target.value)}
                disabled={isChangingOnBreak}
              >
                <Space direction="vertical">
                  <Radio value="now">Resume now, charge today, and grant monthly credits after payment</Radio>
                  <Radio value="next_cycle">Resume next billing date</Radio>
                </Space>
              </Radio.Group>
              <Button type="link" onClick={() => navigate('/account/subscription')} style={{ paddingLeft: 0 }}>
                Choose a different plan
              </Button>
            </>
          )}
        </Space>
      </Modal>

      <Modal
        title="You're all set for On Break"
        className="account-billing-modal account-billing-on-break-modal"
        open={isOnBreakConfirmationVisible}
        onCancel={() => setIsOnBreakConfirmationVisible(false)}
        footer={
          <Button type="primary" onClick={() => setIsOnBreakConfirmationVisible(false)}>
            Done
          </Button>
        }
      >
        {onBreakConfirmation && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Alert
              type="success"
              showIcon
              message={
                onBreakConfirmation.status === 'active'
                  ? 'Your subscription is on break, and your credits are safe.'
                  : onBreakConfirmation.effective_at
                  ? `Your On Break plan is scheduled to begin on ${formatDate(onBreakConfirmation.effective_at)}.`
                  : 'Your On Break plan is scheduled for your next billing date.'
              }
              description="Your existing credits remain protected, and you can resume a plan at any time."
            />
            {onBreakConfirmation.follow_up_at && (
              <Text type="secondary">
                We'll check in around {formatDate(onBreakConfirmation.follow_up_at)} based on your answer.
              </Text>
            )}
            {onBreakConfirmation.resource_keys.length > 0 && (
              <>
                <div className="account-billing-modal-block-header">
                  <h3>Useful next steps</h3>
                  <p>These resources match where you are in your publishing process.</p>
                </div>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  {onBreakConfirmation.resource_keys
                    .map((key) => ({ key, resource: ON_BREAK_RESOURCE_DETAILS[key] }))
                    .map(({ key, resource }) => resource ? (
                        <Card key={key} size="small">
                          <Space direction="vertical" size={4} style={{ width: '100%' }}>
                            <Text strong>{resource.title}</Text>
                            <Text type="secondary">{resource.description}</Text>
                            <Button
                              type="link"
                              style={{ padding: 0, width: 'fit-content' }}
                              onClick={() => {
                                setIsOnBreakConfirmationVisible(false);
                                navigate(resource.route);
                              }}
                            >
                              Open resource
                            </Button>
                          </Space>
                        </Card>
                      ) : null)}
                </Space>
              </>
            )}
          </Space>
        )}
      </Modal>

      <Modal
        title="Pause Subscription"
        className="account-billing-modal"
        open={isPauseModalVisible}
        onCancel={() => {
          if (!isPausing) {
            setIsPauseModalVisible(false);
          }
        }}
        okText="Pause"
        okButtonProps={{ loading: isPausing }}
        onOk={handleConfirmPause}
      >
        <div className="account-billing-modal-body">
          <section className="account-billing-modal-block">
            <div className="account-billing-modal-block-header">
              <h3>Pause length</h3>
              <p>
                Choose how many upcoming monthly renewals to skip. Billing resumes automatically at the end of the
                selected skip window, and you can resume early at any time.
              </p>
            </div>
            <Radio.Group
              value={selectedPauseSkips}
              onChange={(e) => setSelectedPauseSkips(e.target.value)}
              disabled={isPausing}
              className="account-billing-radio-list"
            >
              <Space direction="vertical">
                {[1, 2, 3].map((value) => (
                  <Radio
                    key={value}
                    value={value}
                    disabled={
                      typeof pauseLimitInfo.remaining === 'number' && pauseLimitInfo.remaining < value
                    }
                  >
                    {`Skip ${value} renewal${value === 1 ? '' : 's'} (${value} month${value === 1 ? '' : 's'})`}
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
          </section>
          <section className="account-billing-modal-block account-billing-modal-block--muted">
            <Text type="secondary">
              You can skip up to {MAX_PAUSE_SKIP_RENEWALS} renewals in any rolling 12-month period. Subscription credits remain protected while your subscription stays active.
            </Text>
            {typeof pauseLimitInfo.remaining === 'number' && (
              <Text type="secondary" style={{ display: 'block', marginTop: 6 }}>
                {`Remaining pause skips: ${pauseLimitInfo.remaining} of ${MAX_PAUSE_SKIP_RENEWALS}${
                  typeof pauseLimitInfo.windowEnd === 'number'
                    ? ` (window resets ${formatDate(pauseLimitInfo.windowEnd)})`
                    : ''
                }.`}
              </Text>
            )}
          </section>
        </div>
      </Modal>

      <Modal
        title="Extend Pause"
        className="account-billing-modal"
        open={isExtendPauseModalVisible}
        onCancel={() => {
          if (!isExtendingPause) {
            setIsExtendPauseModalVisible(false);
          }
        }}
        okText="Extend Pause"
        okButtonProps={{ loading: isExtendingPause }}
        cancelButtonProps={{ disabled: isExtendingPause }}
        onOk={handleConfirmExtendPause}
      >
        <div className="account-billing-modal-body">
          <section className="account-billing-modal-block">
            <div className="account-billing-modal-block-header">
              <h3>Additional pause length</h3>
              <p>
                Add extra skipped renewals to push out your scheduled resume date. You can still resume early at any time.
              </p>
            </div>
            {currentPauseEndsLabel && (
              <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                {`Currently scheduled to resume on ${currentPauseEndsLabel}.`}
              </Text>
            )}
            <Radio.Group
              value={selectedExtendPauseSkips}
              onChange={(e) => setSelectedExtendPauseSkips(e.target.value)}
              disabled={isExtendingPause}
              className="account-billing-radio-list"
            >
              <Space direction="vertical">
                {[1, 2, 3].map((value) => (
                  <Radio
                    key={value}
                    value={value}
                    disabled={
                      typeof pauseLimitInfo.remaining === 'number' && pauseLimitInfo.remaining < value
                    }
                  >
                    {`Skip ${value} additional renewal${value === 1 ? '' : 's'} (${value} month${value === 1 ? '' : 's'})`}
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
          </section>
          <section className="account-billing-modal-block account-billing-modal-block--muted">
            <Text type="secondary">
              {`Remaining pause skips this year: ${
                typeof pauseLimitInfo.remaining === 'number'
                  ? pauseLimitInfo.remaining
                  : MAX_PAUSE_SKIP_RENEWALS
              } of ${MAX_PAUSE_SKIP_RENEWALS}. Extending consumes from the same allowance.`}
            </Text>
            {currentPauseEligibleLabel && (
              <Text type="secondary" style={{ display: 'block', marginTop: 6 }}>
                {`Next pause skip replenishes on ${currentPauseEligibleLabel}.`}
              </Text>
            )}
          </section>
        </div>
      </Modal>

      <Modal
        title="Resume Subscription"
        className="account-billing-modal"
        open={isResumeConfirmVisible}
        onCancel={() => {
          if (!isResuming) {
            setIsResumeConfirmVisible(false);
          }
        }}
        okText="Resume Now"
        okButtonProps={{ loading: isResuming }}
        cancelButtonProps={{ disabled: isResuming }}
        onOk={async () => {
          if (isResuming) {
            return;
          }
          setIsResumeConfirmVisible(false);
          await resumeSubscriptionRequest();
        }}
      >
        <div className="account-billing-modal-body">
          <section className="account-billing-modal-block">
            <div className="account-billing-modal-block-header">
              <h3>Resume now</h3>
              <p>
                Resuming now restarts your billing cycle immediately. You can pause again whenever you have pause skips available.
              </p>
            </div>
            {typeof pauseLimitInfo.remaining === 'number' && (
              <Text>
                {`Pause skips available after resuming: ${pauseLimitInfo.remaining} of ${MAX_PAUSE_SKIP_RENEWALS}.`}
              </Text>
            )}
          </section>
          {(currentPauseEndsLabel || currentPauseEligibleLabel) && (
            <section className="account-billing-modal-block account-billing-modal-block--muted">
              {currentPauseEndsLabel && (
                <Text type="secondary" style={{ display: 'block' }}>
                  {`Waiting for the scheduled resume${currentPauseEndsLabel ? ` on ${currentPauseEndsLabel}` : ''} keeps your pause skips unchanged.`}
                </Text>
              )}
              {currentPauseEligibleLabel && (
                <Text type="secondary" style={{ display: 'block', marginTop: currentPauseEndsLabel ? 6 : 0 }}>
                  {`Next pause skip replenishes on ${currentPauseEligibleLabel}.`}
                </Text>
              )}
            </section>
          )}
        </div>
      </Modal>

      <Modal
        title="Subscription Credits"
        className="account-billing-modal"
        open={isBatchModalVisible}
        onCancel={() => {
          setIsBatchModalVisible(false);
          if (tourOpenedBatchModalRef.current) {
            tourOpenedBatchModalRef.current = false;
          }
        }}
        footer={null}
        width={screens.md ? 900 : '95vw'}
        style={{ maxWidth: 1200 }}
        destroyOnClose
      >
        <div data-tour-id="billing-subscription-credits-modal">
          <div className="account-billing-modal-body">
            <section className="account-billing-modal-block account-billing-table-surface">
            <Table<SubscriptionCreditBatch>
              dataSource={subscriptionBatches}
              columns={subscriptionBatchColumns}
              loading={subscriptionBatchesLoading}
              pagination={false}
              rowKey={(record) => record.key || record.batchId || `${record.grantedAt ?? 0}-${record.expiresAt ?? 0}`}
              size="small"
              scroll={{ x: true }}
              style={{ textAlign: 'center' }}
              locale={{ emptyText: <Empty description="No credit batches found" /> }}
            />
            </section>
          </div>
        </div>
      </Modal>

      {/* Credit Top Up Modal */}
      <Modal
        title={
          <div className="account-billing-modal-title">
            <RocketOutlined className="account-billing-modal-title-icon" />
            Credit Top Up
          </div>
        }
        className="account-billing-modal"
        open={isGetCreditsModalVisible}
        onCancel={() => {
          setIsGetCreditsModalVisible(false);
          const atticusReturn = creditTopUpContext?.source === 'atticus'
            ? atticusQuoteReturnPath(creditTopUpContext.returnTo) : undefined;
          if (atticusReturn) navigate(atticusReturn);
        }}
        footer={null}
        width={screens.md ? 800 : '95vw'}
        style={{ maxWidth: 900 }}
        centered
        bodyStyle={{ padding: screens.md ? 16 : 12 }}
      >
        {userID ? (
            <CustomPricing
              userID={usersSharedState.userID}
              email={usersSharedState.email}
              isForeverCredits={true}
              currentPlanPriceId={planDetails.stripePriceID || planDetails.priceId}
              currentPlanIsLegacy={!!planDetails.isLegacy}
              currentPlanForeverDiscountPercent={planDetails.foreverCreditDiscountPercent}
              shortfallContext={creditTopUpContext ?? undefined}
              beforeCheckout={creditTopUpContext?.source === 'atticus' && atticusQuoteReturnPath(creditTopUpContext.returnTo)
                ? async () => ({ payload: {
                  successUrl: `${window.location.origin}${atticusQuoteReturnPath(creditTopUpContext.returnTo)}`,
                  cancelUrl: `${window.location.origin}${atticusQuoteReturnPath(creditTopUpContext.returnTo)}`,
                } }) : undefined}
            compactForeverGrid={true}
            renderMode="foreverWithSide"
            purchaseMode="direct"
            onSuccess={async () => {
              setIsGetCreditsModalVisible(false);
              await fetchUpdatedPlanDetails();
              await refreshSubscriptionSummary();
              await loadSubscriptionCreditBatches();
              const atticusReturn = creditTopUpContext?.source === 'atticus'
                ? atticusQuoteReturnPath(creditTopUpContext.returnTo) : undefined;
              if (atticusReturn) navigate(atticusReturn);
            }}
            sidePanel={{
              type: hasCurrentPlan ? 'currentPlan' : 'subscribe',
              planName: planDetails.planName,
              monthlyCreditsAllocated: planDetails.monthlyCreditsAllocated || 0,
              monthlyPrice: planDetails.price || 0,
              billingInterval: planDetails.billingInterval,
              intervalMonths: planDetails.intervalMonths,
              upfrontCredits: planUpfrontCredits,
              nextRenewalDate: subscriptionSummary
                ? (subscriptionSummary.trialEnd ??
                   subscriptionSummary.cancelAt ??
                   (subscriptionSummary.nextPaymentDate || subscriptionSummary.currentPeriodEnd))
                : undefined,
              pendingChange: (subscriptionSummary && subscriptionSummary.scheduledTargetProductId)
                ? {
                    targetPriceId: subscriptionSummary.scheduledTargetPriceId || undefined,
                    targetProductId: subscriptionSummary.scheduledTargetProductId || undefined,
                    effectiveAt: subscriptionSummary.scheduledEffectiveDate || undefined,
                    action: (subscriptionSummary.scheduledAction as any) || undefined
                  }
                : undefined,
              renewDisabled:
                !!subscriptionSummary?.cancelAt ||
                !!subscriptionSummary?.isPaused ||
                !!subscriptionSummary?.isOnBreak ||
                canRenewEarly === false,
              renewDisabledTooltip: renewTooltipMessage,
              onRenewNow: () => {
                message.loading({ content: 'Processing renewal...', key: RENEW_TOAST_KEY, duration: 0 });
                setIsRenewFlowActive(true);
                setIsGetCreditsModalVisible(false);
                startDirectRenewFlow();
              },
              onSubscribe: () => {
                setIsGetCreditsModalVisible(false);
                goToPricing();
              }
            }}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: 16 }}>
            <Spin />
          </div>
        )}
      </Modal>

      {/* Customer selection for billing portal */}
      <Modal
        title={
          <div className="account-billing-modal-title account-billing-modal-title--stacked">
            <CreditCardOutlined className="account-billing-modal-title-icon" />
            <div className="account-billing-modal-title-copy">
              <Title level={4}>Manage Payment Method</Title>
              <span className="account-billing-modal-subtitle">You have multiple subscriptions. Select one to manage.</span>
            </div>
          </div>
        }
        className="account-billing-modal"
        open={isCustomerSelectionModalVisible}
        onCancel={() => {
          setIsCustomerSelectionModalVisible(false);
          setSelectedCustomerId(null);
        }}
        footer={null}
        width={560}
        centered
        bodyStyle={{ padding: 24 }}
      >
        <div className="account-billing-modal-body">
          <section className="account-billing-modal-block">
        <Radio.Group
          onChange={(e) => setSelectedCustomerId(e.target.value)}
          value={selectedCustomerId}
          style={{ width: '100%' }}
        >
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            {availableCustomers.map((customer: any) => {
              const activeSub = customer.subscriptions.find(
                (sub: any) => sub.status === 'active' || sub.status === 'trialing'
              );
              if (!activeSub) return null;

              return (
                <Card
                  key={customer.customer_id}
                  hoverable
                  className={`account-billing-select-card ${
                    selectedCustomerId === customer.customer_id ? 'account-billing-select-card--selected' : ''
                  }`}
                  onClick={() => setSelectedCustomerId(customer.customer_id)}
                >
                  <Radio value={customer.customer_id} style={{ width: '100%' }}>
                    <Row gutter={[16, 8]} align="middle">
                      <Col flex="1">
                        <Space>
                          <Text strong style={{ fontSize: 16 }}>{activeSub.plan_name}</Text>
                          <Badge
                            status={activeSub.status === 'active' ? 'success' : activeSub.status === 'trialing' ? 'processing' : 'default'}
                            text={activeSub.status === 'active' ? 'Active' : activeSub.status === 'trialing' ? 'Trial' : activeSub.status}
                          />
                        </Space>
                      </Col>
                      <Col>
                        <Text type="secondary">
                          <CalendarOutlined style={{ marginRight: 4 }} />
                          {activeSub.status === 'trialing' && activeSub.trial_end
                            ? `Trial ends ${formatDate(activeSub.trial_end)}`
                            : activeSub.cancel_at
                            ? `Cancels ${formatDate(activeSub.cancel_at)}`
                            : `Next billing ${formatDate(activeSub.current_period_end)}`}
                        </Text>
                      </Col>
                    </Row>
                  </Radio>
                </Card>
              );
            })}
          </Space>
        </Radio.Group>
          </section>

        <div className="account-billing-modal-footer">
            <Button
              onClick={() => {
                setIsCustomerSelectionModalVisible(false);
                setSelectedCustomerId(null);
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              disabled={!selectedCustomerId}
              loading={isLoadingCustomerPortal}
              onClick={() => {
                if (selectedCustomerId) {
                  setIsCustomerSelectionModalVisible(false);
                  handleBilling(selectedCustomerId);
                }
              }}
            >
              Manage Payment Method
            </Button>
        </div>
        </div>
      </Modal>

      {/* Subscription selection modal */}
      <Modal
        title={
          <div className="account-billing-modal-title account-billing-modal-title--stacked">
            {selectionModalCopy.icon}
            <div className="account-billing-modal-title-copy">
              <Title level={4}>{selectionModalCopy.title}</Title>
              <span className="account-billing-modal-subtitle">{selectionModalCopy.description}</span>
            </div>
            {selectionGuidance && (
              <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                {selectionGuidance}
              </Text>
            )}
          </div>
        }
        className="account-billing-modal"
        open={isSubscriptionSelectionModalVisible}
        onCancel={() => {
          setIsSubscriptionSelectionModalVisible(false);
          setSelectionGuidance(null);
          setSelectedSubscriptionCustomerId(null);
          setPauseAfterSelectionImmediate(false);
          setResumeAfterSelectionImmediate(false);
          setPendingPauseSkips(null);
          setReactivateAfterSelectionImmediate(false);
          setRenewAfterSelectionImmediate(false);
          setSubscriptionSelectionMode(null);
          if (currentSelectionMode === 'renew') {
            setRenewalErrorMessage(null);
          }
        }}
        footer={null}
        width={600}
        centered
        bodyStyle={{ padding: 24 }}
      >
        {currentSelectionMode === 'renew' && renewalErrorMessage ? (
          <Alert message={renewalErrorMessage} type="error" showIcon style={{ marginBottom: 16, borderRadius: 8 }} />
        ) : (
          <div className="account-billing-modal-body">
            <section className="account-billing-modal-block">
            <Radio.Group
              onChange={(e) => setSelectedSubscriptionCustomerId(e.target.value)}
              value={selectedSubscriptionCustomerId}
              style={{ width: '100%' }}
            >
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                {availableSubscriptions.map((customer: any) =>
                  customer.subscriptions.map((sub: any) => (
                    <Card
                      key={`${customer.customer_id}-${sub.subscription_id}`}
                      hoverable
                      className={`account-billing-select-card ${
                        selectedSubscriptionCustomerId === customer.customer_id ? 'account-billing-select-card--selected' : ''
                      }`}
                      onClick={() => setSelectedSubscriptionCustomerId(customer.customer_id)}
                    >
                      <Radio value={customer.customer_id} style={{ width: '100%' }}>
                        <Row gutter={[16, 8]}>
                          <Col span={24}>
                            <Space direction="vertical" style={{ width: '100%' }}>
                              <Row justify="space-between" align="middle">
                                <Col>
                                  <Text strong style={{ fontSize: 16 }}>{sub.plan_name}</Text>
                                </Col>
                                <Col>
                                  <Badge
                                    status={sub.status === 'active' ? 'success' : sub.status === 'trialing' ? 'processing' : 'warning'}
                                    text={sub.status === 'active' ? 'Active' : sub.status === 'trialing' ? 'Trial' : sub.status}
                                  />
                                </Col>
                              </Row>
                              {sub.next_payment && (
                                <Row justify="space-between">
                                  <Col><Text type="secondary">Next Payment:</Text></Col>
                                  <Col>
                                    <Text strong>${(sub.next_payment.amount_due / 100).toFixed(2)}</Text>
                                    <Text type="secondary" style={{ marginLeft: 8 }}>
                                      on {formatDate(sub.next_payment.due_date)}
                                    </Text>
                                  </Col>
                                </Row>
                              )}
                              <Row>
                                <Col span={24}>
                                  <Text type="secondary" style={{ fontSize: 12 }}>
                                    <CalendarOutlined style={{ marginRight: 4 }} />
                                    Current period ends {formatDate(sub.current_period_end)}
                                  </Text>
                                </Col>
                              </Row>
                              {sub.paused_until && (
                                <Row>
                                  <Col span={24}>
                                    <Text type="warning" style={{ fontSize: 12 }}>
                                      Paused until {formatDate(sub.paused_until)}
                                    </Text>
                                  </Col>
                                </Row>
                              )}
                            </Space>
                          </Col>
                        </Row>
                      </Radio>
                    </Card>
                  ))
                )}
              </Space>
            </Radio.Group>
            </section>

            <div className="account-billing-modal-footer">
                <Button
                  onClick={() => {
                    setIsSubscriptionSelectionModalVisible(false);
                    setSelectionGuidance(null);
                    setSelectedSubscriptionCustomerId(null);
                    setPauseAfterSelectionImmediate(false);
                    setResumeAfterSelectionImmediate(false);
                    setPendingPauseSkips(null);
                    setReactivateAfterSelectionImmediate(false);
                    setRenewAfterSelectionImmediate(false);
                    setSubscriptionSelectionMode(null);
                    if (currentSelectionMode === 'renew') {
                      setRenewalErrorMessage(null);
                    }
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  disabled={!selectedSubscriptionCustomerId}
                  loading={isLoadingRenewalDetails}
                  onClick={handleSubscriptionSelection}
                >
                  {selectionModalCopy.primaryButton}
                </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Renew Now confirmation */}
      <Modal
        title={
          <div className="account-billing-modal-title">
            <RocketOutlined
              className="account-billing-modal-title-icon"
              style={{ color: renewalErrorMessage ? 'var(--accent-danger)' : undefined }}
            />
            {renewalErrorMessage ? 'Unable to Renew Subscription' : 'Renew Subscription Now'}
          </div>
        }
        className="account-billing-modal"
        open={isRenewModalVisible}
        onOk={handleRenewNowConfirm}
        onCancel={handleRenewNowCancel}
        confirmLoading={isRenewing}
        okText={renewalErrorMessage ? undefined : 'Confirm Renewal'}
        cancelText={renewalErrorMessage ? 'Close' : 'Cancel'}
        okButtonProps={{ disabled: !!renewalErrorMessage || isRenewing, style: { borderRadius: 6 }, hidden: !!renewalErrorMessage }}
        cancelButtonProps={{ disabled: isRenewing, style: { borderRadius: 6 } }}
        centered
        bodyStyle={{ padding: 24 }}
      >
        {isLoadingRenewalDetails ? (
          <div className="loading-card-wrapper" style={{ padding: '32px 0' }}>
            <LoadingCard label="Loading subscription details..." />
          </div>
        ) : renewalErrorMessage ? (
          <div>
            <Alert
              message="Renewal Not Available"
              description={renewalErrorMessage}
              type="error"
              showIcon
              style={{ borderRadius: 8, marginBottom: 16 }}
            />
            {renewalErrorMessage.includes('scheduled for cancellation') && (
              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <Button
                  type="primary"
                  icon={<CreditCardOutlined />}
                  onClick={() => {
                    setIsRenewModalVisible(false);
                    handleBilling();
                  }}
                  style={{ borderRadius: 8 }}
                >
                  Manage Subscription
                </Button>
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Reactivate your subscription, then try renewing again
                  </Text>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="account-billing-modal-body">
            <section className="account-billing-modal-block account-billing-renew-summary">
              <div className="account-billing-summary-row">
                <Text strong>Plan:</Text>
                <Text>{renewalPlanName}</Text>
              </div>
              <div className="account-billing-summary-row">
                <Text strong>Amount:</Text>
                <Text>${(renewalAmount! / 100).toFixed(2)}</Text>
              </div>
              <div className="account-billing-summary-row">
                <Text strong>Billing Date:</Text>
                <Text>Today</Text>
              </div>
            </section>
            <section className="account-billing-modal-block account-billing-modal-block--muted">
              <Text>
                {subscriptionSummary?.scheduledTargetProductId
                  ? `This will immediately renew your subscription and apply your scheduled ${subscriptionSummary?.scheduledAction || 'change'}. Your billing date will move to today.`
                  : 'This will immediately renew your subscription at your current level, charge you now, and move your billing date to today.'}
              </Text>
            </section>
          </div>
        )}
      </Modal>

      {/* Retention Offer Modal */}
      <Modal
        title={
          <div className="account-billing-modal-title account-billing-modal-title--stacked">
            <CrownOutlined className="account-billing-modal-title-icon" />
            <div className="account-billing-modal-title-copy">
              <Title level={3}>Before you cancel</Title>
              <span className="account-billing-modal-subtitle">
                {showRetentionDiscountSave
                  ? 'Keep your credits protected or claim a savings offer before your subscription ends.'
                  : 'Keep your credits protected while your subscription stays active.'}
              </span>
            </div>
          </div>
        }
        className="account-billing-modal"
        open={isRetentionModalVisible}
        onCancel={() => {
          setIsRetentionModalVisible(false);
          resetCancelFeedbackState();
          setCancelFlowNextStep(null);
        }}
        footer={
          <div className="account-billing-retention-footer">
            {showRetentionDiscountSave && (
              <Button
                type="primary"
                size="large"
                loading={isApplyingRetention}
                disabled={isChangingOnBreak}
                onClick={applyRetention}
              >
                <CheckCircleOutlined /> Claim My Discount & Continue
              </Button>
            )}
            <Button
              type="text"
              danger
              onClick={() => {
                setIsRetentionModalVisible(false);
                setIsCancelConfirmVisible(true);
              }}
            >
              No thanks, I still want to cancel
            </Button>
          </div>
        }
        centered
        width={showOnBreakCancellationSave && showRetentionDiscountSave ? 860 : 520}
        bodyStyle={{
          maxHeight: 'calc(100vh - 300px)',
          overflowY: 'auto',
        }}
      >
        <div className="account-billing-modal-body">
          {retentionError && (
            <Alert
              type="error"
              message="Unable to apply savings"
              description={retentionError}
              showIcon
            />
          )}

          <div
            style={{
              display: 'grid',
              gridTemplateColumns:
                showOnBreakCancellationSave && showRetentionDiscountSave
                  ? 'repeat(auto-fit, minmax(300px, 1fr))'
                  : '1fr',
              gap: 16,
              alignItems: 'start',
            }}
          >
            {showOnBreakCancellationSave && (
              <section className="account-billing-modal-block">
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Text strong style={{ fontSize: 18 }}>Move to On Break</Text>
                  <div className="account-billing-secondary-surface account-billing-summary-list">
                    {[
                      ['On Break price', `${onBreakPlanPrice || '$3.99'} / month`],
                      ['Starts', 'Next billing date'],
                      ['Monthly credits added', '0'],
                      ['Existing credits', 'Never expire while subscribed'],
                    ].map(([label, value]) => (
                      <div className="account-billing-summary-row" key={label}>
                        <span className="account-billing-summary-label">{label}</span>
                        <span className="account-billing-summary-value">{value}</span>
                      </div>
                    ))}
                  </div>
                  <Text type="secondary">
                    Your current plan stays active until your next billing date. Resume your saved plan when you are ready.
                  </Text>
                  {onBreakEngagementEnabled && renderOnBreakQuestions(isChangingOnBreak)}
                  {planDetails.isLegacy && (
                    <Alert
                      type="info"
                      showIcon
                      message="Your legacy plan return option will be preserved."
                    />
                  )}
                  <Button
                    type="primary"
                    icon={<PauseCircleOutlined />}
                    block
                    size="large"
                    loading={isChangingOnBreak}
                    disabled={
                      isApplyingRetention ||
                      (onBreakEngagementEnabled &&
                        (!selectedOnBreakStage || !selectedOnBreakExpectedReturn))
                    }
                    onClick={moveToOnBreakFromCancellation}
                  >
                    Move to On Break
                  </Button>
                </Space>
              </section>
            )}

            {showRetentionDiscountSave && (
              <section className="account-billing-modal-block">
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  {showOnBreakCancellationSave && <Text strong style={{ fontSize: 18 }}>Discount options</Text>}
                  <Radio.Group
                    onChange={(e) => setRetentionChoice(e.target.value)}
                    value={retentionChoice}
                    style={{ width: '100%' }}
                  >
                    <Space direction="vertical" style={{ width: '100%' }} size="middle">
                      <Card
                        hoverable
                        onClick={() => setRetentionChoice('25_NEXT')}
                        className={`account-billing-select-card ${
                          retentionChoice === '25_NEXT' ? 'account-billing-select-card--selected' : ''
                        }`}
                      >
                        <Radio value="25_NEXT" style={{ width: '100%' }}>
                          <Space direction="vertical" style={{ width: '100%', marginLeft: 8 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <Tag color="error">SAVE 25%</Tag>
                              <Text strong>Instant Savings</Text>
                              <Tag color="blue">Popular</Tag>
                            </div>
                            <Text style={{ color: 'var(--text-secondary)' }}>
                              Get <strong>25% off</strong> your next invoice
                            </Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              Best for quick savings. Applies to your next invoice.
                            </Text>
                          </Space>
                        </Radio>
                      </Card>

                      <Card
                        hoverable
                        onClick={() => setRetentionChoice('15_3')}
                        className={`account-billing-select-card ${
                          retentionChoice === '15_3' ? 'account-billing-select-card--selected' : ''
                        }`}
                      >
                        <Radio value="15_3" style={{ width: '100%' }}>
                          <Space direction="vertical" style={{ width: '100%', marginLeft: 8 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <Tag color="blue">3 MONTHS</Tag>
                              <Text strong>Extended Savings</Text>
                            </div>
                            <Text style={{ color: 'var(--text-secondary)' }}>
                              Save <strong>15% every month</strong> for the next 3 months
                            </Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              Total savings over 90 days.
                            </Text>
                          </Space>
                        </Radio>
                      </Card>
                    </Space>
                  </Radio.Group>
                </Space>
              </section>
            )}
          </div>

          <div className="account-billing-retention-trust">
            <Space size={4}>
              <CheckCircleOutlined style={{ color: 'var(--accent-success)', fontSize: 12 }} />
              <Text type="secondary" style={{ fontSize: 11 }}>
                No hidden fees. Cancel anytime.
              </Text>
            </Space>
          </div>
        </div>
      </Modal>

{/* Confirm Cancel Modal */}
<Modal
  title={
    <div className="account-billing-modal-title">
      <QuestionCircleOutlined
        className="account-billing-modal-title-icon"
        style={{ color: 'var(--accent-danger)' }}
      />
      Are you sure you want to cancel?
    </div>
  }
  className="account-billing-modal"
  open={isCancelConfirmVisible}
  onCancel={() => {
    setIsCancelConfirmVisible(false);
    resetCancelFeedbackState();
    setCancelFlowNextStep(null);
  }}
  footer={
    <div className="account-billing-modal-footer">
      <Button
        onClick={() => {
          setIsCancelConfirmVisible(false);
          resetCancelFeedbackState();
          setCancelFlowNextStep(null);
        }}
      >
        Keep Subscription
      </Button>
      <Button
        danger
        type="primary"
        loading={isCancelling}
        disabled={!isCancelFeedbackValid}
        onClick={confirmCancelSubscription}
      >
        Confirm Cancellation
      </Button>
    </div>
  }
  centered
  bodyStyle={{ padding: 24 }}
>
  <div className="account-billing-modal-body">
    <section className="account-billing-modal-block">
      <Alert
        type="info"
        showIcon
        message="Cancellation effective at period end"
        description={
          <div>
            Your subscription will remain active until{' '}
            <strong>
              {formatDateTimeLocal(
                subscriptionSummary?.currentPeriodEnd ||
                  subscriptionSummary?.nextPaymentDate ||
                  (typeof fallbackCancelAt === 'number' ? fallbackCancelAt : null),
                userTimeZone
              )}
            </strong>.
            <br />
            After access ends, remaining subscription credits stay usable for{' '}
            {SUBSCRIPTION_CREDIT_CANCEL_GRACE_DAYS} days, then expire. Forever Credits never expire.
          </div>
        }
        style={{ borderRadius: 8 }}
      />
    </section>
  </div>
</Modal>

      <Modal
        title={
          <div className="account-billing-modal-title">
            Why are you leaving?
          </div>
        }
        className="account-billing-modal"
        open={isCancelReasonModalVisible}
        onCancel={() => {
          setIsCancelReasonModalVisible(false);
          resetCancelFeedbackState();
          setCancelFlowNextStep(null);
        }}
        footer={
          <div className="account-billing-modal-footer">
            <Button
              onClick={() => {
                setIsCancelReasonModalVisible(false);
                resetCancelFeedbackState();
                setCancelFlowNextStep(null);
              }}
            >
              Keep Subscription
            </Button>
            <Button type="primary" disabled={!isCancelFeedbackValid} onClick={continueCancelFlow}>
              Continue
            </Button>
          </div>
        }
        centered
        bodyStyle={{ padding: 24 }}
      >
        {renderCancellationFeedbackSection()}
      </Modal>

      {/* Invoices Modal */}
      <Modal
        title={
          <div className="account-billing-modal-title">
            <FileTextOutlined className="account-billing-modal-title-icon" />
            Your Invoices
          </div>
        }
        className="account-billing-modal"
        open={isInvoicesModalVisible}
        onCancel={() => setIsInvoicesModalVisible(false)}
        footer={null}
        width="90vw"
        style={{ maxWidth: 1200 }}
        centered
        bodyStyle={{ padding: 24 }}
      >
        <div className="account-billing-modal-body">
        {invoicesError && (
          <Alert
            message="Error"
            description={invoicesError}
            type="error"
            showIcon
          />
        )}

        <section className="account-billing-modal-block account-billing-invoice-toolbar">
          <Input.Search
            placeholder="Search by invoice number, amount, or status..."
            allowClear
            enterButton={<SearchOutlined />}
            style={{ width: '100%' }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />

          <div className="account-billing-status-filters">
            <span>Filter by status:</span>
            <Button type={!statusFilter ? 'primary' : 'default'} onClick={() => setStatusFilter(null)}>
              All
            </Button>
            <Button type={statusFilter === 'paid' ? 'primary' : 'default'} onClick={() => setStatusFilter('paid')}>
              Paid
            </Button>
            <Button type={statusFilter === 'open' ? 'primary' : 'default'} onClick={() => setStatusFilter('open')}>
              Open
            </Button>
            <Button type={statusFilter === 'void' ? 'primary' : 'default'} onClick={() => setStatusFilter('void')}>
              Void
            </Button>
            {(searchText || statusFilter) && (
              <Button
                onClick={() => {
                  setSearchText('');
                  setStatusFilter(null);
                }}
              >
                Clear Filters
              </Button>
            )}
          </div>
        </section>

        <section className="account-billing-modal-block account-billing-table-surface">
        <Table<InvoiceRow>
          dataSource={filteredInvoices}
          loading={isLoadingInvoices}
          rowKey="id"
          pagination={{ pageSize: 5 }}
          locale={{ emptyText: <Empty description="No invoices found" /> }}
          scroll={{ x: 'max-content' }}
          columns={[
            {
              title: 'Date',
              dataIndex: 'created',
              key: 'created',
              sorter: (a, b) => a.created - b.created,
              render: (text: number) => new Date(text * 1000).toLocaleDateString(),
              defaultSortOrder: 'descend',
              width: 100,
              fixed: 'left' as const
            },
            {
              title: 'Type',
              dataIndex: 'object',
              key: 'type',
              width: 120,
              render: (_: string, record: InvoiceRow) => {
                const type = record.record_type || record.object || 'unknown';
                const typeConfig: Record<string, { color: string; text: string }> = {
                  invoice: { color: 'blue', text: 'Invoice' },
                  charge: { color: 'green', text: 'Payment' },
                  payment_intent: { color: 'green', text: 'Payment' }
                };
                const config = typeConfig[type] || { color: 'default', text: 'Invoice' };
                return <Tag color={config.color}>{config.text}</Tag>;
              }
            },
            {
              title: 'Number',
              dataIndex: 'number',
              key: 'number',
              width: 120,
              render: (text: string | undefined) => text || 'N/A'
            },
            {
              title: 'Amount',
              key: 'amount',
              sorter: (a, b) => {
                const amountA = a.display_amount || a.amount_paid || a.total || a.amount_due || 0;
                const amountB = b.display_amount || b.amount_paid || b.total || b.amount_due || 0;
                return amountA - amountB;
              },
              width: 100,
              render: (_: any, record: InvoiceRow) => {
                const amount = record.display_amount || record.amount_paid || record.total || record.amount_due || 0;
                return (
                  <span>
                    ${(amount / 100).toFixed(2)}
                    {record.status === 'draft' && amount > 0 && (
                      <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                        (draft)
                      </Text>
                    )}
                  </span>
                );
              }
            },
            {
              title: 'Status',
              dataIndex: 'status',
              key: 'status',
              width: 100,
              render: (text: string | undefined) => (
                <Tag
                  color={
                    text === 'paid'
                      ? 'success'
                      : text === 'open'
                      ? 'processing'
                      : text === 'void'
                      ? 'error'
                      : text === 'draft'
                      ? 'warning'
                      : 'default'
                  }
                >
                  {(text || 'UNKNOWN').toUpperCase()}
                </Tag>
              )
            },
            {
              title: 'Actions',
              key: 'actions',
              width: 200,
              fixed: 'right' as const,
              render: (_: any, record: InvoiceRow) => (
                <Space size="small">
                  {record.hosted_invoice_url && (
                    <Button
                      type="link"
                      size="small"
                      onClick={() => window.open(record.hosted_invoice_url, '_blank')}
                      icon={<DownloadOutlined />}
                    >
                      Invoice
                    </Button>
                  )}
                  {record.invoice_pdf && (
                    <Button
                      type="link"
                      size="small"
                      onClick={() => window.open(record.invoice_pdf, '_blank')}
                      icon={<DownloadOutlined />}
                    >
                      PDF
                    </Button>
                  )}
                  {record.receipt_url && (
                    <Button
                      type="link"
                      size="small"
                      onClick={() => window.open(record.receipt_url, '_blank')}
                      icon={<DownloadOutlined />}
                    >
                      Receipt
                    </Button>
                  )}
                  {!record.hosted_invoice_url && !record.invoice_pdf && !record.receipt_url && (
                    <Text type="secondary" style={{ fontSize: 12 }}>No receipt</Text>
                  )}
                </Space>
              )
            }
          ]}
        />
        </section>
        </div>
      </Modal>

      <Modal
        open={isRenewFlowActive}
        className="account-billing-modal"
        footer={null}
        closable={false}
        maskClosable={false}
        centered
        width={360}
        bodyStyle={{ padding: 24, textAlign: 'center' }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
          <LoadingCard label="Processing renewal..." />
          <Text type="secondary" style={{ fontSize: 12 }}>Don't close this window</Text>
        </div>
      </Modal>
    </>
  );
}
